import { setStreamAbortSource } from "../lib/agenticStream/streamTelemetry";
import { messageHasFinalManuscriptChange } from "src/components/PersistentChat/manuscriptChangeMessageRef";
import { readFrozenChatReplies, saveFrozenChatReplies, type FrozenChatReply } from "src/components/PersistentChat/frozenChatReplies";
import * as Sentry from "@sentry/react";
import { resolveBenchmarkWriteModel } from "../components/BenchmarkTakeover/proseMode";
import { normalizePersistentChatErrorMessage } from "./normalizePersistentChatErrorMessage";
import { firstFiveWords } from "~shared/utils/firstFiveWords";
import { CHAT_NATURAL_TONE_EXPERIMENT_QUERY_PARAM } from "~shared/chatPersonalization";
import {
  buildEntityGenerateUserMessageFromCtaAction,
  entityGenerateInputsFromCtaAction,
} from "~shared/agenticChat/entityGenerateUserMessageContent";
import type {
  AgenticChatRequestBody,
  PlanApproveRequestBody,
  PlanResumeRequestBody,
} from "~shared/agenticChat/requestSchemas";
import { buildManuscriptSelectionTarget } from "~shared/manuscriptSelectionTarget";
import {
  parseStoryBibleGeneratedEntity,
  type StoryBibleGeneratedEntity,
} from "~shared/agenticChat/storyBibleGeneratedEntity";
import { create } from "zustand";
import { mutate as globalSWRMutate } from "swr";
import { AgenticChatMode, AppEventPublisher } from "src/AppEventPublisher";
import Auth from "src/Auth";
import { useProjectStore } from "./projectStore";
import { sendViaAgenticVercelChat } from "src/lib/agenticStream/agenticVercelChatBridge";
import {
  flushClearEditorSelectionContext,
  scheduleClearEditorSelectionContext,
  scheduleSyncEditorSelectionContext,
} from "src/lib/editorSelectionContextSync";
import { makeId } from "src/Utils";
import { normalizeTerminalPlanStatus, resolvePlanCurrentStepIndex } from "src/lib/normalizeTerminalPlanStatus";
import { normalizeOutlineItemsField } from "../../../src/shared/utils/normalizeOutlineItemsField";
import { isAbortLikePersistentChatStreamError, shouldCapturePersistentChatStreamError } from "src/lib/persistentChatErrorReporting";
import { queryClient } from "src/lib/query-client";
import { isChatNaturalToneExperimentEnabledFromLocationParts } from "src/lib/chatNaturalToneExperimentUrl";
import type { ThinkingStepData, ThinkingTask, ThinkingTaskIcon } from "~shared/types/thinking-step-types";
import { buildThinkingStepTaskCountTitle, isHiddenThinkingToolTask } from "~shared/thinkingTaskCount";
import { isThinkingWriteToolStart } from "~shared/thinkingToolWrite";
import {
  isManuscriptStagingToolName,
  manuscriptStagingThinkingTitleForMode,
} from "~shared/manuscriptStagingThinking";
import type { PersistentChatWithMessages, PersistentChatMessageData } from "~shared/types/persistent-chat-types";
import type { ClientUiAction } from "~shared/clientUiActions";
import type { AgenticLegacyStreamChunk } from "src/lib/agenticStream/agenticVercelStreamConsumer";
import { getApplyUiActionThinkingTitle } from "~shared/applyUiActionThinkingLabels";
import { DEFAULT_AGENTIC_CHAT_MODE } from "~shared/constants";
import { linearPathToLeaf, isOptimisticClientUserMessageId, shouldBranchResendUserMessage } from "~shared/persistentChatBranching";
import { resolveWormholeOutputLengthForAgenticChat } from "~shared/wormholeOutputLengthWords";
import { chatQueryKey, DefaultChatModel, NEW_CHAT_ID } from "src/components/PersistentChat/PersistentChatTypes";
import { messageHasRewriteSuggestionCarouselMetadata } from "src/components/RewriteSuggestionCarousel/rewriteSuggestionParser";
import {
  applyOutlineUpdateFromChunk,
  isLatestOutlineUpdateRefetch,
  reapplyChapterDiffsToOutline,
  reapplyOutlineUpdateSummariesFromChunk,
  startOutlineUpdateRefetch,
} from "src/components/StoryBible/StructuredOutline/StructuredOutlineStore";
import {
  applyCharacterUpdateFromChunk,
  applyMetaUpdateFromChunk,
  applyStoryEngineMetaToStore,
  isCurrentStoryBibleOwner,
  isLatestCharacterUpdateRefetch,
  isLatestMetaUpdateRefetch,
  isLatestWorldbuildingUpdateRefetch,
  reapplyMetaDiffsFromUpdates,
  collectMetaUpdatedFieldKeys,
  startCharacterUpdateRefetch,
  startMetaUpdateRefetch,
  startWorldbuildingUpdateRefetch,
} from "src/utils/storyBibleUpdateFromChunk";
import { fetchChatPage } from "src/components/PersistentChat/PersistentChatQueries";
import { resolveEffectiveChatId } from "src/components/PersistentChat/persistentChatSwitchPolicy";
import type { QuestionStep } from "src/components/PersistentChat/AskUserQuestion";
import { normalizeAskUserQuestionSteps } from "src/components/PersistentChat/askUserQuestionNormalize";
import { useCrossAppSignalStore, CrossAppSignal, getSynopsisCTASignal } from "src/stores/crossAppSignalStore";
import { useCommentStore } from "src/stores/commentStore";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { ctaActionsEquivalent, type CTAAction, type CTAActionStatus } from "src/components/PersistentChat/ThinkingStep/cta-types";
import { isStoryBibleCtaType } from "~shared/agenticChat/storyBibleCtaTypes";
import {
  STORY_BIBLE_CTA_PROGRESS_RESET,
  onStoryBibleCtaContentUpdate,
  onStoryBibleCtaEntityGenerated,
  onStoryBibleCtaToolComplete,
  onStoryBibleCtaToolStart,
  storyBibleCtaProgressFieldsForSetCtaAction,
} from "src/stores/storyBibleCtaProgressStore";
import {
  COMMENTING_PROGRESS_RESET,
  commentingProgressFieldsForStarted,
  ensureActiveCommentingSessionOnToolStart,
  onCommentingCommentCreated,
  onCommentingFinished,
  onCommentingToolComplete,
  onCommentingToolStart,
} from "src/stores/commentingProgressStore";
import { COMMENTING_PROGRESS, mergeCommentingProgress } from "src/components/PersistentChat/commentingProgress";
import { ctaActionForResendFromUserMessage } from "src/components/PersistentChat/storyBibleCtaResend";
import { userQuestionIsGenerateCastCharacterCount } from "src/components/PersistentChat/storyBibleCta";
import {
  clearAutoSwitchedFromChatToWrite,
  maybeMarkAutoSwitchedFromChatToWrite,
} from "src/components/PersistentChat/modeSwitchBackToast";
import {
  clearStoryBibleCtaAutoPromotedFromChat,
  restoreComposerToChatOnlyAfterStoryBibleCtaCancel,
} from "src/components/PersistentChat/storyBibleCtaModeSwitch";
import { maybeRecordAgentManuscriptEditUndo, manuscriptUndoCaptureOperation } from "src/lib/agentManuscriptEditUndo";
import { parseWritePovTenseChiclets } from "src/components/PersistentChat/ThinkingStep/writeSettingsForChiclets";
import { getBrowserTimeZone } from "src/utils/getBrowserTimeZone";

const FEEDBACK_PLAN_UPSELL_CHAT_MESSAGE =
  "Feedback is available on Pro and Max plans. Upgrade your plan to run Feedback on this manuscript.";

// RAF-throttled text buffer for streaming updates
let _textBuffer = "";
let _textFlushRafId: number | null = null;
let _rewriteArtifactStream: {
  callId: string;
  bufferedText: string;
  suppressUntilStreamEnd: boolean;
} | null = null;

function resetRewriteArtifactStream() {
  _rewriteArtifactStream = null;
}

function rewriteToolResultHasSuggestionCard(chunk: Record<string, unknown>): boolean {
  const rawResult = chunk.rawResult;
  if (!rawResult || typeof rawResult !== "object") return false;
  const result = rawResult as {
    renderAsCarousel?: unknown;
    ui?: { type?: unknown; props?: { renderAsCarousel?: unknown } };
  };
  return result.renderAsCarousel === true
    || (
      result.ui?.type === "RewriteSuggestionCarousel"
      && result.ui.props?.renderAsCarousel !== false
    );
}

function bufferArtifactTextIfNeeded(text: string): boolean {
  if (!_rewriteArtifactStream) return false;
  if (!_rewriteArtifactStream.suppressUntilStreamEnd) {
    _rewriteArtifactStream.bufferedText += text;
  }
  return true;
}

function flushTextBuffer(set: any) {
  if (!_textBuffer) return;
  const buffered = _textBuffer;
  _textBuffer = "";
  _textFlushRafId = null;
  set((state: PersistentChatState) => ({
    currentStreamingText: state.currentStreamingText + buffered,
  }));
}

function appendStreamingText(text: string, set: any) {
  _textBuffer += text;
  if (_textFlushRafId === null) {
    _textFlushRafId = requestAnimationFrame(() => flushTextBuffer(set));
  }
}

/** Live Write-button settings for agentic prose tools (mirrors the Write menu for this session). */
export function buildAgenticWriteButtonSettingsFromEditor():
  | {
      writeModel?: string;
      wormholeOutputLength?: number;
      wormholeOutputCards?: number;
      creativitySliderPosition?: number;
    }
  | undefined {
  const { editorOptions, userData } = useSudowriteStore.getState();
  if (!editorOptions) return undefined;
  // Muse 2 Alpha's comparison UI belongs to Write/Draft. Chat still receives a
  // supported generation model when that picker preference is active.
  const writeModel = resolveBenchmarkWriteModel(typeof editorOptions.writeModel === "string" ? editorOptions.writeModel.trim() : "");
  const persistedLength =
    userData?.fetched && userData?.data?.wormholeOutputLength !== undefined && userData?.data?.wormholeOutputLength !== null
      ? Number(userData.data.wormholeOutputLength)
      : undefined;
  const wormholeOutputLength = resolveWormholeOutputLengthForAgenticChat(
    editorOptions.wormholeOutputLength,
    persistedLength,
  );
  const wormholeOutputCards =
    typeof editorOptions.wormholeOutputCards === "number" && Number.isFinite(editorOptions.wormholeOutputCards)
      ? Math.floor(editorOptions.wormholeOutputCards)
      : undefined;
  const creativitySliderPosition =
    typeof editorOptions.creativitySliderPosition === "number" && Number.isFinite(editorOptions.creativitySliderPosition)
      ? editorOptions.creativitySliderPosition
      : undefined;
  const hasWriteModel = writeModel.length > 0;
  const hasLength = typeof wormholeOutputLength === "number" && Number.isFinite(wormholeOutputLength);
  const hasCards = typeof wormholeOutputCards === "number" && wormholeOutputCards >= 1;
  const hasCreativity = typeof creativitySliderPosition === "number";
  if (!hasWriteModel && !hasLength && !hasCards && !hasCreativity) return undefined;
  return {
    ...(hasWriteModel ? { writeModel } : {}),
    ...(hasLength ? { wormholeOutputLength } : {}),
    ...(hasCards ? { wormholeOutputCards } : {}),
    ...(hasCreativity ? { creativitySliderPosition } : {}),
  };
}

function flushTextBufferSync(set: any) {
  if (_textFlushRafId !== null) {
    cancelAnimationFrame(_textFlushRafId);
    _textFlushRafId = null;
  }
  if (_textBuffer) {
    const remaining = _textBuffer;
    _textBuffer = "";
    set((state: PersistentChatState) => ({
      currentStreamingText: state.currentStreamingText + remaining,
    }));
  }
}

/**
 * Manuscript ↔ outline chapter links are stored as Story Engine `scenes[..].itemId`. The sidebar
 * and “Is this document in your Outline?” read those from `useStoryBibleStore`, loaded via GET
 * `/api/story-engine/:projectId/get`. `refresh_project` only reloads Firebase project data —
 * without this hook, outline link changes from chat stay invisible until a full reload.
 */
async function syncStoryEngineScenesIntoStoryBible(projectId: string | undefined): Promise<void> {
  if (!projectId || typeof projectId !== "string") return;
  try {
    const { useStoryBibleStore } = await import("src/stores/storyBibleStore");
    const sb = useStoryBibleStore.getState();
    if (!isCurrentStoryBibleOwner(sb, projectId)) return;
    const results = await Auth.fetchWithIdToken(`/api/story-engine/${projectId}/get`, {});
    if (!isCurrentStoryBibleOwner(sb, projectId)) return;
    const eng = results?.storyEngine as { id?: string; scenes?: unknown } | undefined;
    if (!eng || typeof eng !== "object") return;
    if (eng.id !== useStoryBibleStore.getState().storyEngineIdRef.current) return;
    const nextScenes = eng.scenes && typeof eng.scenes === "object" ? eng.scenes : {};
    sb.setScenes(() => nextScenes);
  } catch (err) {
    Sentry.captureException(err, {
      tags: { feature: "apply_client_ui_actions", syncStoryEngineScenes: true },
      extra: { projectId },
    });
  }
}

function cancelTextBufferFlush() {
  if (_textFlushRafId !== null) {
    cancelAnimationFrame(_textFlushRafId);
    _textFlushRafId = null;
  }
  _textBuffer = "";
}

/** Chains async `applyClientUiActions` from plan streams so `ask_user_question` can await it (mirrors `pendingProjectUiActionsRef`). */
type PlanStreamUiChainRef = { current: Promise<void> };

function shouldLogInsertDraftQuestionDebug(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.__AGENTIC_CHAT_DEBUG_INSERT_PROMPT__ === true
      || window.localStorage?.getItem("agenticChatDebugInsertPrompt") === "1";
  } catch {
    return false;
  }
}

function isInsertDraftQuestionClient(question: string): boolean {
  return /\binsert it into your document now\b/i.test(question);
}

function stripTrackChangeTags(text: string): string {
  return text.replace(/<\/?(?:insertion|inserted|deletion|deleted|insert|annotation)(?:\s[^>]*)?>/gi, "");
}

function sanitizeMentionedEntityName(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const stripped = stripTrackChangeTags(raw).trim();
  if (!stripped) return undefined;
  return stripped.replace(/\s+/g, " ");
}

function shouldEnableForeignLanguageNaturalToneExperiment(): boolean {
  if (typeof window === "undefined") return false;
  return isChatNaturalToneExperimentEnabledFromLocationParts(window.location.search, window.location.hash);
}

/**
 * Prefer root `clientActions`; fall back to `rawResult.clientActions`; for `apply_ui_action` optionally parse arguments.
 */
function extractClientActionsFromToolCompleteChunk(chunk: Record<string, unknown>): unknown[] {
  const dedupeKey = (value: unknown): string => {
    if (value == null) return String(value);
    if (typeof value !== "object") return `${typeof value}:${String(value)}`;
    if (Array.isArray(value)) return `[${value.map((entry) => dedupeKey(entry)).join(",")}]`;
    const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([k, v]) => `${k}:${dedupeKey(v)}`).join(",")}}`;
  };

  /** Merge payloads: a nonempty but useless `clientActions` array must not hide valid `arguments.actions`. */
  const out: unknown[] = [];
  const seen = new Set<string>();
  const append = (arr: unknown[]) => {
    for (const item of arr) {
      const key = dedupeKey(item);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
  };

  const top = chunk.clientActions;
  if (Array.isArray(top) && top.length > 0) append(top);

  const raw = chunk.rawResult as { clientActions?: unknown } | undefined;
  if (raw?.clientActions != null && Array.isArray(raw.clientActions) && raw.clientActions.length > 0) {
    append(raw.clientActions);
  }

  const toolName = typeof chunk.tool === "string" ? chunk.tool : "";
  if (toolName === "apply_ui_action") {
    const args = (chunk.arguments as { actions?: unknown } | undefined)?.actions;
    if (Array.isArray(args) && args.length > 0) append(args);
  }

  return out;
}

function parseClientUiActionsFromChunk(raw: unknown): ClientUiAction[] {
  if (!Array.isArray(raw)) return [];
  const out: ClientUiAction[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const type = (item as { type?: unknown }).type;
    if (type === "set_agentic_chat_mode") {
      const mode = (item as { mode?: unknown }).mode;
      if (mode === "write" || mode === "chat") {
        out.push({ type: "set_agentic_chat_mode", mode });
      }
    } else if (type === "refresh_project") {
      out.push({ type: "refresh_project" });
    } else if (type === "set_active_document") {
      const documentId = (item as { document_id?: unknown }).document_id;
      if (typeof documentId === "string" && documentId.trim().length > 0) {
        out.push({ type: "set_active_document", document_id: documentId.trim() });
      }
    } else if (type === "refresh_document_draft_scenes") {
      const documentId = (item as { document_id?: unknown }).document_id;
      if (typeof documentId === "string" && documentId.trim().length > 0) {
        out.push({ type: "refresh_document_draft_scenes", document_id: documentId.trim() });
      }
    } else if (type === "apply_manuscript_replacement") {
      const documentId = (item as { document_id?: unknown }).document_id;
      const oldText = (item as { old_text?: unknown }).old_text;
      const newText = (item as { new_text?: unknown }).new_text;
      const plainTextStart = (item as { plain_text_start?: unknown }).plain_text_start;
      const plainTextEnd = (item as { plain_text_end?: unknown }).plain_text_end;
      const pmFrom = (item as { pm_from?: unknown }).pm_from;
      const pmTo = (item as { pm_to?: unknown }).pm_to;
      if (
        typeof documentId === "string"
        && documentId.trim().length > 0
        && typeof oldText === "string"
        && oldText.length > 0
        && typeof newText === "string"
      ) {
        const plainStart = Number(plainTextStart);
        const plainEnd = Number(plainTextEnd);
        const hasPlainRange =
          Number.isFinite(plainStart)
          && Number.isFinite(plainEnd)
          && plainEnd > plainStart;
        out.push({
          type: "apply_manuscript_replacement",
          document_id: documentId.trim(),
          old_text: oldText,
          new_text: newText,
          ...(hasPlainRange
            ? { plain_text_start: Math.floor(plainStart), plain_text_end: Math.floor(plainEnd) }
            : {}),
          ...(Number.isFinite(Number(pmFrom)) && Number.isFinite(Number(pmTo)) && Number(pmTo) > Number(pmFrom)
            ? { pm_from: Math.floor(Number(pmFrom)), pm_to: Math.floor(Number(pmTo)) }
            : {}),
          expected_document_revision: String(
            (item as { expected_document_revision?: unknown }).expected_document_revision ?? "",
          ),
        });
      }
    } else if (type === "highlight_agent_edit") {
      const documentId = (item as { document_id?: unknown }).document_id;
      const highlightSnippet = (item as { highlight_snippet?: unknown }).highlight_snippet;
      const highlightFullDocument = (item as { highlight_full_document?: unknown }).highlight_full_document === true;
      if (
        typeof documentId === "string" &&
        documentId.trim().length > 0 &&
        (
          highlightFullDocument ||
          (typeof highlightSnippet === "string" && highlightSnippet.trim().length > 0)
        )
      ) {
        out.push({
          type: "highlight_agent_edit",
          document_id: documentId.trim(),
          ...(typeof highlightSnippet === "string" && highlightSnippet.trim().length > 0
            ? { highlight_snippet: highlightSnippet.trim() }
            : {}),
          ...(highlightFullDocument ? { highlight_full_document: true } : {}),
        });
      }
    } else if (type === "highlight_manuscript") {
      const passage = (item as { passage?: unknown }).passage;
      const matchAll = (item as { match_all?: unknown }).match_all === true;
      if (typeof passage === "string" && passage.trim()) {
        out.push({
          type: "highlight_manuscript",
          passage: passage.trim(),
          ...(matchAll ? { match_all: true as const } : {}),
        });
      }
    } else if (type === "highlight_manuscript_batch") {
      const rawPassages = (item as { passages?: unknown }).passages;
      const focusIndex = (item as { focusIndex?: unknown }).focusIndex;
      const matchAll = (item as { match_all?: unknown }).match_all === true;
      if (Array.isArray(rawPassages)) {
        const passages = rawPassages
          .map((p) => (typeof p === "string" ? p.trim() : ""))
          .filter((p): p is string => !!p);
        if (passages.length > 0) {
          out.push({
            type: "highlight_manuscript_batch",
            passages,
            ...(Number.isInteger(focusIndex) && Number(focusIndex) >= 0 ? { focusIndex: Number(focusIndex) } : {}),
            ...(matchAll ? { match_all: true as const } : {}),
          });
        }
      }
    } else if (type === "clear_manuscript_highlights") {
      const rawColor = (item as { color?: unknown }).color;
      const color = typeof rawColor === "string" && rawColor.trim() ? rawColor.trim() : undefined;
      out.push({
        type: "clear_manuscript_highlights",
        ...(color ? { color } : {}),
      });
    } else if (type === "set_writing_card_count") {
      const target = (item as { target?: unknown }).target;
      const count = (item as { count?: unknown }).count;
      if (
        (target === "write" || target === "rewrite")
        && typeof count === "number"
        && Number.isFinite(count)
        && Number.isInteger(count)
      ) {
        out.push({ type: "set_writing_card_count", target, count });
      }
    }
  }
  return out;
}

const STREAM_CLIENT_UI_ACTION_TYPES = new Set([
  "set_agentic_chat_mode",
  "set_writing_card_count",
  "refresh_project",
  "set_active_document",
  "highlight_agent_edit",
  "apply_manuscript_replacement",
  "refresh_document_draft_scenes",
]);

async function applySetWritingCardCountAction(
  action: Extract<ClientUiAction, { type: "set_writing_card_count" }>,
  actionsForSentry: unknown,
): Promise<void> {
  const { useSudowriteStore } = await import("src/stores/sudowriteStore");
  const store = useSudowriteStore.getState();
  const key = action.target === "write" ? "wormholeOutputCards" : "rewriteOutputCards";
  const latestEditorOptions = store.editorOptions || {};
  store.setEditorOptions({
    ...latestEditorOptions,
    [key]: action.count,
  });
  const userData = store.userData;
  if (!userData?.update) return;
  try {
    await userData.update({ [key]: action.count });
  } catch (err) {
    Sentry.captureException(err, {
      tags: { feature: "apply_client_ui_actions" },
      extra: { clientUiActions: actionsForSentry, attemptedAction: action },
    });
  }
}

function extractLegacyClientUiActions(raw: unknown): unknown[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((item) => {
    if (!item || typeof item !== "object") return true;
    const type = (item as { type?: unknown }).type;
    return typeof type !== "string" || !STREAM_CLIENT_UI_ACTION_TYPES.has(type);
  });
}

function suppressDocumentSwitchForOffScreenFeedback(
  parsedActions: ClientUiAction[],
  allActions: unknown[],
): ClientUiAction[] {
  const feedbackDocumentIds = new Set(
    allActions.flatMap((action) => {
      if (!action || typeof action !== "object") return [];
      const candidate = action as { type?: unknown; document_id?: unknown };
      return candidate.type === "run_manuscript_feedback" && typeof candidate.document_id === "string"
        ? [candidate.document_id]
        : [];
    }),
  );
  return parsedActions.filter((action) =>
    action.type !== "set_active_document" || !feedbackDocumentIds.has(action.document_id));
}

/** Applies agent-requested UI updates streamed with tool_complete (e.g. chat mode toggle). */
async function applyClientUiActionsFromStream(actions: ClientUiAction[] | undefined) {
  if (!actions?.length) return;
  try {
    const { useSudowriteStore } = await import("src/stores/sudowriteStore");
    const userData = useSudowriteStore.getState().userData;
    // Match PersistentChatUserSettings / DEFAULT_AGENTIC_CHAT_MODE when userData has no stored mode.
    let current =
      (userData?.data?.agenticChatMode?.value as string | undefined) ?? DEFAULT_AGENTIC_CHAT_MODE.value;
    for (const action of actions) {
      if (action.type === "set_agentic_chat_mode") {
        if (!userData) {
          continue;
        }
        if (current !== action.mode) {
          maybeMarkAutoSwitchedFromChatToWrite(current, action.mode);
          if (current === "chat" && action.mode === "write") {
            AppEventPublisher.agenticChat.modeSwitch(AgenticChatMode.Chat, AgenticChatMode.Write);
          } else if (current === "write" && action.mode === "chat") {
            AppEventPublisher.agenticChat.modeSwitch(AgenticChatMode.Write, AgenticChatMode.Chat);
          }
          try {
            await userData.update({ agenticChatMode: { value: action.mode } });
            current = action.mode;
          } catch (err) {
            Sentry.captureException(err, {
              tags: { feature: "apply_client_ui_actions" },
              extra: { clientUiActions: actions, attemptedAction: action },
            });
            /* Continue remaining actions so refresh_project / set_active_document still apply */
          }
        }
      } else if (action.type === "refresh_project") {
        try {
          const { useProjectStore } = await import("src/stores/projectStore");
          const project = useProjectStore.getState().project;
          if (project) {
            await project.fetch();
            if (useProjectStore.getState().project?.id !== project.id) continue;
            await useProjectStore.getState().setProject(project);
            await syncStoryEngineScenesIntoStoryBible(project.id);
          }
        } catch (err) {
          Sentry.captureException(err, {
            tags: { feature: "apply_client_ui_actions" },
            extra: { clientUiActions: actions, attemptedAction: action },
          });
        }
      } else if (action.type === "set_active_document") {
        try {
          const { useProjectStore } = await import("src/stores/projectStore");
          await useProjectStore.getState().setDocumentId(action.document_id);
        } catch (err) {
          Sentry.captureException(err, {
            tags: { feature: "apply_client_ui_actions" },
            extra: { clientUiActions: actions, attemptedAction: action },
          });
        }
      } else if (action.type === "highlight_agent_edit") {
        try {
          window.dispatchEvent(
            new CustomEvent("sudo-agent-highlight-edit", {
              detail: {
                documentId: action.document_id,
                highlightSnippet: action.highlight_snippet,
                highlightFullDocument: action.highlight_full_document === true,
              },
            }),
          );
        } catch (err) {
          Sentry.captureException(err, {
            tags: { feature: "apply_client_ui_actions" },
            extra: { clientUiActions: actions, attemptedAction: action },
          });
        }
      } else if (action.type === "apply_manuscript_replacement") {
        try {
          window.dispatchEvent(
            new CustomEvent("sudo-agent-apply-manuscript-replacement", {
              detail: {
                documentId: action.document_id,
                oldText: action.old_text,
                newText: action.new_text,
                plainTextStart: action.plain_text_start,
                plainTextEnd: action.plain_text_end,
                pmFrom: action.pm_from,
                pmTo: action.pm_to,
                expectedDocumentRevision: action.expected_document_revision,
              },
            }),
          );
        } catch (err) {
          Sentry.captureException(err, {
            tags: { feature: "apply_client_ui_actions" },
            extra: { clientUiActions: actions, attemptedAction: action },
          });
        }
      } else if (action.type === "refresh_document_draft_scenes") {
        const documentId = action.document_id.trim();
        try {
          const Auth = (await import("src/Auth")).default;
          const scenes = await Auth.fetchWithIdToken(`/api/scenes/`, { documentId });
          const { useStoryBibleStore } = await import("src/stores/storyBibleStore");
          const sb = useStoryBibleStore.getState();
          sb.setDocumentScenes(documentId, scenes);
          sb.bumpDraftScenesAgentRefresh(documentId);
          window.dispatchEvent(
            new CustomEvent("sudo-agent-refresh-draft-scenes", {
              detail: { documentId },
            }),
          );
        } catch (err) {
          Sentry.captureException(err, {
            tags: { feature: "apply_client_ui_actions", action: "refresh_document_draft_scenes" },
            extra: { clientUiActions: actions, attemptedAction: action, documentId },
          });
        }
      } else if (action.type === "set_writing_card_count") {
        await applySetWritingCardCountAction(action, actions);
      }
    }
  } catch (err) {
    Sentry.captureException(err, {
      tags: { feature: "apply_client_ui_actions" },
      extra: { clientUiActions: actions },
    });
  }
}

interface CreatedCommentInfo {
  commentId: string;
  annotationId: string;
  documentId: string;
  line_number: number;
  markdownLink: string;
  content: string;
  quoted_text: string;
  suggestion: string | null;
  feedback_type: string;
  feedback_category: string | null;
}

type MutationToolResultPayload = {
  action?: string;
  succeeded?: unknown;
};

function extractMutationToolResultPayload(chunk: any): MutationToolResultPayload | null {
  const rawResult = chunk?.rawResult;
  if (!rawResult || typeof rawResult !== "object") return null;
  const data = (rawResult as { data?: unknown }).data;
  if (typeof data !== "string" || !data.trim()) return null;
  try {
    return JSON.parse(data) as MutationToolResultPayload;
  } catch {
    return null;
  }
}

function extractCreatedCommentsFromToolResult(chunk: any): CreatedCommentInfo[] | null {
  const parsed = extractMutationToolResultPayload(chunk) as { created?: unknown } | null;
  if (parsed && Array.isArray(parsed.created) && parsed.created.length > 0) {
    return parsed.created as CreatedCommentInfo[];
  }
  return null;
}

function didMutateManuscriptCommentsCreateComments(chunk: any, newStatus: "complete" | "error"): boolean {
  if (newStatus !== "complete") return false;
  const toolName = typeof chunk?.tool === "string" ? chunk.tool.trim().toLowerCase() : "";
  if (toolName !== "mutate_manuscript_comments") return false;
  return extractCreatedCommentsFromToolResult(chunk) !== null;
}

function maybeOpenCommentSidebarAfterCommentCreate(chunk: any, newStatus: "complete" | "error"): void {
  if (newStatus !== "complete") return;
  const created = extractCreatedCommentsFromToolResult(chunk);
  if (!created || created.length === 0) return;

  try {
    const state = useCommentStore.getState();
    const activeDocumentId = String(state.documentId ?? "").trim();
    const createdForActiveDocument = created.filter(
      (c) => String(c?.documentId ?? "").trim() === activeDocumentId,
    );
    if (createdForActiveDocument.length === 0) {
      // Comments were created on a different manuscript than the one currently open.
      // Do not auto-open/modify the sidebar on this page.
      return;
    }

    // Add optimistic comments immediately so they appear in the sidebar
    // before the SWR revalidation completes
    const invocationId = chunk?.rawResult?.invocationId || "agentic-chat";
    if (!state.showComments) {
      state.setShowComments(true);
    }
    state.addOptimisticAIComments(createdForActiveDocument, null, invocationId);

    // Trigger SWR revalidation so freshly created comments are confirmed by server data
    // Use a delay to work around SWR's 2-second dedup window and ensure DB transaction completes.
    setTimeout(() => {
      try {
        const { mutateComments, isMutateCommentsReady, documentId } = useCommentStore.getState();
        const isStoreMutateReady = isMutateCommentsReady && documentId;

        if (isStoreMutateReady) {
          void mutateComments().catch((error: unknown) => {
            Sentry.captureException(error, {
              tags: { feature: "mutate_manuscript_comments_refresh_comments" },
              extra: { chunk },
            });
          });
        } else if (documentId) {
          // Fallback: use global SWR mutate with the cache key pattern from useComments
          const allKeys = Array.from((globalSWRMutate as any).keys?.() ?? []);
          const commentKeys = allKeys.filter((key): key is [string, string, boolean] => {
            if (!Array.isArray(key)) return false;
            return key[0] === documentId;
          });

          for (const key of commentKeys) {
            void globalSWRMutate(key);
          }
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: "mutate_manuscript_comments_refresh_comments" },
          extra: { chunk },
        });
      }
    }, 500);
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: "mutate_manuscript_comments_open_sidebar" },
      extra: { chunk },
    });
  }
}

function maybeApplyOptimisticCommentDelete(chunk: any, newStatus: "complete" | "error"): void {
  if (newStatus !== "complete") return;
  const toolName = typeof chunk?.tool === "string" ? chunk.tool.trim().toLowerCase() : "";
  if (toolName !== "mutate_manuscript_comments") return;

  const payload = extractMutationToolResultPayload(chunk);
  const action = String(payload?.action ?? "").toLowerCase();
  if (action !== "delete") return;
  if (!Array.isArray(payload?.succeeded) || payload.succeeded.length === 0) return;

  const deletedIds = new Set(
    (payload.succeeded as unknown[]).map((id) => String(id ?? "").trim()).filter(Boolean),
  );
  if (deletedIds.size === 0) return;

  try {
    const state = useCommentStore.getState();
    const currentAllComments = Array.isArray(state.allComments) ? state.allComments : [];
    if (currentAllComments.length === 0) return;

    const nextAllComments = currentAllComments.filter((comment: any) => !deletedIds.has(String(comment?.id ?? "")));
    if (nextAllComments.length === currentAllComments.length) return;

    state.setComments(nextAllComments);
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: "mutate_manuscript_comments_optimistic_delete" },
      extra: { chunk },
    });
  }
}

async function applyClientUiActions(actionsOrChunk: unknown): Promise<void> {
  const actions: unknown[] = Array.isArray(actionsOrChunk)
    ? actionsOrChunk
    : actionsOrChunk && typeof actionsOrChunk === "object"
      ? extractClientActionsFromToolCompleteChunk(actionsOrChunk as Record<string, unknown>)
      : [];
  if (actions.length === 0) return;
  const { mutate } = await import("swr");
  const { useSudowriteStore } = await import("src/stores/sudowriteStore");
  const sudowriteStore = useSudowriteStore.getState();
  const userData = sudowriteStore.userData;
  const hasPaidTierManuscriptFeedbackAccess =
    sudowriteStore.subscription?.hasPaidTierManuscriptFeedbackAccess ??
    sudowriteStore.subscriptionJSON?.hasPaidTierManuscriptFeedbackAccess ??
    false;

  for (const action of actions) {
    if (!action || typeof action !== "object") continue;
    const uiAction = action as {
      type?: unknown;
      mode?: unknown;
      keys?: unknown;
      projectId?: unknown;
      projectTitle?: unknown;
      documentId?: unknown;
      document_id?: unknown;
      feedback_type_names?: unknown;
      feedback_run_id?: unknown;
    };

    if (uiAction.type === "run_manuscript_feedback") {
      if (!hasPaidTierManuscriptFeedbackAccess) {
        usePersistentChatStore.setState({
          errorText: FEEDBACK_PLAN_UPSELL_CHAT_MESSAGE,
          activeCommenting: null,
          ...COMMENTING_PROGRESS_RESET,
        });
        continue;
      }
      const { useStoryBibleStore } = await import("src/stores/storyBibleStore");
      const namesRaw = uiAction.feedback_type_names;
      const names =
        Array.isArray(namesRaw)
          ? namesRaw.filter((n): n is string => typeof n === "string" && n.trim().length > 0).map((n) => n.trim())
          : [];
      if (!names.length) {
        Sentry.captureMessage("Ignored run_manuscript_feedback client action without feedback_type_names", {
          level: "warning",
          tags: { feature: "apply_client_ui_actions", action: "run_manuscript_feedback" },
        });
        continue;
      }
      usePersistentChatStore.setState((state) => ({
        activeCommenting: {
          id:
            state.activeCommenting?.status === "in_progress" && state.activeCommenting.id.startsWith("feedback-run-")
              ? state.activeCommenting.id
              : `feedback-run-${Date.now()}`,
          status: "in_progress",
          commentsCreated: 0,
          feedbackTypes: names,
          customFeedbackInstructions: [],
        },
        ...commentingProgressFieldsForStarted(),
      }));
      const targetDocumentId =
        typeof uiAction.document_id === "string" && uiAction.document_id.trim().length > 0
          ? uiAction.document_id.trim()
          : undefined;
      if (targetDocumentId) {
        useStoryBibleStore.getState().requestManuscriptFeedbackFromAgent(names, { documentId: targetDocumentId, ...(typeof uiAction.feedback_run_id === "string" ? { feedbackRunId: uiAction.feedback_run_id } : {}) });
      } else if (typeof uiAction.feedback_run_id === "string") {
        useStoryBibleStore.getState().requestManuscriptFeedbackFromAgent(names, { feedbackRunId: uiAction.feedback_run_id });
      } else {
        useStoryBibleStore.getState().requestManuscriptFeedbackFromAgent(names);
      }
      continue;
    }
    if (uiAction.type === "refresh_swr" && Array.isArray(uiAction.keys)) {
      for (const key of uiAction.keys) {
        if (typeof key === "string" && key.length > 0) {
          try {
            void mutate(key);
          } catch (error) {
            Sentry.captureException(error, {
              tags: { feature: "apply_client_ui_actions", action: "refresh_swr" },
              extra: { key },
            });
          }
        }
      }
      continue;
    }
    if (uiAction.type === "set_agentic_chat_mode") {
      if (!userData) continue;
      const nextMode = uiAction.mode;
      if (nextMode !== "write" && nextMode !== "chat") continue;
      const currentMode = userData?.data?.agenticChatMode?.value;
      if (currentMode === nextMode) continue;
      maybeMarkAutoSwitchedFromChatToWrite(
        typeof currentMode === "string" ? currentMode : undefined,
        nextMode,
      );
      if (currentMode === "write" || currentMode === "chat") {
        AppEventPublisher.agenticChat.modeSwitch(
          currentMode === "write" ? AgenticChatMode.Write : AgenticChatMode.Chat,
          nextMode === "write" ? AgenticChatMode.Write : AgenticChatMode.Chat,
        );
      }
      try {
        await userData.update({ agenticChatMode: { value: nextMode } });
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: "apply_client_ui_actions" },
          extra: { clientUiActions: actions, nextMode },
        });
      }
      continue;
    }

    if (uiAction.type === "set_writing_card_count") {
      const target = (uiAction as { target?: unknown }).target;
      const count = (uiAction as { count?: unknown }).count;
      if (
        (target === "write" || target === "rewrite")
        && typeof count === "number"
        && Number.isFinite(count)
        && Number.isInteger(count)
      ) {
        await applySetWritingCardCountAction(
          { type: "set_writing_card_count", target, count },
          actions,
        );
      }
      continue;
    }

    if (uiAction.type === "refresh_comments") {
      const documentId = typeof uiAction.documentId === "string" ? uiAction.documentId : "";
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const actionType = (uiAction as any).action;
      if (documentId) {
        try {
          const { useCommentStore } = await import("src/stores/commentStore");
          const initialState = useCommentStore.getState();
          const { setComments } = initialState;

          // Only optimistically clear comments for DELETE actions to avoid sidebar flicker on updates
          // For create/update/accept/reject/resolve, let SWR refresh naturally without clearing
          if (initialState.documentId === documentId && actionType === "delete") {
            setComments([]);
            // Also clear annotation highlights from the editor
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const tiptapEditor = window.tiptap as any;
            if (tiptapEditor?.commands?.unsetAllAnnotations) {
              tiptapEditor.commands.unsetAllAnnotations();
            }
          }

          // Helper to trigger comment refresh with fallback to global SWR mutate
          const triggerCommentRefresh = async () => {
            // Small delay ensures DB/socket side effects settle before fetch.
            await new Promise((resolve) => setTimeout(resolve, 150));

            // Re-read store state at execution time (avoid stale closures).
            const latestState = useCommentStore.getState();
            const {
              documentId: latestStoreDocumentId,
              mutateComments: latestMutateComments,
              setComments: setLatestComments,
              setCommentsLoaded,
            } = latestState;

            // For the currently visible document, fetch and apply immediately so UI updates without reload.
            if (latestStoreDocumentId === documentId) {
              try {
                const { default: CommentModel } = await import("src/models/Comment");
                const freshComments = await CommentModel.getAll(documentId, {
                  includeResolved: true,
                });
                setLatestComments(freshComments);
                setCommentsLoaded(true);
              } catch (error) {
                Sentry.captureException(error, {
                  tags: { feature: "apply_client_ui_actions", action: "refresh_comments_fetch" },
                  extra: { documentId },
                });
              }
            }

            // Revalidate SWR cache to keep hooks in sync.
            const isStoreMutateReady =
              typeof latestMutateComments === "function" &&
              latestMutateComments.length > 0 &&
              latestStoreDocumentId === documentId;

            if (isStoreMutateReady) {
              await latestMutateComments();
              return;
            }

            // Fallback: use global SWR mutate with the cache key
            // The cache key format matches useComments hook: [documentId, userId, isAuthor]
            // We need to match any key that starts with the documentId
            const allKeys = Array.from((globalSWRMutate as any).keys?.() ?? []);
            const commentKeys = allKeys.filter((key): key is [string, string, boolean] => {
              if (!Array.isArray(key)) return false;
              return key[0] === documentId;
            });

            if (commentKeys.length > 0) {
              for (const key of commentKeys) {
                await globalSWRMutate(key);
              }
            }
          };

          void triggerCommentRefresh().catch((error) => {
            Sentry.captureException(error, {
              tags: { feature: "apply_client_ui_actions", action: "refresh_comments" },
              extra: { documentId },
            });
          });
        } catch (error) {
          Sentry.captureException(error, {
            tags: { feature: "apply_client_ui_actions", action: "refresh_comments" },
            extra: { documentId },
          });
        }
      }
      continue;
    }

    if (uiAction.type === "highlight_manuscript") {
      const passage = typeof (uiAction as { passage?: unknown }).passage === "string"
        ? (uiAction as { passage: string }).passage.trim()
        : "";
      if (!passage) continue;
      try {
        const { useProjectStore } = await import("src/stores/projectStore");
        const { useSudowriteStore } = await import("src/stores/sudowriteStore");
        const { normalizePreferredManuscriptHighlightLight, resolveHighlightColorLight } = await import("~shared/highlightColors");
        const { applyAgentManuscriptHighlight } = await import("src/components/PersistentChat/applyAgentManuscriptHighlight");
        const editor = useProjectStore.getState().getManuscriptEditor?.();
        const rawColor = (uiAction as { color?: unknown }).color;
        const requestedColor = typeof rawColor === "string" ? resolveHighlightColorLight(rawColor) : undefined;
        const rawPreferred = useSudowriteStore.getState().userData?.data?.preferredManuscriptHighlightLight;
        const preferredHighlightLight = requestedColor ?? normalizePreferredManuscriptHighlightLight(rawPreferred);
        const matchAll = (uiAction as { match_all?: unknown }).match_all === true;
        applyAgentManuscriptHighlight(editor, passage, {
          ...(preferredHighlightLight ? { preferredHighlightLight } : {}),
          ...(matchAll ? { matchAll: true } : {}),
        });
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: "apply_client_ui_actions", action: "highlight_manuscript" },
        });
      }
      continue;
    }

    if (uiAction.type === "highlight_manuscript_batch") {
      const rawPassages = (uiAction as { passages?: unknown }).passages;
      const focusIdx = (uiAction as { focusIndex?: unknown }).focusIndex;
      const passages = Array.isArray(rawPassages)
        ? rawPassages.map((p) => (typeof p === "string" ? p.trim() : "")).filter(Boolean)
        : [];
      if (passages.length === 0) continue;
      try {
        const { useProjectStore } = await import("src/stores/projectStore");
        const { useSudowriteStore } = await import("src/stores/sudowriteStore");
        const { normalizePreferredManuscriptHighlightLight, resolveHighlightColorLight } = await import("~shared/highlightColors");
        const { applyAgentManuscriptHighlightBatch } = await import(
          "src/components/PersistentChat/applyAgentManuscriptHighlight"
        );
        const editor = useProjectStore.getState().getManuscriptEditor?.();
        const focusIndex =
          Number.isInteger(focusIdx) && Number(focusIdx) >= 0 ? Number(focusIdx) : undefined;
        const rawColor = (uiAction as { color?: unknown }).color;
        const requestedColor = typeof rawColor === "string" ? resolveHighlightColorLight(rawColor) : undefined;
        const rawPreferred = useSudowriteStore.getState().userData?.data?.preferredManuscriptHighlightLight;
        const preferredHighlightLight = requestedColor ?? normalizePreferredManuscriptHighlightLight(rawPreferred);
        const matchAll = (uiAction as { match_all?: unknown }).match_all === true;
        applyAgentManuscriptHighlightBatch(editor, passages, {
          focusIndex,
          selectText: passages.length <= 1,
          ...(preferredHighlightLight ? { preferredHighlightLight } : {}),
          ...(matchAll ? { matchAll: true } : {}),
        });
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: "apply_client_ui_actions", action: "highlight_manuscript_batch" },
        });
      }
      continue;
    }

    if (uiAction.type === "clear_manuscript_highlights") {
      try {
        const { useProjectStore } = await import("src/stores/projectStore");
        const { clearAgentManuscriptHighlights } = await import(
          "src/components/PersistentChat/applyAgentManuscriptHighlight"
        );
        const editor = useProjectStore.getState().getManuscriptEditor?.();
        const rawColor = (uiAction as { color?: unknown }).color;
        const color = typeof rawColor === "string" && rawColor.trim() ? rawColor.trim() : undefined;
        const result = clearAgentManuscriptHighlights(editor, color ? { color } : undefined);
        if (result.ok === false && result.reason === "invalid_color") {
          Sentry.captureMessage("apply_ui_action clear_manuscript_highlights: invalid color", {
            level: "warning",
            extra: { color: rawColor },
          });
          continue;
        }
        if (
          result.ok &&
          (result.scope === "all" ||
            (result.scope === "color" && result.remaining === 0))
        ) {
          usePersistentChatStore.getState().setActiveHighlight("");
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: "apply_client_ui_actions", action: "clear_manuscript_highlights" },
        });
      }
      continue;
    }

    if (uiAction.type === "select_series_outline") {
      try {
        const targetProjectId = typeof uiAction.projectId === "string" ? uiAction.projectId : "";
        if (!targetProjectId) continue;

        const { useSeriesStore } = await import("src/stores/seriesStore");
        const { useStructuredOutlineStore } = await import("src/components/StoryBible/StructuredOutline/StructuredOutlineStore");
        const { useStoryBibleStore } = await import("src/stores/storyBibleStore");

        const seriesStore = useSeriesStore.getState();
        const outlineStore = useStructuredOutlineStore.getState();
        const storyBibleStore = useStoryBibleStore.getState();

        const contents = seriesStore.contents || [];
        const targetProject =
          contents.find((c: any) => c.projectId === targetProjectId) ||
          {
            projectId: targetProjectId,
            title: typeof uiAction.projectTitle === "string" && uiAction.projectTitle.trim() ? uiAction.projectTitle.trim() : "Series Book",
          };

        seriesStore.setSelectedSeriesProjectId(targetProjectId);
        seriesStore.setSelectedProject("outline", targetProject);
        storyBibleStore.setIsActive(true);
        const { outline } = await Auth.fetchWithIdToken(`/api/outline`, {
          projectId: targetProjectId,
        });
        outlineStore.setOutline(outline);
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: "apply_client_ui_actions" },
          extra: { clientUiActions: actions, uiActionType: "select_series_outline", projectId: uiAction.projectId },
        });
      }
    }
  }
}

async function generateChatTitle(messages: PersistentChatMessageData[], projectId: string): Promise<string | null> {
  return new Promise((resolve) => {
    let title = "";

    Auth.streamWithIdToken(
      "/api/ai/agenticChat/generateTitle",
      {
        history: messages,
        projectId,
        documentId: "",
      },
      null,
      (chunk: any) => {
        if (chunk.text) {
          title += chunk.text;
        }
      },
      () => {
        let result = title?.trim() || null;
        if (result) {
          if (result.startsWith('"')) result = result.slice(1);
          if (result.endsWith('"')) result = result.slice(0, -1);
        }
        resolve(result);
      },
      () => {
        resolve(null);
      }
    );
  });
}

async function updateChatTitle(chatId: string, title: string): Promise<void> {
  await Auth.fetchWithIdToken(`/api/chats/${chatId}`, {
    title,
    method: "PUT",
  });
}

export type AgenticToolRun = {
  callId: string;
  name: string;
  status: "running" | "complete" | "error";
  summary?: string;
};

export type AgenticOutlineUpdate = {
  summary: string;
  chapterIds: string[];
  rationale?: string;
  oldContent?: { name: string; summary: string }[];
  newContent?: { name: string; summary: string }[];
};

export type AgenticCharacterUpdate = {
  characterId: string;
  characterName: string;
  updatedFields: string[];
  oldFields: Record<string, string>;
  newFields: Record<string, string>;
};

export type AgenticWorldbuildingUpdate = {
  entryId: string;
  entryName: string;
  updatedFields: string[];
  oldFields: Record<string, string>;
  newFields: Record<string, string>;
};

export type AgenticMetaUpdate = {
  updatedFields: string[];
  oldFields: Record<string, string>;
  newFields: Record<string, string>;
};

export type SuggestedAction = {
  action: string;
  chapterId: string;
  chapterName: string;
  message: string;
};

export type AgentResponsePhase = "idle" | "connecting" | "streaming";

type ReferencedComment = {
  commentId: string;
  documentId?: string;
  previewText?: string;
};


function dedupeReferencedComments(references: ReferencedComment[]): ReferencedComment[] {
  const seen = new Set<string>();
  const out: ReferencedComment[] = [];
  for (const reference of references) {
    const commentId = String(reference.commentId ?? "").trim();
    if (!commentId) continue;
    const key = `${commentId}::${reference.documentId ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      commentId,
      documentId: reference.documentId,
      previewText: reference.previewText,
    });
  }
  return out;
}

function extractReferencedCommentIdsFromMutationArgs(rawArgs: unknown): string[] {
  if (!rawArgs || typeof rawArgs !== "object") return [];
  const args = rawArgs as { action?: unknown; comment_id?: unknown; comment_ids?: unknown };
  const action = typeof args.action === "string" ? args.action : "";
  if (action !== "accept" && action !== "reject" && action !== "delete" && action !== "update") {
    return [];
  }
  const ids = new Set<string>();
  if (typeof args.comment_id === "string" && args.comment_id.trim()) {
    ids.add(args.comment_id.trim());
  }
  if (Array.isArray(args.comment_ids)) {
    for (const id of args.comment_ids) {
      const normalized = String(id ?? "").trim();
      if (normalized) ids.add(normalized);
    }
  }
  return [...ids];
}

const getToolIcon = (toolName: string, elementType?: string): ThinkingTaskIcon => {
  const name = toolName.toLowerCase();
  const isReadOp = name === "read" || name === "read_file" || name === "list" || name.includes("read") || name === "view outline";
  const isWriteOp =
    name === "write" ||
    name === "write_file" ||
    name === "edit_outline" ||
    name === "create_file" ||
    name === "delete_file" ||
    name.includes("update");
  if (isReadOp && !isWriteOp) {
    if (elementType === "character") return "characters";
    if (elementType === "worldbuilding") return "worldbuilding";
    if (elementType === "meta") return "meta";
    if (elementType === "outline") return "list";
    return "book";
  }
  if (isWriteOp) {
    if (elementType === "character") return "characters";
    if (elementType === "worldbuilding") return "worldbuilding";
    if (elementType === "meta") return "meta";
    return "list";
  }
  if (name === "generate_character") return "characters";
  if (name === "generate_worldbuilding") return "worldbuilding";
  if (name === "create_outline" || name === "reorder_outline") return "list";
  if (isManuscriptStagingToolName(name)) return "book";
  if (name.includes("search") || name.includes("web")) return "search";
  return "lightbulb";
};

const getManuscriptOperationThinkingTitle = (toolArgs?: Record<string, unknown>): string | null => {
  if (!toolArgs?.operation) return null;
  const op = String(toolArgs.operation).toLowerCase();
  if (op === "review_edit_document") return "Reviewing document";
  if (op === "find_replace_document" || op === "multi_find_replace_document") return "Replacing text";
  if (op === "edit_document" || op === "edit_document_batch") return "Editing document";
  if (op === "create_document") return "Creating document";
  if (op === "rename_document") return "Renaming document";
  if (op === "switch_document") return "Opening document";
  if (op === "scan_document_chapters") return "Scanning chapters";
  if (op === "link_document_to_outline_chapter" || op === "unlink_document_from_outline") {
    return "Linking outline";
  }
  return null;
};

const getToolTitle = (
  toolName: string,
  elementType?: string,
  toolArgs?: Record<string, unknown>,
  mode?: "chat" | "write",
): string => {
  const name = toolName.toLowerCase();
  if (isManuscriptStagingToolName(name)) {
    return manuscriptStagingThinkingTitleForMode(mode) ?? "Editing";
  }
  if (name === "mutate_manuscript_comments") {
    const action = (toolArgs?.action as string)?.toLowerCase();
    if (action === "delete") return "Deleting comments";
    if (action === "accept") return "Accepting suggestions";
    if (action === "reject") return "Rejecting suggestions";
    if (action === "resolve") return "Resolving comments";
    return "Commenting";
  }
  const isReadOp = name === "read" || name === "read_file" || name === "list" || name.includes("read") || name === "view outline";
  const isWriteOp =
    name === "write" ||
    name === "write_file" ||
    name === "edit_outline" ||
    name === "create_file" ||
    name === "delete_file" ||
    name.includes("update");
  if (isReadOp && !isWriteOp) {
    const titles: Record<string, string> = { character: "Read Characters", worldbuilding: "Read Worldbuilding", meta: "Read Story Bible", outline: "Read Outline" };
    return titles[elementType ?? ""] || "Read Story Bible";
  }
  if (isWriteOp) {
    const titles: Record<string, string> = { character: "Update Characters", worldbuilding: "Update Worldbuilding", meta: "Update Story Bible", outline: "Update Outline", document: "Update document" };
    return titles[elementType ?? "outline"] || "Update content";
  }
  if (name === "generate_character") return "Generate Character";
  if (name === "generate_worldbuilding") return "Generate Worldbuilding";
  if (name === "generate_write_button_prose") return "Writing prose";
  if (name === "present_manuscript_draft_for_approval") return "Present Draft";
  if (name === "bulk_outline_mutation") return "Outline Edit";
  if (name === "create_outline") return "Create Outline";
  if (name === "reorder_outline") return "Reorder Outline";
  if (name === "web_search" || name === "search web") return "Searching web";
  if (name === "locate_document_text") return "Locating in document";
  if (name === "locate_project_text") return "Locating in project";
  if (name === "read_passages_by_hits") return "Reading passages";
  if (name === "count_text") return "Counting text";
  if (name === "explore") return "Searching project";
  if (name === "find") return "Finding text";
  if (name === "scan_document_chapters") return "Scanning chapters";
  if (name === "sudowrite_docs") return "Searching help docs";
  if (name === "run_feedback") return "Running feedback";
  const manuscriptOperationTitle = getManuscriptOperationThinkingTitle(toolArgs);
  if ((name === "manage_project_documents" || name === "apply_manuscript_edits") && manuscriptOperationTitle) {
    return manuscriptOperationTitle;
  }
  if (name === "manage_project_documents" && toolArgs?.operation) {
    const op = String(toolArgs.operation).toLowerCase();
    if (op === "review_edit_document") return "Reviewing document";
    if (op === "edit_document" || op === "edit_document_batch") return "Editing document";
    if (op === "create_document") return "Creating document";
    if (op === "rename_document") return "Renaming document";
    if (op === "switch_document") return "Opening document";
    if (op === "scan_document_chapters") return "Scanning chapters";
    if (op === "link_document_to_outline_chapter" || op === "unlink_document_from_outline") {
      return "Linking outline";
    }
  }
  if (name === "manage_draft_scenes") return "Updating draft scenes";
  if (name === "apply_ui_action") {
    const fromArgs = getApplyUiActionThinkingTitle(toolArgs);
    if (fromArgs) return fromArgs;
    return "Updating project";
  }
  return toolName.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
};

const removeThinkingPlaceholder = (tasks: ThinkingTask[]) =>
  tasks.filter((task) => task.id !== "thinking-placeholder");

/** True once tool/reasoning timeline entries exist (not just the initial "Thinking" placeholder). */
const hasNonPlaceholderThinkingTasks = (timeline: ThinkingTask[]) =>
  timeline.some((task) => task.id !== "thinking-placeholder");

/**
 * Write prose can stream before `tool_start` (selection fast path) or during nested Write inside
 * `manage_project_documents`. Keep the thinking accordion visible with a write-style placeholder.
 */
const ensureThinkingBoxForStreamedProse = (
  updateThinkingTimeline: (updater: (prev: ThinkingTask[]) => ThinkingTask[]) => void,
  timeline: ThinkingTask[],
) => {
  if (hasNonPlaceholderThinkingTasks(timeline)) {
    const hasRunningWriteTask = timeline.some(
      (task) =>
        task.status === "running"
        && (
          task.isWrite
          || task.toolName === "generate_write_button_prose"
          || task.toolName === "manage_project_documents"
        ),
    );
    if (hasRunningWriteTask) return;
    if (!timeline.some((task) => task.id === "thinking-placeholder")) {
      updateThinkingTimeline((prev) => [
        ...removeThinkingPlaceholder(prev),
        {
          id: "thinking-placeholder",
          icon: "brain",
          title: "Writing",
          status: "running",
          isWrite: true,
        },
      ]);
    }
    return;
  }

  updateThinkingTimeline((prev) => {
    if (prev.length === 0) {
      return [{
        id: "thinking-placeholder",
        icon: "brain",
        title: "Writing",
        status: "running",
        isWrite: true,
      }];
    }
    return prev.map((task) =>
      task.id === "thinking-placeholder"
        ? { ...task, isWrite: true, title: "Writing" }
        : task,
    );
  });
};

function applyProsePreviewChunk(
  current: Record<string, string>,
  chunk: { previewId?: unknown; text?: unknown; reset?: unknown; replace?: unknown; explanation?: unknown; title?: unknown; groupId?: unknown },
  details: Record<string, string> = {},
  priorGroups: PersistentChatState["currentProsePreviewGroups"] = {},
): Pick<PersistentChatState, "currentProsePreview" | "currentProsePreviews" | "currentProsePreviewExplanations" | "currentProsePreviewGroups"> {
  // Older servers lack IDs; their one preview still clears through its own event.
  const id = typeof chunk.previewId === "string" ? chunk.previewId : "legacy";
  const previews = { ...current };
  const explanations = { ...details };
  const groups = { ...priorGroups };
  if (chunk.reset) { delete previews[id]; delete explanations[id]; delete groups[id]; }
  else if (typeof chunk.text === "string" && (chunk.text || Object.prototype.hasOwnProperty.call(previews, id))) {
    if (chunk.text) previews[id] = (chunk.replace === true ? "" : Object.prototype.hasOwnProperty.call(previews, id) ? previews[id] : "") + chunk.text;
    if (typeof chunk.explanation === "string") explanations[id] = chunk.explanation;
    if (typeof chunk.groupId === "string" || typeof chunk.title === "string") {
      groups[id] = { id: typeof chunk.groupId === "string" ? chunk.groupId : groups[id]?.id ?? id,
        title: typeof chunk.title === "string" ? chunk.title : groups[id]?.title ?? "" };
    }
  }
  // Keep the newest active generation visible. Other streams retain their full
  // text, so finishing this generation can restore a sibling without mixing them.
  const active = Object.values(previews);
  return { currentProsePreviewGroups: groups, currentProsePreviewExplanations: explanations, currentProsePreviews: previews, currentProsePreview: active[active.length - 1] ?? "" };
}

const handleStreamedProseChunk = (
  text: string,
  get: () => PersistentChatStore,
  set: (partial: Partial<PersistentChatState> | ((state: PersistentChatState) => Partial<PersistentChatState>)) => void,
) => {
  if (!text) return;
  ensureThinkingBoxForStreamedProse(get().updateThinkingTimeline, get().thinkingTaskTimeline);
  appendStreamingText(text, set);
};

const getThinkingTitleAfterTool = (elementType?: string, isWriteOp?: boolean): string => {
  if (isWriteOp) return "Thinking";
  const titles: Record<string, string> = {
    character: "Analyzing characters",
    worldbuilding: "Analyzing worldbuilding",
    meta: "Analyzing story bible",
    outline: "Analyzing outline",
  };
  return titles[elementType ?? ""] || "Thinking";
};

const isWriteToolName = (toolName: string, toolArgs?: Record<string, unknown>): boolean =>
  isThinkingWriteToolStart(toolName, toolArgs);

function shouldSkipManuscriptStagingThinking(input: {
  toolName: string;
  mode?: "chat" | "write";
}): boolean {
  return isManuscriptStagingToolName(input.toolName) && input.mode === "chat";
}

function shouldSkipThinkingToolTimeline(toolName: string, mode?: "chat" | "write"): boolean {
  return isHiddenThinkingToolTask({
    toolName,
    title: getToolTitle(toolName, undefined, undefined, mode),
  }) || shouldSkipManuscriptStagingThinking({ toolName, mode });
}

/** Tools that restore prior Story Bible state should drop pending diff overlays after refresh. */
function storyBibleRefreshShouldClearPendingDiffs(toolName: string): boolean {
  return toolName === "undo_outline_changes" || toolName === "undo_last_action" || toolName === "restore_session_baseline";
}

function inferStoryBibleRefreshHintFromToolCompleteChunk(
  toolName: string,
  chunk: Record<string, unknown>,
): { characters?: boolean; worldbuilding?: boolean; meta?: boolean; outline?: boolean } | undefined {
  const explicitHint = chunk.storyBibleRefresh && typeof chunk.storyBibleRefresh === "object"
    ? chunk.storyBibleRefresh as { characters?: boolean; worldbuilding?: boolean; meta?: boolean; outline?: boolean }
    : undefined;
  if (explicitHint) return explicitHint;

  const name = toolName.toLowerCase();
  const rawResult = (chunk.rawResult && typeof chunk.rawResult === "object")
    ? chunk.rawResult as Record<string, unknown>
    : {};
  const receipt = (rawResult.storyBibleMutationReceipt && typeof rawResult.storyBibleMutationReceipt === "object")
    ? rawResult.storyBibleMutationReceipt as Record<string, unknown>
    : {};
  const domain = typeof receipt.domain === "string" ? receipt.domain.toLowerCase() : "";
  const elementType = typeof chunk.elementType === "string" ? chunk.elementType.toLowerCase() : "";

  const characters =
    name === "generate_character"
    || name === "generate_cast"
    || name === "regenerate_trait"
    || domain === "character"
    || elementType === "character";
  const worldbuilding =
    name === "generate_worldbuilding"
    || name === "regenerate_worldbuilding_trait"
    || domain === "worldbuilding"
    || elementType === "worldbuilding";
  const meta =
    name === "synopsis_generate"
    || name === "synopsis_rewrite"
    || domain === "meta"
    || elementType === "meta";
  const outline =
    name === "create_outline"
    || name === "edit_outline"
    || name === "bulk_outline_mutation"
    || name === "reorder_outline"
    || elementType === "outline";

  if (!characters && !worldbuilding && !meta && !outline) return undefined;
  return { ...(characters ? { characters: true } : {}), ...(worldbuilding ? { worldbuilding: true } : {}), ...(meta ? { meta: true } : {}), ...(outline ? { outline: true } : {}) };
}

type StoryBibleRefreshHint = { outline?: boolean; characters?: boolean; worldbuilding?: boolean; meta?: boolean };

/** Capture local state before Stop waits for server persistence. Each domain is
 * independently guarded so one dirty editor does not suppress other saved work. */
async function prepareCancelledStoryBibleRefresh(projectId: string, stillCurrent: () => boolean) {
  const [{ useStoryBibleStore }, { useStructuredOutlineStore }] = await Promise.all([
    import("src/stores/storyBibleStore"),
    import("src/components/StoryBible/StructuredOutline/StructuredOutlineStore"),
  ]);
  const initial = useStoryBibleStore.getState();
  const initialOutline = useStructuredOutlineStore.getState();
  const outline = initialOutline.outline;
  const outlineWasDirty = initialOutline.localDirty || initialOutline.restoring;
  const guards = {
    outline: () => {
      const current = useStructuredOutlineStore.getState();
      return !outlineWasDirty && !current.localDirty && !current.restoring
        && current.outline === outline && (!outline || outline.projectId === projectId);
    },
    characters: () => useStoryBibleStore.getState().charactersEntities === initial.charactersEntities,
    worldbuilding: () => useStoryBibleStore.getState().worldbuildingEntries === initial.worldbuildingEntries,
    meta: () => {
      const current = useStoryBibleStore.getState();
      return initial.saved !== false && current.saved !== false
        && current.seed === initial.seed && current.synopsis === initial.synopsis && current.metadata === initial.metadata;
    },
  };
  return async (hint: StoryBibleRefreshHint) => {
    await Promise.allSettled((Object.keys(guards) as (keyof typeof guards)[]).filter(domain => hint?.[domain] === true).map(domain =>
      applyStoryBibleRefreshHint(projectId, { [domain]: true }, false,
        () => stillCurrent() && useProjectStore.getState().project?.id === projectId && guards[domain]()),
    ));
  };
}

/** When the server hints pending entity updates, refresh Story Bible state so the UI matches DB if granular SSE events were missed. */
async function applyStoryBibleRefreshHint(
  projectId: string,
  hint: { characters?: boolean; worldbuilding?: boolean; meta?: boolean; outline?: boolean },
  clearPendingDiffs = false,
  shouldApply?: () => boolean,
) {
  const { useStoryBibleStore } = await import("src/stores/storyBibleStore");
  if (shouldApply && !shouldApply()) return;
  const store = useStoryBibleStore.getState();
  if (!isCurrentStoryBibleOwner(store, projectId)) return;
  try {
    if (hint.outline) {
      const { useStructuredOutlineStore } = await import("src/components/StoryBible/StructuredOutline/StructuredOutlineStore");
      const outlineStore = useStructuredOutlineStore.getState();
      const outlineResponse = await Auth.fetchWithIdToken("/api/outline", { projectId });
      if (shouldApply && !shouldApply()) return;
      if (outlineResponse?.outline) {
        outlineStore.setOutline(outlineResponse.outline);
      }
      if (clearPendingDiffs) outlineStore.clearAllChapterDiffs();
    }
    if (hint.characters || hint.worldbuilding) {
      if (clearPendingDiffs) {
        store.clearAllEntityDiffs();
        store.clearAllPendingNewEntities();
      }
    }
    if (hint.characters) await store.refreshCharacters(projectId, { force: true, shouldApply });
    if (shouldApply && !shouldApply()) return;
    if (hint.worldbuilding) await store.refreshWorldbuildingEntries(projectId, "persistentChatStore.applyStoryBibleRefreshHint", { force: true, shouldApply });
    if (hint.meta) {
      if (clearPendingDiffs) store.clearAllMetaDiffs();
      const results = await Auth.fetchWithIdToken(`/api/story-engine/${projectId}/get`, {});
      if (shouldApply && !shouldApply()) return;
      applyStoryEngineMetaToStore(store, projectId, results);
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { context: "agenticChat:storyBibleRefresh" },
      extra: { projectId, storyBibleRefresh: hint },
    });
  }
}

/** Fingerprint outline content flush relies on (`items` + top-level Story-Bible outline notes). Not a full outline deep-equal. */
function outlineFlushFingerprint(outline: { content?: { items?: unknown; instructions?: unknown } } | null | undefined): string {
  try {
    const content = outline?.content;
    const items = normalizeOutlineItemsField(content?.items);
    const instructions = typeof content?.instructions === "string" ? content.instructions : "";
    return JSON.stringify({ instructions, items: items ?? [] });
  } catch {
    return "";
  }
}

type StructuredOutlineStoreSnapshot = {
  outline?: Record<string, unknown> & { content?: { items?: unknown }; projectId?: string };
  localDirty?: boolean;
  restoring?: boolean;
  setOutline?: (outline: unknown) => void;
  setLocalClean?: () => void;
};

type OutlineFlushBeforeMessageResult = {
  /** True when localDirty was set for the active project outline. */
  attempted: boolean;
  /** True when a flush was not needed or completed successfully. */
  succeeded: boolean;
};

const OUTLINE_FLUSH_RESTORING_POLL_MS = 200;
const OUTLINE_FLUSH_RESTORING_MAX_WAIT_MS = 5000;
const OUTLINE_FLUSH_MAX_ATTEMPTS = 3;
const OUTLINE_FLUSH_RETRY_BASE_MS = 300;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function getStructuredOutlineStoreState(): Promise<{
  store: StructuredOutlineStoreSnapshot;
  getState: () => StructuredOutlineStoreSnapshot;
}> {
  const { useStructuredOutlineStore } = await import("src/components/StoryBible/StructuredOutline/StructuredOutlineStore");
  const getState = () => useStructuredOutlineStore.getState() as StructuredOutlineStoreSnapshot;
  return { store: getState(), getState };
}

async function waitForOutlineRestoreToFinish(
  getState: () => StructuredOutlineStoreSnapshot,
  restoreDeadline: number,
): Promise<boolean> {
  while (getState().restoring === true && Date.now() < restoreDeadline) {
    await delay(OUTLINE_FLUSH_RESTORING_POLL_MS);
  }
  return getState().restoring !== true;
}

type OutlineFlushPutResult = "success" | "fingerprint_mismatch" | "failed";

async function putDirtyOutlineOnce(
  projectId: string,
  outlineProjectId: string,
  getState: () => StructuredOutlineStoreSnapshot,
): Promise<OutlineFlushPutResult> {
  const outlineStore = getState();
  const outline = outlineStore.outline;
  if (!outline) return "failed";
  // Mirror StructuredOutline saveOutline: never PUT while restoreSnapshot is in flight.
  if (outlineStore.restoring === true) return "failed";

  const beforeFingerprint = outlineFlushFingerprint(outline);
  const result = await Auth.fetchWithIdToken("/api/outline", {
    ...outline,
    projectId: outlineProjectId,
    method: "PUT",
  });

  const latest = getState();
  const afterFingerprint = outlineFlushFingerprint(latest.outline);

  if (!result?.success) return "failed";

  // If the user edited the outline while the PUT was in flight, do not clobber newer local state
  // or clear dirty — retry with the latest snapshot instead of failing the chat send.
  if (afterFingerprint !== beforeFingerprint) {
    return "fingerprint_mismatch";
  }

  if (result?.outline) {
    latest.setOutline?.(result.outline);
  }
  latest.setLocalClean?.();
  return "success";
}

/**
 * Ensure pending local outline edits are persisted before sending a chat turn.
 * This prevents stale server context (e.g. deleted local outline still appearing to the agent).
 *
 * BUGBOT FOLLOW-UP (05b59b77): mirrors `/api/outline` PUT semantics from `StructuredOutline` saveOutline;
 * unifying callers would reduce drift on conflict handling and dirty flags.
 *
 * When flush still fails after waiting/restoring and retries, callers should continue the chat turn
 * and pass `outlineSaveFailed` so the agent knows server context may be stale.
 */
async function flushDirtyOutlineBeforeMessage(projectId: string): Promise<OutlineFlushBeforeMessageResult> {
  const noFlushNeeded: OutlineFlushBeforeMessageResult = { attempted: false, succeeded: true };
  let shouldFlush = false;
  try {
    const { store: outlineStore, getState } = await getStructuredOutlineStoreState();
    const outline = outlineStore.outline;
    if (!outline || outlineStore.localDirty !== true) return noFlushNeeded;

    const outlineProjectId =
      typeof outline.projectId === "string" && outline.projectId.trim().length > 0
        ? outline.projectId
        : projectId;

    // Only flush the currently active project's outline.
    if (outlineProjectId !== projectId) return noFlushNeeded;

    shouldFlush = true;
    const restoreDeadline = Date.now() + OUTLINE_FLUSH_RESTORING_MAX_WAIT_MS;
    const initialRestoreFinished = await waitForOutlineRestoreToFinish(getState, restoreDeadline);
    if (!initialRestoreFinished) {
      return { attempted: true, succeeded: false };
    }

    let fingerprintMismatchRetries = 0;
    for (let attempt = 0; attempt < OUTLINE_FLUSH_MAX_ATTEMPTS; attempt += 1) {
      if (getState().restoring === true) {
        const restoreFinished = await waitForOutlineRestoreToFinish(getState, restoreDeadline);
        if (!restoreFinished) {
          return { attempted: true, succeeded: false };
        }
      }
      if (getState().localDirty !== true) {
        return { attempted: true, succeeded: true };
      }

      const putResult = await putDirtyOutlineOnce(projectId, outlineProjectId, getState);
      if (putResult === "success") {
        return { attempted: true, succeeded: true };
      }

      if (putResult === "fingerprint_mismatch" && fingerprintMismatchRetries < 1) {
        fingerprintMismatchRetries += 1;
        continue;
      }

      if (attempt < OUTLINE_FLUSH_MAX_ATTEMPTS - 1) {
        await delay(OUTLINE_FLUSH_RETRY_BASE_MS * (2 ** attempt));
      }
    }

    return { attempted: true, succeeded: false };
  } catch (error) {
    Sentry.captureException(error, {
      tags: { context: "agenticChat:flushDirtyOutlineBeforeMessage" },
      extra: { projectId },
    });
    return shouldFlush
      ? { attempted: true, succeeded: false }
      : noFlushNeeded;
  }
}

const getToolSignal = (toolName: string): CrossAppSignal | null => {
  const name = toolName.toLowerCase();
  if (name === "create_outline" || name === "reorder_outline" ||
      name === "created outline" || name === "creating outline" || name === "reorder outline") {
    return CrossAppSignal.AGENTIC_OUTLINE_RUNNING;
  }
  if (name === "generate_character" ||
      name === "generated character" || name === "generating character") {
    return CrossAppSignal.AGENTIC_CHARACTER_RUNNING;
  }
  if (name === "generate_worldbuilding" ||
      name === "generated worldbuilding" || name === "generating worldbuilding") {
    return CrossAppSignal.AGENTIC_WORLDBUILDING_RUNNING;
  }
  if (name === "generate_cast") {
    return CrossAppSignal.AGENTIC_CAST_RUNNING;
  }
  return null;
};

/** Clear story-bible shimmer/running signals when aborting SSE (stale onError skips this). */
function clearAgenticStoryBibleCrossAppSignals(opts?: { ctaSignalKey?: string | null }) {
  const crossApp = useCrossAppSignalStore.getState();
  crossApp.setSignalActive(CrossAppSignal.AGENTIC_OUTLINE_RUNNING, false);
  crossApp.setSignalActive(CrossAppSignal.AGENTIC_CHARACTER_RUNNING, false);
  crossApp.setSignalActive(CrossAppSignal.AGENTIC_WORLDBUILDING_RUNNING, false);
  crossApp.setSignalActive(CrossAppSignal.AGENTIC_CAST_RUNNING, false);
  crossApp.setSignalActive(getSynopsisCTASignal(), false);
  if (opts?.ctaSignalKey) {
    crossApp.setSignalActive(opts.ctaSignalKey, false);
  }
}

const deriveThinkingSteps = (timeline: ThinkingTask[]): ThinkingStepData[] => {
  if (timeline.length === 0) return [];

  const hasRunning = timeline.some((task) => task.status === "running");
  const stepTitle = buildThinkingStepTaskCountTitle(timeline, hasRunning);

  return [{
    id: "thinking-step-live",
    title: stepTitle,
    tasks: timeline,
    status: hasRunning ? "running" : "complete",
    isCollapsed: false,
  }];
};

type PersistentChatState = {
  streaming: boolean;
  stopping: boolean;
  frozenReplies: FrozenChatReply[];
  queuedSend: SendMessageParams | null;
  stopError: string;
  stopPromise: Promise<boolean> | null;
  activeRequest: { priorClientRequestIds?: string[]; targetClientRequestId?: string; cancellationSource?: CancellationSource; clientRequestId: string; projectId: string; chatId: string; onChatResolved?: (id: string) => void } | null;
  currentStreamingText: string;
  /** Provisional prose is never persisted or used by manuscript actions. */
  currentProsePreview: string;
  currentProsePreviews: Record<string, string>;
  currentProsePreviewExplanations: Record<string, string>;
  currentProsePreviewGroups: Record<string, { id: string; title: string }>;
  /** Immediate recap while the final plan response is being generated. */
  completedPlanSummary: string;
  completedPlanSummaryId: string | null;
  currentTurnUserMessageId: string | null;
  currentRetryRequestId: string | null;
  currentRetryAssistantMessageId: string | null;
  currentTurnPassage: string | null;
  /** Comment threads referenced this turn (expanded card + mutation targets), for UI chips. */
  currentTurnReferencedComments: ReferencedComment[];
  optimisticUserMessageId: string | null;
  errorText: string;

  toolRuns: AgenticToolRun[];
  outlineUpdates: AgenticOutlineUpdate[];
  characterUpdates: AgenticCharacterUpdate[];
  worldbuildingUpdates: AgenticWorldbuildingUpdate[];
  metaUpdates: AgenticMetaUpdate[];
  searchedDocs: string[];
  lookingAtData: string[];
  cutoffData: { originalLength: number; newLength: number } | null;
  contextWarning: {
    message: string;
    percentageIncluded: number;
    totalMessages: number;
    includedMessages: number;
  } | null;

  thinkingSteps: ThinkingStepData[];
  thinkingTaskTimeline: ThinkingTask[];
  activeReasoningTaskId: string | null;
  toolTaskIdByCallId: Record<string, string>;

  activeHighlight: string;
  activeHighlightHtml: string;
  /** PM bounds captured when the highlight chip was last set from the manuscript. */
  activeHighlightSelectionHint: EditorSelectionHint | null;
  dismissedHighlight: string;
  messageCountBeforeResponse: number;
  pendingAgentResponse: boolean;
  /** Successful transport completion whose assistant row has not reconciled yet. */
  completedResponseAwaitingPersistence: boolean;
  reconcilingBeforeSend: boolean;
  failedFollowUp: { params: SendMessageParams; message: string; chatUnavailable?: boolean } | null;
  responsePhase: AgentResponsePhase;

  abortController: { current: AbortController };

  contextElements: string[];
  mentionedEntities: Array<{ type: string; name: string }>;
  currentModel: string | null;
  currentPreserveChatTextWithSuggestions: boolean;
  currentWritePovTenseChiclets: Array<{ label: string }> | null;

  activePlan: PlanData | null;
  activeCommenting: CommentingData | null;
  suggestedAction: SuggestedAction | null;
  currentUserQuestion: {
    questions: QuestionStep[];
  } | null;
  currentChatMadeChanges: boolean;
  ctaAction: CTAAction | null;
  /** Set when a Story Bible entity-generate CTA finishes before the persisted assistant row lands. */
  streamingStoryBibleGeneratedEntity: StoryBibleGeneratedEntity | null;
  /** Monotonic 0–1 progress for the floating Story Bible CTA card. */
  storyBibleCtaProgress: number;
  storyBibleCtaToolStartedAt: number | null;
  storyBibleCtaCastAppliedCount: number;
  storyBibleCtaCastExpectedCount: number | null;
  commentingProgress: number;
  commentingToolStartedAt: number | null;
  /**
   * After `chat_acknowledged`, the server id we expect the router to land on (only skip `reset` when
   * `chatId` matches this — avoids suppressing reset if the user navigated elsewhere).
   */
  pendingNewChatMaterializationId: string | null;
  /** True while `pausePlan` has set optimistically `paused` but the pause API has not settled yet. */
  pausePlanApiRequestInFlight: boolean;
  /** True from Resume click until the plan/resume stream settles — blocks stale metadata sync. */
  planResumeRequestInFlight: boolean;
};

export type PlanStepData = {
  id: string;
  goalId?: string;
  title: string;
  description?: string;
  status: "pending" | "in_progress" | "completed" | "cancelled";
};

export type PlanData = {
  workProgress?: { completed: number; total: number };
  outlineProgress?: { saved: number; total: number };
  id: string;
  description: string;
  specialConsiderations?: string;
  steps: PlanStepData[];
  estimatedCreditCost?: number;
  creditCostEstimateRange?: {
    min?: number;
    max?: number;
    avg?: number;
  };
  estimates?: {
    estimatedCreditCost?: number;
    creditCostEstimateRange?: {
      min?: number;
      max?: number;
      avg?: number;
    };
  };
  status: "proposed" | "in_progress" | "paused" | "completed" | "cancelled" | "cancelling";
  currentStepIndex: number;
};

export type CommentingData = {
  id: string;
  status: "in_progress" | "completed" | "cancelled";
  commentsCreated: number;
  feedbackTypes: string[];
  customFeedbackInstructions: Array<{
    name: string;
    description?: string;
    content: string;
  }>;
};

/** Approve-plan stream end: normalize status (e.g. all steps done + lost plan_completed) then keep or clear activePlan. */
function activePlanAfterApprovePlanStreamEnd(plan: PlanData | null | undefined): PlanData | null {
  if (!plan) return null;
  const p = normalizeTerminalPlanStatus(plan);
  if (p.status === "in_progress") {
    // Stream ended without an explicit pause/completed event (interrupted turn):
    // keep the plan visible and resumable instead of dropping it.
    return { ...p, status: "paused" };
  }
  if (p.status === "paused" || p.status === "cancelled" || p.status === "completed") {
    return p;
  }
  return null;
}

export { normalizeTerminalPlanStatus };

/** When the server omits `plan_completed` but every step is done, treat the plan as completed. */
function normalizePlanIfAllStepsDone(plan: PlanData | null): PlanData | null {
  if (!plan) return plan;
  return normalizeTerminalPlanStatus(plan);
}

type CTAActionPayloadForSend = {
  type: string;
  targetSurface?: "story_bible_outline_summary";
  targetId?: string;
  targetName?: string;
  customInstructions?: string;
  /** generate_outline only: OUTLINE_FRAMWORKS value (e.g. "novel-outline", "custom-outline"). */
  outlineFramework?: string;
  characterId?: string;
  entryId?: string;
  traitId?: string;
  traitLabel?: string;
  role?: string;
  roleLabel?: string;
  roleExplicitlySelected?: boolean;
  fields?: Array<{ id?: string; label: string; value?: unknown }>;
};

export type EditorSelectionHint = {
  documentId: string;
  pmFrom?: number;
  pmTo?: number;
  plainTextStart?: number;
  plainTextEnd?: number;
};

function editorSelectionHintsEqual(
  a: EditorSelectionHint | null | undefined,
  b: EditorSelectionHint | null | undefined,
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    a.documentId === b.documentId
    && a.pmFrom === b.pmFrom
    && a.pmTo === b.pmTo
    && a.plainTextStart === b.plainTextStart
    && a.plainTextEnd === b.plainTextEnd
  );
}

type SendMessageParams = {
  instructions: string;
  /** Bubble text when it should differ from payload `message` (internal markers). */
  instructionsDisplayOverride?: string;
  /** Exact prose inserted through the composer paste attachment flow. */
  pastedExcerpts?: string[];
  modelKey?: string;
  mode?: "write" | "chat";
  passage: string;
  /** HTML serialization of the current manuscript selection so the agent can see inline formatting (italic/bold/etc). */
  passageHtml?: string;
  /** Annotation ids overlapping `passage` in the manuscript (TipTap), for prioritizing those comment threads in agent context. */
  selectionAnnotationIds?: string[];
  /** Visible comment thread ids in the exact sidebar order for the current manuscript document. */
  commentSidebarOrderIds?: string[];
  /** Expanded comment card thread id in the editor, if any. */
  activeManuscriptCommentId?: string;
  documentText: string;
  /** The document currently open in the editor, even when a highlight chip targets another document. */
  openDocumentId?: string;
  documentId: string;
  projectId: string;
  chatId: string;
  isStoryBibleActive: boolean;
  setChatId: (id: string) => void;
  currentMessages: any[];
  hidden?: boolean;
  /** Called when the agentic stream fails (network, HTTP, or error event). */
  onStreamError?: (message: string) => void;
  /** TipTap/PM selection (or caret) when the user sent the message — hints server + model where to search; may be stale. */
  editorSelectionHint?: EditorSelectionHint;
  ctaAction?: CTAActionPayloadForSend;
  userMessageMetadata?: Record<string, unknown>;
  /** Existing user turn to retry or edit without duplicating a stalled request. */
  resendOfMessageId?: string;
  resendIntent?: "retry" | "edit";
  /** Explicit recovery into a fresh conversation, ignoring a previous acknowledgement. */
  startNewChat?: boolean;
  replacementCommandId?: string;
  replacesClientRequestId?: string;
  priorClientRequestIds?: string[];
  targetClientRequestId?: string;
  /** Explicit parent to attach the new user message to; when omitted, parent is inferred from active leaf. */
  parentMessageId?: string | null;
  /** When true, do not auto-cancel a currently proposed plan before sending this message. */
  keepProposedPlan?: boolean;
  /** When true, server should treat this turn as already plan-approved for multi-mutation guardrails. */
  editedPlanApproved?: boolean;
  /** Edited plan content to persist before execution begins. */
  editedPlan?: PlanData;
};

export type CancellationSource = "unknown" | "stop" | "back" | "new_chat" | "delete_chat" | "cta_cancel" | "navigation" | "replacement" | "stream_error" | "skip_question" | "feedback_cancel";

type PersistentChatActions = {
  setStreaming: (streaming: boolean) => void;
  setCurrentStreamingText: (text: string) => void;
  setErrorText: (text: string) => void;

  setToolRuns: (updater: AgenticToolRun[] | ((prev: AgenticToolRun[]) => AgenticToolRun[])) => void;
  setOutlineUpdates: (updates: AgenticOutlineUpdate[]) => void;
  setSearchedDocs: (docs: string[] | ((prev: string[]) => string[])) => void;
  setLookingAtData: (data: string[] | ((prev: string[]) => string[])) => void;
  setCutoffData: (data: { originalLength: number; newLength: number } | null) => void;
  setContextWarning: (warning: PersistentChatState["contextWarning"]) => void;

  setThinkingSteps: (steps: ThinkingStepData[]) => void;
  updateThinkingTimeline: (updater: (prev: ThinkingTask[]) => ThinkingTask[]) => void;
  resetThinkingTimeline: () => void;
  ensureThinkingPlaceholder: (opts?: { isWrite?: boolean }) => void;
  clearThinkingPlaceholder: () => void;
  appendReasoningTask: (text: string) => void;
  finalizeReasoningTask: () => void;
  startToolTimelineTask: (callId: string | undefined, toolName: string, elementType?: string, isWrite?: boolean, readTargetLabel?: string, toolArgs?: Record<string, unknown>) => void;
  updateToolTimelineProgress: (callId: string | undefined, message: string) => void;
  completeToolTimelineTask: (callId: string | undefined, outcome?: string, elementType?: string) => void;
  completeRunningThinkingTasks: () => void;
  getToolTaskId: (callId?: string) => string | undefined;

  setActiveHighlight: (highlight: string, highlightHtml?: string, selectionHint?: EditorSelectionHint | null) => void;
  dismissActiveHighlight: () => void;
  clearDismissedHighlight: () => void;
  setMessageCountBeforeResponse: (count: number) => void;

  abortStream: (source?: CancellationSource) => Promise<boolean>;
  cancelActiveCta: () => void;
  resetForNewMessage: (opts?: { passage?: string | null }) => void;
  reset: (source?: CancellationSource) => void;
  resetForDocumentSwitch: () => void;

  sendMessage: (params: SendMessageParams) => Promise<void>;
  clearStreamingTextIfServerHasData: (messages: any[], projectId?: string) => void;

  clearCurrentUserQuestion: () => void;
  setCtaAction: (action: CTAAction | null) => void;
  updateCtaActionStatus: (status: CTAActionStatus) => void;

  setPlan: (plan: PlanData | null) => void;
  cancelCommenting: () => void;
  updatePlanStep: (stepIndex: number, status: PlanStepData["status"]) => void;
  updatePlanStepById: (stepId: string, status: PlanStepData["status"]) => void;
  setPlanStatus: (status: PlanData["status"]) => void;
  completePlan: (planId?: string) => void;
  removePlanStep: (stepId: string) => void;
  approvePlan: (params: PlanActionParams) => Promise<void>;
  cancelPlan: (params: PlanActionParams) => Promise<void>;
  pausePlan: (params: PlanActionParams) => Promise<void>;
  resumePlan: (params: PlanActionParams) => Promise<void>;
};

type PlanActionParams = {
  chatId: string;
  projectId: string;
  planId: string;
  modelKey?: string;
  mode?: "write" | "chat";
  removedStepIds?: string[];
  setChatId: (id: string) => void;
};

export type PersistentChatStore = PersistentChatState & PersistentChatActions;

function normalizePlanStepId(stepId: unknown): string | undefined {
  if (stepId === null || stepId === undefined) return undefined;
  const normalized = String(stepId).trim();
  return normalized.length > 0 ? normalized : undefined;
}

/** Resolve SSE step index from stepId (preferred) or explicit stepIndex; returns -1 if neither is usable. */
function resolvePlanStreamStepIndex(plan: PlanData, stepIndex: number | undefined, stepId: unknown): number {
  const normalizedStepId = normalizePlanStepId(stepId);
  if (normalizedStepId) {
    return plan.steps.findIndex((s) => String(s.id).trim() === normalizedStepId);
  }
  if (typeof stepIndex === "number") return stepIndex;
  return -1;
}

/** Ignore out-of-order plan_step_complete chunks while a plan is actively executing (prevents ghost checkmarks). */
function applyPlanPausedChunk(
  plan: PlanData,
  chunk: { currentStepIndex?: unknown },
): PlanData {
  const paused: PlanData = { ...plan, status: "paused" };
  const fromChunk = typeof chunk.currentStepIndex === "number" ? chunk.currentStepIndex : undefined;
  const resolvedIndex = fromChunk ?? resolvePlanCurrentStepIndex(paused);
  const maxIndex = Math.max(paused.steps.length - 1, 0);
  return {
    ...paused,
    currentStepIndex: Math.min(Math.max(0, resolvedIndex), maxIndex),
  };
}

/** Goal plans may finish independent goals out of order. Only accept a full, matching snapshot. */
function applyGoalPlanProgressSnapshot(plan: PlanData, snapshot: unknown): PlanData {
  if (!Array.isArray(snapshot) || snapshot.length !== plan.steps.length) return plan;
  const statuses = new Map<string, PlanStepData["status"]>();
  for (const entry of snapshot) {
    if (!entry || typeof entry.id !== "string" || statuses.has(entry.id)
      || !["pending", "in_progress", "completed", "cancelled"].includes(entry.status)
      || !plan.steps.some(step => String(step.id) === entry.id)) return plan;
    statuses.set(entry.id, entry.status);
  }
  const steps = plan.steps.map(step => ({ ...step, status: statuses.get(String(step.id))! }));
  return { ...plan, steps, currentStepIndex: resolvePlanCurrentStepIndex({ ...plan, steps }) };
}

function shouldApplyPlanStepCompleteEvent(
  plan: PlanData,
  resolvedIndex: number,
  chunk: { planId?: unknown; stepId?: unknown; goalId?: unknown },
): boolean {
  if (resolvedIndex < 0 || resolvedIndex >= plan.steps.length) return false;
  if (chunk.planId != null && chunk.planId !== plan.id) return false;
  if (chunk.goalId != null) {
    // Goal-backed tasks can finish independently. Require all identities to
    // match before accepting completion outside the legacy sequential cursor.
    return chunk.planId === plan.id && chunk.stepId === plan.steps[resolvedIndex].id
      && chunk.goalId === plan.steps[resolvedIndex].goalId;
  }
  const guardStatuses: PlanData["status"][] = ["in_progress", "paused", "cancelling"];
  if (!guardStatuses.includes(plan.status)) return true;

  const inProgressIdx = plan.steps.findIndex((s) => s.status === "in_progress");
  const expectedIdx = inProgressIdx >= 0 ? inProgressIdx : plan.steps.findIndex((s) => s.status === "pending");
  if (expectedIdx < 0) return true;
  return resolvedIndex === expectedIdx;
}

const initialState: PersistentChatState = {
  currentRetryRequestId: null,
  currentRetryAssistantMessageId: null,
  stopping: false,
  frozenReplies: readFrozenChatReplies(),
  queuedSend: null,
  stopError: "",
  stopPromise: null,
  activeRequest: null,
  streaming: false,
  currentStreamingText: "",
  currentProsePreview: "",
  currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {},
  completedPlanSummary: "",
  completedPlanSummaryId: null,
  currentTurnUserMessageId: null,
  currentTurnPassage: null,
  currentTurnReferencedComments: [],
  optimisticUserMessageId: null,
  errorText: "",
  toolRuns: [],
  outlineUpdates: [],
  characterUpdates: [],
  worldbuildingUpdates: [],
  metaUpdates: [],
  searchedDocs: [],
  lookingAtData: [],
  cutoffData: null,
  contextWarning: null,
  thinkingSteps: [],
  thinkingTaskTimeline: [],
  activeReasoningTaskId: null,
  toolTaskIdByCallId: {},
  activeHighlight: "",
  activeHighlightHtml: "",
  activeHighlightSelectionHint: null,
  dismissedHighlight: "",
  messageCountBeforeResponse: 0,
  pendingAgentResponse: false,
  responsePhase: "idle",
  abortController: { current: new AbortController() },
  reconcilingBeforeSend: false,
  completedResponseAwaitingPersistence: false,
  failedFollowUp: null,
  contextElements: [],
  mentionedEntities: [],
  currentModel: null,
  currentPreserveChatTextWithSuggestions: false,
  currentWritePovTenseChiclets: null,
  activePlan: null,
  activeCommenting: null,
  suggestedAction: null,
  currentUserQuestion: null,
  currentChatMadeChanges: false,
  ctaAction: null,
  streamingStoryBibleGeneratedEntity: null,
  ...STORY_BIBLE_CTA_PROGRESS_RESET,
  ...COMMENTING_PROGRESS_RESET,
  pendingNewChatMaterializationId: null,
  pausePlanApiRequestInFlight: false,
  planResumeRequestInFlight: false,
};

let stopRecoveryTimeout: ReturnType<typeof setTimeout> | null = null;
let stopRecoveryAttempt = 0;
let sendOperationGeneration = 0;

let pendingAgentResponseClearTimeout: ReturnType<typeof setTimeout> | null = null;

/** True when a terminal SSE callback belongs to a stream superseded by a newer abortController. */
function isStalePersistentChatStream(
  get: () => PersistentChatStore,
  streamSignal: AbortSignal,
): boolean {
  return get().abortController.current.signal !== streamSignal;
}

type AbortAgentStreamOpts = {
  preservePauseInFlight?: boolean;
  preserveResumeInFlight?: boolean;
};

/** Abort the active agentic SSE without clearing the plan card. */
function abortAgentStreamPreservingPlan(
  set: (partial: Partial<PersistentChatState> | ((state: PersistentChatState) => Partial<PersistentChatState>)) => void,
  get: () => PersistentChatStore,
  opts?: AbortAgentStreamOpts,
) {
  cancelTextBufferFlush();
  const state = get();
  const activePlan = state.activePlan;
  clearAgenticStoryBibleCrossAppSignals({ ctaSignalKey: state.ctaAction?.signalKey });
  setStreamAbortSource(state.abortController.current.signal, "replacement");
  state.abortController.current.abort();
  if (pendingAgentResponseClearTimeout) {
    clearTimeout(pendingAgentResponseClearTimeout);
    pendingAgentResponseClearTimeout = null;
  }
  set({
    abortController: { current: new AbortController() },
    reconcilingBeforeSend: false,
    completedResponseAwaitingPersistence: false,
    streaming: false,
    currentProsePreview: "",
    currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {},
    pendingAgentResponse: false,
    responsePhase: "idle",
    pausePlanApiRequestInFlight: opts?.preservePauseInFlight ? state.pausePlanApiRequestInFlight : false,
    planResumeRequestInFlight: opts?.preserveResumeInFlight ? state.planResumeRequestInFlight : false,
    ...(activePlan ? { activePlan } : {}),
  });
}

export const usePersistentChatStore = create<PersistentChatStore>((set, get) => ({
  ...initialState,

  setStreaming: (streaming) => set({ streaming }),
  setCurrentStreamingText: (text) => set({ currentStreamingText: text }),
  setErrorText: (text) => set({ errorText: text }),

  setToolRuns: (updater) => {
    if (typeof updater === "function") {
      set((state) => ({ toolRuns: updater(state.toolRuns) }));
    } else {
      set({ toolRuns: updater });
    }
  },

  setOutlineUpdates: (updates) => set({ outlineUpdates: updates }),

  setSearchedDocs: (updater) => {
    if (typeof updater === "function") {
      set((state) => ({ searchedDocs: updater(state.searchedDocs) }));
    } else {
      set({ searchedDocs: updater });
    }
  },

  setLookingAtData: (updater) => {
    if (typeof updater === "function") {
      set((state) => ({ lookingAtData: updater(state.lookingAtData) }));
    } else {
      set({ lookingAtData: updater });
    }
  },

  setCutoffData: (data) => set({ cutoffData: data }),
  setContextWarning: (warning) => set({ contextWarning: warning }),
  setThinkingSteps: (steps) => set({ thinkingSteps: steps }),

  updateThinkingTimeline: (updater) => {
    set((state) => {
      const newTimeline = updater(state.thinkingTaskTimeline);
      return {
        thinkingTaskTimeline: newTimeline,
        thinkingSteps: deriveThinkingSteps(newTimeline),
      };
    });
  },

  resetThinkingTimeline: () => {
    set({
      activeReasoningTaskId: null,
      toolTaskIdByCallId: {},
      thinkingTaskTimeline: [],
      thinkingSteps: [],
    });
  },

  ensureThinkingPlaceholder: (opts) => {
    const { updateThinkingTimeline } = get();
    const isWrite = opts?.isWrite === true;
    updateThinkingTimeline((prev) => {
      if (prev.length > 0) return prev;
      return [{
        id: "thinking-placeholder",
        icon: "brain",
        title: isWrite ? "Writing" : "Thinking",
        status: "running",
        ...(isWrite ? { isWrite: true as const } : {}),
      }];
    });
  },

  clearThinkingPlaceholder: () => {
    const { updateThinkingTimeline } = get();
    updateThinkingTimeline((prev) => removeThinkingPlaceholder(prev));
  },

  appendReasoningTask: (text) => {
    if (!text) return;

    set((state) => {
      const baseTasks = removeThinkingPlaceholder(state.thinkingTaskTimeline);
      const activeId = state.activeReasoningTaskId;

      let newTimeline: ThinkingTask[];
      let newActiveId = activeId;

      if (!activeId) {
        const newId = `reasoning-${makeId(8)}`;
        newActiveId = newId;
        newTimeline = [
          ...baseTasks,
          {
            id: newId,
            icon: "brain",
            title: "Thinking",
            status: "running",
            explanation: text,
          },
        ];
      } else {
        newTimeline = baseTasks.map((task) =>
          task.id === activeId
            ? { ...task, explanation: `${task.explanation || ""}${text}` }
            : task
        );
      }

      return {
        thinkingTaskTimeline: newTimeline,
        thinkingSteps: deriveThinkingSteps(newTimeline),
        activeReasoningTaskId: newActiveId,
      };
    });
  },

  finalizeReasoningTask: () => {
    set((state) => {
      const activeId = state.activeReasoningTaskId;
      if (!activeId) return state;

      const newTimeline = state.thinkingTaskTimeline.map((task) =>
        task.id === activeId
          ? {
              ...task,
              title: "Thought for a few seconds",
              status: "complete" as const,
              explanation: (task.explanation || "").trim(),
            }
          : task
      );

      return {
        thinkingTaskTimeline: newTimeline,
        thinkingSteps: deriveThinkingSteps(newTimeline),
        activeReasoningTaskId: null,
      };
    });
  },

  getToolTaskId: (callId?: string) => {
    const state = get();
    const normalizedCallId = callId && callId.length > 0 ? callId : undefined;

    if (normalizedCallId && state.toolTaskIdByCallId[normalizedCallId]) {
      const id = state.toolTaskIdByCallId[normalizedCallId];
      set((s) => {
        const newMap = { ...s.toolTaskIdByCallId };
        delete newMap[normalizedCallId];
        return { toolTaskIdByCallId: newMap };
      });
      return id;
    }

    return [...state.thinkingTaskTimeline]
      .reverse()
      .find((task) => task.icon !== "brain" && task.status === "running")?.id;
  },

  startToolTimelineTask: (callId, toolName, elementType, isWrite, readTargetLabel, toolArgs) => {
    const normalizedCallId = callId && callId.length > 0 ? callId : undefined;
    const timelineId = `tool-${normalizedCallId ?? makeId(8)}`;
    const lookupKey = normalizedCallId ?? timelineId;

    set((state) => {
      const title = readTargetLabel
        ? `Reading ${readTargetLabel}`
        : getToolTitle(toolName, elementType, toolArgs, get().mode);
      const newTimeline = [
        ...removeThinkingPlaceholder(state.thinkingTaskTimeline),
        {
          id: timelineId,
          icon: getToolIcon(toolName, elementType),
          title,
          status: "running" as const,
          elementType: elementType as ThinkingTask["elementType"],
          isWrite,
          toolName: toolName,
        },
      ];

      return {
        thinkingTaskTimeline: newTimeline,
        thinkingSteps: deriveThinkingSteps(newTimeline),
        toolTaskIdByCallId: { ...state.toolTaskIdByCallId, [lookupKey]: timelineId },
      };
    });
  },

  updateToolTimelineProgress: (callId, message) => {
    const trimmed = String(message ?? "").trim();
    if (!trimmed) return;
    const taskId = get().getToolTaskId(callId);
    if (!taskId) return;
    set((state) => {
      const newTimeline = state.thinkingTaskTimeline.map((task) =>
        task.id === taskId && task.status === "running"
          ? { ...task, title: trimmed }
          : task,
      );
      return {
        thinkingTaskTimeline: newTimeline,
        thinkingSteps: deriveThinkingSteps(newTimeline),
      };
    });
  },

  completeToolTimelineTask: (callId, outcome, elementType) => {
    const taskId = get().getToolTaskId(callId);
    if (!taskId) return;

    const isError = outcome?.toLowerCase().startsWith("error");
    const status: ThinkingTask["status"] = isError ? "error" : "complete";

    set((state) => {
      const baseTimeline = removeThinkingPlaceholder(state.thinkingTaskTimeline);
      const finishingTask = baseTimeline.find((task) => task.id === taskId);
      /** Keep `Editing…` banner while the assistant streams after mutation tools (`isWrite` is set on tool_start). */
      const continuationIsWrite =
        finishingTask?.isWrite
        ?? (finishingTask?.toolName ? isWriteToolName(finishingTask.toolName) : false);

      let isWriteOp = Boolean(continuationIsWrite);
      let newTimeline = baseTimeline.map((task) => {
        if (task.id !== taskId) return task;
        const updates: Partial<ThinkingTask> = { status, explanation: outcome ?? task.explanation };
        if (task.toolName === "manage_preferences") {
          if (isError) {
            return { ...task, ...updates, openChatPreferencesLink: false };
          }
          queryClient.invalidateQueries({ queryKey: ["chatPersonalizationMemories"] });
          return { ...task, ...updates, title: "Memory preserved", openChatPreferencesLink: true };
        }
        if (elementType && !task.elementType) {
          const titleLower = task.title.toLowerCase();
          const inferredEditFromTitle =
            titleLower.includes("update") || titleLower.includes("create") || titleLower.includes("delete");
          isWriteOp = isWriteOp || inferredEditFromTitle;
          updates.elementType = elementType as ThinkingTask["elementType"];
          updates.icon = getToolIcon(isWriteOp ? "write" : "read", elementType);
          updates.title = getToolTitle(isWriteOp ? "write" : "read", elementType);
        }
        return { ...task, ...updates };
      });

      if (!isError) {
        newTimeline = [
          ...newTimeline,
          {
            id: "thinking-placeholder",
            icon: "brain" as const,
            title: getThinkingTitleAfterTool(elementType, isWriteOp),
            status: "running" as const,
            ...(continuationIsWrite || isWriteOp ? { isWrite: true as const } : {}),
          },
        ];
      }

      return {
        thinkingTaskTimeline: newTimeline,
        thinkingSteps: deriveThinkingSteps(newTimeline),
      };
    });
  },

  completeRunningThinkingTasks: () => {
    set((state) => {
      const newTimeline = state.thinkingTaskTimeline.map((task) => {
        if (task.status === "running") {
          const isReasoning = task.icon === "brain";
          return {
            ...task,
            status: "complete" as const,
            title: isReasoning ? "Thought for a few seconds" : task.title,
            explanation: isReasoning ? (task.explanation || "").trim() : task.explanation,
          };
        }
        return task;
      });

      return {
        thinkingTaskTimeline: newTimeline,
        thinkingSteps: deriveThinkingSteps(newTimeline),
        activeReasoningTaskId: null,
        toolTaskIdByCallId: {},
      };
    });
  },

  setActiveHighlight: (highlight, highlightHtml, selectionHint) => {
    const nextHighlight = String(highlight ?? "");
    const nextHighlightHtml = String(highlightHtml ?? "");

    set((state) => {
      if (!nextHighlight) {
        if (!state.activeHighlight?.trim() && !state.activeHighlightSelectionHint) {
          return state;
        }
        scheduleClearEditorSelectionContext();
        return {
          activeHighlight: "",
          activeHighlightHtml: "",
          activeHighlightSelectionHint: null,
          dismissedHighlight: "",
        };
      }
      // Prevent immediately re-adding the same highlight after users dismiss it via the `x` chip action.
      if (state.dismissedHighlight && state.dismissedHighlight === nextHighlight) {
        return state;
      }
      const nextSelectionHint =
        selectionHint === undefined
          ? state.activeHighlightSelectionHint
          : selectionHint;
      if (
        nextHighlight === state.activeHighlight
        && nextHighlightHtml === state.activeHighlightHtml
        && editorSelectionHintsEqual(nextSelectionHint, state.activeHighlightSelectionHint)
      ) {
        return state;
      }
      scheduleSyncEditorSelectionContext({
        passage: nextHighlight,
        passageHtml: nextHighlightHtml,
        editorSelectionHint: nextSelectionHint,
      });
      return {
        activeHighlight: nextHighlight,
        activeHighlightHtml: nextHighlightHtml,
        activeHighlightSelectionHint: nextSelectionHint,
        dismissedHighlight: "",
      };
    });
  },
  dismissActiveHighlight: () =>
    set((state) => {
      const hasHighlightChip =
        !!state.activeHighlight?.trim() || !!state.activeHighlightSelectionHint;
      const hasInFlightTurnPassage = !!state.currentTurnPassage?.trim();
      if (!hasHighlightChip && !hasInFlightTurnPassage) {
        return state;
      }
      flushClearEditorSelectionContext();
      return {
        activeHighlight: "",
        activeHighlightHtml: "",
        activeHighlightSelectionHint: null,
        dismissedHighlight:
          state.activeHighlight
          || state.dismissedHighlight
          || state.currentTurnPassage
          || "",
        currentTurnPassage: null,
      };
    }),
  clearDismissedHighlight: () =>
    set((state) => (state.dismissedHighlight ? { dismissedHighlight: "" } : state)),
  setMessageCountBeforeResponse: (count) => set({ messageCountBeforeResponse: count }),

  abortStream: (source = "unknown") => {
    if (get().stopPromise) return get().stopPromise!;
    if (stopRecoveryTimeout) {
      clearTimeout(stopRecoveryTimeout);
      stopRecoveryTimeout = null;
    }
    flushTextBufferSync(set);
    const state = get();
    if (!state.stopping) stopRecoveryAttempt = 0;
    const stoppedUserId = state.currentTurnUserMessageId ?? state.optimisticUserMessageId;
    if (state.activeRequest && stoppedUserId
      && !state.frozenReplies.some(reply => reply.clientRequestId === state.activeRequest!.clientRequestId)) {
      const frozenReplies = [...state.frozenReplies, {
        ...state.activeRequest, userMessageId: stoppedUserId, content: state.currentStreamingText,
        createdAt: new Date().toISOString(),
      }];
      saveFrozenChatReplies(frozenReplies);
      set({ frozenReplies, currentStreamingText: "" });
    }
    clearAgenticStoryBibleCrossAppSignals({ ctaSignalKey: state.ctaAction?.signalKey });
    // Fence callbacks before abort(): abort listeners may fire synchronously.
    set({
      abortController: { current: new AbortController() },
      ...(state.activeRequest ? { stopping: true, stopError: "" } : {}),
    });
    setStreamAbortSource(state.abortController.current.signal, source);
    state.abortController.current.abort();
    if (pendingAgentResponseClearTimeout) {
      clearTimeout(pendingAgentResponseClearTimeout);
      pendingAgentResponseClearTimeout = null;
    }
    set({
      abortController: { current: new AbortController() },
      reconcilingBeforeSend: false,
      completedResponseAwaitingPersistence: state.reconcilingBeforeSend && state.completedResponseAwaitingPersistence,
      streaming: false,
      currentProsePreview: "",
      currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {},
      pendingAgentResponse: false,
      responsePhase: "idle",
      currentTurnReferencedComments: [],
      pausePlanApiRequestInFlight: false,
      planResumeRequestInFlight: false,
      streamingStoryBibleGeneratedEntity: null,
      ...STORY_BIBLE_CTA_PROGRESS_RESET,
    });
    const request = state.activeRequest;
    if (!request) return Promise.resolve(true);
    const cancellationSource = request.cancellationSource ?? source;
    set({ activeRequest: { ...request, cancellationSource } });
    set({ stopping: true, stopError: "" });
    const recoveryController = get().abortController.current;
    const savedChangesRefresh = prepareCancelledStoryBibleRefresh(request.projectId,
      () => get().abortController.current === recoveryController).catch(error => {
      Sentry.captureException(error, { tags: { context: "agenticChat:cancelledStoryBibleRefresh" } });
      return null;
    });
    const stopStage = "confirmation";
    const promise = (async () => {
      try {
        // Each server poll waits for cleanup. Transient failures are retried
        // in the background without allowing overlapping agent runs.
        let stopped = false;
        let recoveredChatId: string | undefined;
        let storyBibleRefresh: StoryBibleRefreshHint = {};
        for (let attempt = 0; attempt < 3 && !stopped; attempt++) {
          let timeout: ReturnType<typeof setTimeout> | undefined;
          try {
            const result = await Promise.race([
              Promise.all([...new Set([request.clientRequestId, ...(request.priorClientRequestIds ?? [])])].map(clientRequestId =>
                Auth.fetchWithIdToken("/api/ai/agenticChat/message/cancel", { clientRequestId, cancellationSource }),
              )).then(results => ({
                stopped: results.every(result => result?.stopped === true),
                chatId: results.find(result => typeof result?.chatId === "string")?.chatId,
                storyBibleRefresh: results.reduce((hint, result) => ({ ...hint, ...result?.storyBibleUpdates?.[request.projectId] }), {} as StoryBibleRefreshHint),
              })),
              new Promise<never>((_, reject) => {
                timeout = setTimeout(() => reject(new Error("Stop confirmation timed out")), 12_000);
              }),
            ]);
            stopped = result?.stopped === true;
            storyBibleRefresh = { ...storyBibleRefresh, ...result.storyBibleRefresh };
            if (stopped && typeof result.chatId === "string" && result.chatId) recoveredChatId = result.chatId;
          } finally {
            if (timeout) clearTimeout(timeout);
          }
        }
        if (!stopped) throw new Error("Stop is still pending");
        if (get().activeRequest?.clientRequestId !== request.clientRequestId) return true;
        const latestRequest = get().activeRequest!;
        if (latestRequest.chatId === NEW_CHAT_ID && recoveredChatId && latestRequest.onChatResolved) {
          set({ pendingNewChatMaterializationId: recoveredChatId });
          latestRequest.onChatResolved(recoveredChatId);
        }
        // History only synchronizes display. It cannot revoke a confirmed Stop.
        void (async () => {
          if (latestRequest.chatId === NEW_CHAT_ID && recoveredChatId) {
            const savedChatId = recoveredChatId;
            // Reconcile display-only frozen replies without holding the send gate.
            const savedChat = await queryClient.fetchQuery({
              queryKey: chatQueryKey(request.projectId, savedChatId),
              queryFn: () => fetchChatPage(savedChatId),
              staleTime: 0,
            });
            // Stop can fence the acknowledgement before temporary IDs are replaced.
            // Rebind only this request's recovery copy, even after navigation away.
            const savedMessages = savedChat.messages ?? [];
            const savedReply = savedMessages.find(message => message.role === "assistant"
              && message.metadata?.clientRequestId === request.clientRequestId);
            const rootUsers = savedMessages.filter(message => message.role === "user" && !message.parent_message_id);
            const frozenReplies = get().frozenReplies.map(reply => {
              if (reply.projectId !== request.projectId || reply.clientRequestId !== request.clientRequestId) return reply;
              const savedUser = savedMessages.find(message => message.role === "user"
                && (message.id === reply.userMessageId || message.id === savedReply?.metadata?.triggerUserMessageId))
                ?? (rootUsers.length === 1 ? rootUsers[0] : undefined);
              return { ...reply, chatId: savedChatId, userMessageId: savedUser?.id ?? reply.userMessageId };
            });
            saveFrozenChatReplies(frozenReplies);
            set({ frozenReplies });
          } else {
            await queryClient.refetchQueries(
              { queryKey: chatQueryKey(request.projectId, latestRequest.chatId), exact: true },
              { throwOnError: true, cancelRefetch: true },
            );
          }
        })().catch(() => { /* A deleted/offline chat must not block another send. */ });
        const queued = get().queuedSend;
        // NEW_CHAT_ID is a shared placeholder, not conversation identity. reset()
        // detaches the original composer; a later new-chat send must stay new.
        const queuedBelongsToRequest = queued?.projectId === request.projectId
          && queued.chatId === request.chatId
          && (request.chatId !== NEW_CHAT_ID || !!get().activeRequest?.onChatResolved);
        stopRecoveryAttempt = 0;
        set({ activeRequest: null, stopping: false, stopError: "", stopPromise: null, queuedSend: null });
        if (queued) {
          const queuedChatId = queuedBelongsToRequest ? (recoveredChatId ?? latestRequest.chatId) : queued.chatId;
          const history = queryClient.getQueryData<PersistentChatWithMessages>(chatQueryKey(queued.projectId, queuedChatId));
          // Preserve the placeholder promotion marker through sendMessage's reset
          // so the chat-switch effect does not abort this newly queued turn.
          void get().sendMessage({ ...queued,
            chatId: queued.chatId === NEW_CHAT_ID && get().pendingNewChatMaterializationId === queuedChatId
              ? NEW_CHAT_ID : queuedChatId,
            currentMessages: history ? linearPathToLeaf(history.messages, history.active_leaf_message_id) : queued.currentMessages,
          }).catch(() => {
            set({ failedFollowUp: { params: queued, message: "Your queued message could not be sent. Please retry." } });
          });
        }
        // The original stream is fenced. Recover committed writes via the Stop
        // response; a failed refresh must never revoke confirmed cancellation.
        void savedChangesRefresh.then(refresh => refresh?.(storyBibleRefresh)).catch(error => {
          Sentry.captureException(error, { tags: { context: "agenticChat:cancelledStoryBibleRefresh" } });
        });
        // Current history is authoritative; sidebar refresh must not delay Stop
        // or turn successful cancellation into a failure if the sidebar is offline.
        void Promise.resolve()
          .then(() => queryClient.invalidateQueries(["chats", request.projectId]))
          .catch(() => { /* The sidebar can retry independently. */ });
        return true;
      } catch {
        if (get().activeRequest?.clientRequestId !== request.clientRequestId) return false;
        set({ stopping: true, stopPromise: null, stopError: stopStage });
        const delay = Math.min(30_000, 2_000 * 2 ** Math.min(stopRecoveryAttempt++, 4));
        stopRecoveryTimeout = setTimeout(() => {
          stopRecoveryTimeout = null;
          // Navigation may detach the composer, but cancellation still belongs
          // to this request. Never revive recovery for a replacement run.
          if (get().stopping && get().activeRequest?.clientRequestId === request.clientRequestId) {
            void get().abortStream(cancellationSource);
          }
        }, delay);
        return false;
      }
    })();
    set({ stopPromise: promise });
    return promise;
  },

  cancelActiveCta: () => {
    flushTextBufferSync(set);
    const state = get();
    if (!state.ctaAction) return;

    void get().abortStream("cta_cancel");
    if (pendingAgentResponseClearTimeout) {
      clearTimeout(pendingAgentResponseClearTimeout);
      pendingAgentResponseClearTimeout = null;
    }
    clearAgenticStoryBibleCrossAppSignals({ ctaSignalKey: state.ctaAction.signalKey });
    set({
      abortController: { current: new AbortController() },
      reconcilingBeforeSend: false,
      completedResponseAwaitingPersistence: false,
      ctaAction: null,
      currentUserQuestion: null,
      streaming: false,
      currentProsePreview: "",
      currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {},
      pendingAgentResponse: false,
      responsePhase: "idle",
      currentTurnReferencedComments: [],
      streamingStoryBibleGeneratedEntity: null,
      ...STORY_BIBLE_CTA_PROGRESS_RESET,
    });
  },

  resetForNewMessage: (opts) => {
    cancelTextBufferFlush();
    resetRewriteArtifactStream();
    const passage = opts?.passage?.trim() || null;
    const currentCta = get().ctaAction;
    const preserveCastAwaitingInput =
      currentCta?.type === "generate_cast" && currentCta?.status === "awaiting_input";
    const preserveCta = currentCta?.status === "in_progress" || preserveCastAwaitingInput;
    if (currentCta && !preserveCta && currentCta.signalKey) {
      useCrossAppSignalStore.getState().setSignalActive(currentCta.signalKey, false);
    }
    set({
      errorText: "",
      searchedDocs: [],
      lookingAtData: [],
      cutoffData: null,
      contextWarning: null,
      outlineUpdates: [],
      characterUpdates: [],
      worldbuildingUpdates: [],
      metaUpdates: [],
      toolRuns: [],
      thinkingSteps: [],
      thinkingTaskTimeline: [],
      activeReasoningTaskId: null,
      toolTaskIdByCallId: {},
      streaming: true,
      currentStreamingText: "",
      currentProsePreview: "",
      currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {},
      completedPlanSummary: "",
      completedPlanSummaryId: null,
      completedResponseAwaitingPersistence: false,
      currentTurnUserMessageId: null,
      currentRetryRequestId: null,
      currentRetryAssistantMessageId: null,
      currentTurnPassage: passage,
      currentTurnReferencedComments: [],
      optimisticUserMessageId: null,
      contextElements: [],
      mentionedEntities: [],
      currentModel: null,
      currentPreserveChatTextWithSuggestions: false,
      currentWritePovTenseChiclets: null,
      suggestedAction: null,
      currentUserQuestion: preserveCastAwaitingInput ? get().currentUserQuestion : null,
      streamingStoryBibleGeneratedEntity: null,
      ...STORY_BIBLE_CTA_PROGRESS_RESET,
      ...(preserveCta ? {} : { ctaAction: null }),
      pendingAgentResponse: true,
      responsePhase: "connecting",
      pendingNewChatMaterializationId: null,
    });
    if (pendingAgentResponseClearTimeout) {
      clearTimeout(pendingAgentResponseClearTimeout);
      pendingAgentResponseClearTimeout = null;
    }
    get().ensureThinkingPlaceholder();
  },

  reset: (source = "navigation") => {
    cancelTextBufferFlush();
    const restored = restoreComposerToChatOnlyAfterStoryBibleCtaCancel({
      userData: useSudowriteStore.getState().userData,
    });
    if (!restored) {
      clearAutoSwitchedFromChatToWrite();
      clearStoryBibleCtaAutoPromotedFromChat();
    }
    void get().abortStream(source);
    const cancellation = get();
    set({
      ...initialState,
      frozenReplies: cancellation.frozenReplies,
      activeRequest: cancellation.activeRequest ? { ...cancellation.activeRequest, onChatResolved: undefined } : null,
      stopping: cancellation.stopping,
      stopError: cancellation.stopError,
      stopPromise: cancellation.stopPromise,
      abortController: { current: new AbortController() },
      reconcilingBeforeSend: false,
    });
  },

  resetForDocumentSwitch: () => {
    set({
      activeHighlight: "",
      activeHighlightHtml: "",
      activeHighlightSelectionHint: null,
      dismissedHighlight: "",
    });
  },

  clearStreamingTextIfServerHasData: (messages: any[], projectId?: string) => {
    const state = get();
    const frozenReplies = state.frozenReplies.filter(reply => !messages.some(message =>
      message.role === "assistant" && message.metadata?.clientRequestId === reply.clientRequestId
      && (!reply.content.trim() || message.content?.trim())));
    if (frozenReplies.length !== state.frozenReplies.length) {
      saveFrozenChatReplies(frozenReplies);
      set({ frozenReplies });
    }
    if (state.currentRetryRequestId) {
      messages = messages.filter((message) => message.role !== "assistant"
        || message.metadata?.clientRequestId === state.currentRetryRequestId
        || message.id === state.currentRetryAssistantMessageId);
    }
    // Plan progress/approval writes also refresh history. They are not the final
    // response, and must not make an open plan stream appear interrupted.
    if (state.streaming && (state.activePlan?.status === "in_progress" || state.completedPlanSummary)) return;
    let recoveredPlanReply: any;
    if (state.completedPlanSummary && state.completedResponseAwaitingPersistence) {
      recoveredPlanReply = [...messages].reverse().find(message => message.role === "assistant"
        && message.metadata?.planExecution === true
        && message.metadata?.plan?.id === state.completedPlanSummaryId
        && message.metadata?.plan?.status === "completed"
        && (message.content?.trim() || messageHasRewriteSuggestionCarouselMetadata(message) || messageHasFinalManuscriptChange(message)));
      if (!recoveredPlanReply) return;
    }
    const finalizeRecoveredResponse = (persistedAssistant?: any) => {
      const current = get();
      // Reconcile the sidebar as well as the chat after terminal SSE events were
      // missed. Clearing the pending turn below makes repeated history polls a no-op.
      const recoveryProjectId = projectId ?? current.activeRequest?.projectId;
      const isRecoveryProjectActive = () => useProjectStore.getState().project?.id === recoveryProjectId;
      const matchesActiveProject = !current.activeRequest || current.activeRequest.projectId === recoveryProjectId;
      if (recoveryProjectId && matchesActiveProject && isRecoveryProjectActive() && persistedAssistant?.role === "assistant"
        && (current.streaming || current.pendingAgentResponse || current.currentTurnUserMessageId || current.completedResponseAwaitingPersistence)) {
        const metadata = persistedAssistant.metadata;
        const entity = parseStoryBibleGeneratedEntity(metadata?.storyBibleGeneratedEntity);
        const characters = Boolean(metadata?.characterUpdates?.length) || entity?.entityType === "character"
          || (entity?.entityType === "story_bible_section" && entity.entityId === "characters");
        const worldbuilding = Boolean(metadata?.worldbuildingUpdates?.length) || entity?.entityType === "worldbuilding"
          || (entity?.entityType === "story_bible_section" && entity.entityId === "worldbuilding");
        if (characters || worldbuilding) void applyStoryBibleRefreshHint(recoveryProjectId, { characters, worldbuilding }, false, isRecoveryProjectActive);
      }
      // During send preflight, CTA progress may already belong to the follow-up.
      // Reconcile the previous reply without completing the next turn's action.
      if (!current.reconcilingBeforeSend) {
        if (current.ctaAction?.status === "in_progress") {
          if (current.ctaAction.signalKey) {
            useCrossAppSignalStore.getState().setSignalActive(current.ctaAction.signalKey, false);
          }
          set({ ctaAction: { ...current.ctaAction, status: "completed" } });
        }
        // Extra guard: synopsis CTA shimmer can remain on if stream terminal events are dropped.
        useCrossAppSignalStore.getState().setSignalActive(getSynopsisCTASignal(), false);
      }
      if (current.toolRuns.some((tool) => tool.status === "running")) {
        get().setToolRuns((prev) => prev.map((tool) => (tool.status === "running" ? { ...tool, status: "complete" } : tool)));
      }
      set({
        ...(current.streaming ? { streaming: false } : {}),
        ...(current.currentStreamingText ? { currentStreamingText: "" } : {}),
        currentProsePreview: "",
        currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {},
        currentTurnUserMessageId: null,
        currentRetryRequestId: null,
        currentRetryAssistantMessageId: null,
        completedResponseAwaitingPersistence: false,
        completedPlanSummary: "",
        completedPlanSummaryId: null,
        currentTurnPassage: null,
        currentTurnReferencedComments: [],
        optimisticUserMessageId: null,
        pendingAgentResponse: false,
        responsePhase: "idle",
        streamingStoryBibleGeneratedEntity: null,
      });
    };

    if (state.completedPlanSummary && state.completedResponseAwaitingPersistence) {
      finalizeRecoveredResponse(recoveredPlanReply);
      return;
    }

    const persistedAssistantHasStoryBibleCard = (message: { metadata?: unknown } | null | undefined) =>
      !!parseStoryBibleGeneratedEntity((message?.metadata as { storyBibleGeneratedEntity?: unknown } | undefined)?.storyBibleGeneratedEntity);

    if (state.currentTurnUserMessageId) {
      const persistedAssistantByTrigger = [...messages]
        .reverse()
        .find((message) => message?.role === "assistant" && message.metadata?.triggerUserMessageId === state.currentTurnUserMessageId);
      const triggeringUserIndex = messages.findIndex((message) => message?.id === state.currentTurnUserMessageId);
      const persistedAssistantAfterTriggeringUser = triggeringUserIndex >= 0
        ? messages.slice(triggeringUserIndex + 1).find((message) => message?.role === "assistant")
        : null;
      const persistedAssistant = persistedAssistantByTrigger ?? persistedAssistantAfterTriggeringUser;

      if (!persistedAssistant) {
        return;
      }

      const hasStoryBibleCard = persistedAssistantHasStoryBibleCard(persistedAssistant);
      const hasRewriteCarousel = messageHasRewriteSuggestionCarouselMetadata(persistedAssistant) || messageHasFinalManuscriptChange(persistedAssistant);
      // Assistant commentary can persist before the final prose receipt.
      if (state.currentProsePreview && !hasRewriteCarousel) return;

      // Recovery path: if an assistant reply for the current turn has already persisted,
      // force-finish any stale local streaming/pending state even when SSE never emits a terminal event.
      if (state.streaming) {
        // While a CTA stream is actively running, an empty persisted assistant placeholder can
        // appear before the final reply lands. Do not finalize until persisted content exists.
        if (state.ctaAction?.status === "in_progress" && !persistedAssistant.content?.trim() && !hasStoryBibleCard && !hasRewriteCarousel) {
          return;
        }
        if (
          !persistedAssistant.content?.trim()
          && !hasStoryBibleCard
          && !hasRewriteCarousel
          && (state.currentStreamingText?.trim() || state.streamingStoryBibleGeneratedEntity)
        ) {
          return;
        }
        finalizeRecoveredResponse(persistedAssistant);
        return;
      }

      if (state.streamingStoryBibleGeneratedEntity && !hasStoryBibleCard) {
        return;
      }

      if (persistedAssistant.content?.trim() || hasStoryBibleCard || hasRewriteCarousel || !state.currentStreamingText?.trim()) {
        finalizeRecoveredResponse(persistedAssistant);
      }
      return;
    }

    const serverHasNewData = messages.length > state.messageCountBeforeResponse;
    if (serverHasNewData) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.role === "assistant" && !lastMessage.content?.trim() && state.currentStreamingText?.trim()) {
        // Same protection for flows without a current turn id: keep streaming CTA runs alive
        // until terminal events arrive or persisted assistant content is non-empty.
        if (state.streaming && state.ctaAction?.status === "in_progress" && !persistedAssistantHasStoryBibleCard(lastMessage)) return;
        if (messageHasRewriteSuggestionCarouselMetadata(lastMessage) || messageHasFinalManuscriptChange(lastMessage)) {
          finalizeRecoveredResponse(lastMessage);
          return;
        }
        return;
      }
      // Server never persisted the response (aborted turn) — keep local partial text visible.
      if (lastMessage?.role === "user" && state.currentStreamingText?.trim()) {
        if (state.ctaAction?.status === "in_progress") {
          finalizeRecoveredResponse(lastMessage);
        }
        return;
      }
      if (
        state.streamingStoryBibleGeneratedEntity
        && lastMessage?.role === "assistant"
        && !persistedAssistantHasStoryBibleCard(lastMessage)
      ) {
        return;
      }
      finalizeRecoveredResponse(lastMessage);
    }
  },

  sendMessage: async (params) => {
    const operationGeneration = ++sendOperationGeneration;
    if (params.resendIntent === "edit" && params.replacementCommandId) {
      const controller = get().abortController.current;
      try {
        const status = await Auth.fetchWithIdToken("/api/ai/agenticChat/message/status", { clientRequestId: params.replacementCommandId });
        if (get().abortController.current !== controller || operationGeneration !== sendOperationGeneration) return;
        if (status?.state === "running" || status?.state === "cancelling") {
          set({ failedFollowUp: { params, message: "Your edit is still being processed. Check again shortly." } });
          return;
        }
        if (status?.state === "done" && status.userMessageId && status.chatId) {
          set({ failedFollowUp: null, errorText: "" });
          params.setChatId(status.chatId);
          void Promise.resolve(queryClient.invalidateQueries(chatQueryKey(params.projectId, status.chatId))).catch(() => {});
          return;
        }
        if (status?.state === "done" || status?.state === "cancelled") {
          // The server settled without persisting an edit. A new attempt is safe.
          params = { ...params, replacementCommandId: undefined };
        } else if (status?.state !== "unknown") throw new Error("Unknown command status");
      } catch {
        if (get().abortController.current === controller && operationGeneration === sendOperationGeneration) set({ failedFollowUp: { params, message: "Could not check whether your edit was saved. Your text is preserved; please retry." } });
        return;
      }
    }
    const {
      instructions,
      modelKey,
      mode,
      passage,
      passageHtml,
      selectionAnnotationIds,
      commentSidebarOrderIds,
      activeManuscriptCommentId,
      documentText,
      documentId,
      openDocumentId,
      projectId,
      chatId,
      isStoryBibleActive,
      setChatId,
      currentMessages,
      onStreamError,
      instructionsDisplayOverride,
    } = params;

    if (params.startNewChat && chatId === NEW_CHAT_ID) {
      set({ pendingNewChatMaterializationId: null });
    }
    const previousTurn = get();
    const isReplacement = params.resendIntent === "edit";
    if (isReplacement) {
      const original = previousTurn.activeRequest;
      const optimisticTarget = params.resendOfMessageId && isOptimisticClientUserMessageId(params.resendOfMessageId);
      const cachedTarget = optimisticTarget
        ? (currentMessages ?? []).find(message => message.id === params.resendOfMessageId)
          ?? queryClient.getQueryData<PersistentChatWithMessages>(chatQueryKey(projectId, chatId))?.messages.find(message => message.id === params.resendOfMessageId)
        : undefined;
      const targetRequestId = typeof cachedTarget?.metadata?.clientRequestId === "string" ? cachedTarget.metadata.clientRequestId : undefined;
      const frozenTarget = optimisticTarget ? previousTurn.frozenReplies.find(reply =>
        reply.projectId === projectId && reply.userMessageId === params.resendOfMessageId
        && (reply.chatId === chatId || (reply.chatId === NEW_CHAT_ID && previousTurn.pendingNewChatMaterializationId === chatId))) : undefined;
      params = { ...params,
        replacementCommandId: params.replacementCommandId ?? crypto.randomUUID(),
        replacesClientRequestId: params.replacesClientRequestId ?? original?.clientRequestId,
        priorClientRequestIds: [...new Set([...(params.priorClientRequestIds ?? []), ...(original?.priorClientRequestIds ?? []),
          ...(original ? [original.clientRequestId] : []), ...(params.replacesClientRequestId ? [params.replacesClientRequestId] : [])])],
        targetClientRequestId: params.targetClientRequestId ?? (optimisticTarget ? targetRequestId ?? frozenTarget?.clientRequestId ?? original?.targetClientRequestId ?? original?.clientRequestId : undefined),
      };
      flushTextBufferSync(set);
      const stoppedUserId = previousTurn.currentTurnUserMessageId ?? previousTurn.optimisticUserMessageId;
      if (original && stoppedUserId && !get().frozenReplies.some(reply => reply.clientRequestId === original.clientRequestId)) {
        const frozenReplies = [...get().frozenReplies, { ...original, userMessageId: stoppedUserId,
          content: get().currentStreamingText, createdAt: new Date().toISOString() }];
        saveFrozenChatReplies(frozenReplies); set({ frozenReplies });
      }
      // Fence callbacks locally; the replacement command owns server cancellation.
      set({ abortController: { current: new AbortController() }, activeRequest: null,
        stopping: false, stopPromise: null, stopError: "", queuedSend: null,
        streaming: false, currentStreamingText: "", completedResponseAwaitingPersistence: false, reconcilingBeforeSend: false });
      setStreamAbortSource(previousTurn.abortController.current.signal, "replacement");
      previousTurn.abortController.current.abort();
      if (stopRecoveryTimeout) { clearTimeout(stopRecoveryTimeout); stopRecoveryTimeout = null; }
    } else {
      if (previousTurn.queuedSend || previousTurn.reconcilingBeforeSend) return;
      if (previousTurn.stopping) {
        set({ queuedSend: params });
        if (!previousTurn.stopPromise) void get().abortStream();
        return;
      }
    }
    if (!isReplacement && previousTurn.abortController.current.signal.aborted) {
      set({ abortController: { current: new AbortController() } });
    }
    let baselineMessageCountForTurn = currentMessages?.length || 0;
    const isCta = !!params.ctaAction;
    // A refetch can lag behind the completed assistant turn. The server owns the active
    // leaf; only explicit branch/edit operations should supply a client parent.
    const preferredParentMessageId = params.parentMessageId;

    // Set pendingAgentResponse immediately so no render can show the resend button
    // before streaming starts. This must be the first state mutation in sendMessage.
    if (pendingAgentResponseClearTimeout) {
      clearTimeout(pendingAgentResponseClearTimeout);
      pendingAgentResponseClearTimeout = null;
    }
    set({ pendingAgentResponse: true, failedFollowUp: null });

    const store = get();

    const isResend = !!params.resendOfMessageId;

    const pendingMaterializationId = store.pendingNewChatMaterializationId;
    const effectiveChatId = resolveEffectiveChatId(chatId, pendingMaterializationId);
    const resolvedFromPendingMaterialization =
      chatId === NEW_CHAT_ID
      && pendingMaterializationId != null
      && effectiveChatId === pendingMaterializationId;
    if (effectiveChatId !== chatId) {
      setChatId(effectiveChatId);
    }

    // Preserve the completed local answer until a fresh history read contains it.
    // Resetting first would drop that answer and attach the optimistic follow-up
    // to the stale cached user leaf. Refetch also cancels older in-flight reads.
    if (!isReplacement && previousTurn.completedResponseAwaitingPersistence) {
      const controller = previousTurn.abortController.current;
      const stillCurrent = () => get().abortController.current === controller && !controller.signal.aborted;
      set({ reconcilingBeforeSend: true, errorText: "" });
      try {
        const key = chatQueryKey(projectId, effectiveChatId);
        await queryClient.refetchQueries({ queryKey: key, exact: true }, { throwOnError: true, cancelRefetch: true });
        if (!stillCurrent()) return;
        const refreshed = queryClient.getQueryData<PersistentChatWithMessages>(key);
        const path = linearPathToLeaf(refreshed?.messages ?? [], refreshed?.active_leaf_message_id ?? null);
        const userIndex = path.findIndex((message) => message.id === previousTurn.currentTurnUserMessageId);
        const hasReply = path.some((message, index) => message.role === "assistant"
          && (message.metadata?.triggerUserMessageId === previousTurn.currentTurnUserMessageId
            || (userIndex >= 0 && index > userIndex)));
        if (!hasReply) throw new Error("Completed chat reply has not reconciled");
        get().clearStreamingTextIfServerHasData(path);
        if (get().completedResponseAwaitingPersistence) throw new Error("Completed chat reply is still incomplete");
        baselineMessageCountForTurn = path.length;
      } catch (error) {
        if (!stillCurrent()) return;
        const message = "Could not refresh the previous reply. Your follow-up was not sent. Please try again.";
        set({ reconcilingBeforeSend: false, pendingAgentResponse: false, responsePhase: "idle", failedFollowUp: { params, message } });
        onStreamError?.(message);
        return;
      }
      set({ reconcilingBeforeSend: false, pendingAgentResponse: true });
    }

    let messageCountBeforeResponse = baselineMessageCountForTurn + (isResend || isCta ? 0 : 1);
    if (isResend && !isCta && params.resendOfMessageId) {
      const resendIdx = (currentMessages || []).findIndex((m) => m.id === params.resendOfMessageId);
      if (resendIdx >= 0) {
        messageCountBeforeResponse = resendIdx + 1;
      }
    }
    set({ messageCountBeforeResponse });
    const clientRequestId = params.replacementCommandId ?? crypto.randomUUID();
    set({ activeRequest: { clientRequestId, projectId, chatId: effectiveChatId, onChatResolved: setChatId, priorClientRequestIds: params.priorClientRequestIds, targetClientRequestId: params.targetClientRequestId } });
    store.resetForNewMessage({ passage });
    if (params.resendIntent === "retry") set({ currentRetryRequestId: clientRequestId });
    if (resolvedFromPendingMaterialization && pendingMaterializationId) {
      // resetForNewMessage clears pendingNewChatMaterializationId; restore it so the
      // chat-switch effect can treat NEW_CHAT_ID → persisted id as expected materialization
      // and not abort this in-flight follow-up (e.g. hidden cast-size answer).
      set({ pendingNewChatMaterializationId: pendingMaterializationId });
    }

    if (isResend && !isCta && params.resendOfMessageId) {
      const cachedForResendCta = queryClient.getQueryData<PersistentChatWithMessages>(
        chatQueryKey(projectId, effectiveChatId),
      );
      const resendSourceMessages = cachedForResendCta?.messages?.length
        ? cachedForResendCta.messages
        : (currentMessages || []);
      const resendUser = resendSourceMessages.find((m) => m?.id === params.resendOfMessageId);
      const restoredCta = ctaActionForResendFromUserMessage(resendUser);
      if (restoredCta) {
        get().setCtaAction(restoredCta);
      }
    }

    if (!isResend && !isCta && !params.hidden) {
      const optimisticUserMessageId = `optimistic-user-${makeId(8)}`;
      const optimisticMetadata = {
        ...(params.userMessageMetadata ?? {}),
        clientRequestId,
      };
      /** Match PersistentChat.addMessage: parent to current active leaf and advance leaf so linearPathToLeaf shows the bubble. */
      queryClient.setQueryData(chatQueryKey(projectId, effectiveChatId), (prev: PersistentChatWithMessages | undefined) => {
        const base = prev ?? DefaultChatModel;
        const parentId = base.active_leaf_message_id ?? null;
        const optimisticMessage = {
          id: optimisticUserMessageId,
          role: "user",
          content: instructionsDisplayOverride ?? instructions,
          created_at: new Date(),
          ...(parentId != null ? { parent_message_id: parentId } : {}),
          ...(Object.keys(optimisticMetadata).length ? { metadata: optimisticMetadata } : {}),
        };
        return {
          ...base,
          active_leaf_message_id: optimisticUserMessageId,
          messages: [...(base?.messages || []), optimisticMessage as any],
        };
      });
      set({ optimisticUserMessageId });
    } else if (isResend && !isCta && !params.hidden && params.resendOfMessageId) {
      const resendId = params.resendOfMessageId;
      // Match server: branch vs in-place uses full chat rows, not linearPathToLeaf(currentMessages).
      const cachedChat = queryClient.getQueryData<PersistentChatWithMessages>(
        chatQueryKey(projectId, effectiveChatId),
      );
      const allMessagesForBranchResend = cachedChat?.messages?.length
        ? cachedChat.messages
        : (currentMessages || []);
      const branchResend = shouldBranchResendUserMessage(allMessagesForBranchResend as any, resendId, params.resendIntent);
      const preserveTurnContextOnResendMetadata = (
        merged: Record<string, unknown>,
        source: Record<string, unknown>,
      ) => {
        const sourceTurnContext = source.turnContext;
        if (sourceTurnContext && typeof sourceTurnContext === "object") {
          merged.turnContext = sourceTurnContext;
        }
      };
      if (branchResend) {
        const optimisticUserMessageId = `optimistic-user-${makeId(8)}`;
        queryClient.setQueryData(chatQueryKey(projectId, effectiveChatId), (prev: PersistentChatWithMessages | undefined) => {
          const base = prev ?? DefaultChatModel;
          const messages = [...(base.messages || [])];
          const idx = messages.findIndex((m) => m.id === resendId);
          const sourceUser = idx >= 0 ? messages[idx] : null;
          const sourceMetadata =
            sourceUser && typeof sourceUser.metadata === "object" && sourceUser.metadata !== null
              ? { ...(sourceUser.metadata as Record<string, unknown>) }
              : {};
          const mergedMetadata: Record<string, unknown> = {
            ...sourceMetadata,
            ...((params.userMessageMetadata ?? {}) as Record<string, unknown>),
          };
          preserveTurnContextOnResendMetadata(mergedMetadata, sourceMetadata);
          const parentId = sourceUser ? (sourceUser as { parent_message_id?: string | null }).parent_message_id ?? null : null;
          const optimisticMessage = {
            id: optimisticUserMessageId,
            role: "user",
            content: instructionsDisplayOverride ?? instructions,
            created_at: new Date(),
            parent_message_id: parentId,
            ...(Object.keys(mergedMetadata).length ? { metadata: mergedMetadata } : {}),
          };
          return {
            ...base,
            active_leaf_message_id: optimisticUserMessageId,
            messages: [...messages, optimisticMessage as any],
          };
        });
        set({ optimisticUserMessageId });
      } else {
        queryClient.setQueryData(chatQueryKey(projectId, effectiveChatId), (prev: PersistentChatWithMessages | undefined) => {
          const base = prev ?? DefaultChatModel;
          const messages = [...(base.messages || [])];
          const idx = messages.findIndex((m) => m.id === resendId);
          if (idx < 0) return base;
          const sourceUser = messages[idx];
          const sourceMetadata =
            sourceUser && typeof sourceUser.metadata === "object" && sourceUser.metadata !== null
              ? { ...(sourceUser.metadata as Record<string, unknown>) }
              : {};
          const mergedMetadata: Record<string, unknown> = {
            ...sourceMetadata,
            ...((params.userMessageMetadata ?? {}) as Record<string, unknown>),
          };
          preserveTurnContextOnResendMetadata(mergedMetadata, sourceMetadata);
          messages[idx] = {
            ...sourceUser,
            content: instructionsDisplayOverride ?? instructions,
            ...(Object.keys(mergedMetadata).length ? { metadata: mergedMetadata } : {}),
          };
          return {
            ...base,
            active_leaf_message_id: params.resendIntent === "retry" ? base.active_leaf_message_id : resendId,
            messages,
          };
        });
        set({
          currentTurnUserMessageId: resendId,
          optimisticUserMessageId: isOptimisticClientUserMessageId(resendId) ? resendId : null,
        });
      }
    } else if (isCta && !params.hidden) {
      const ctaAction = params.ctaAction;
      const isEntityGenerateCta =
        ctaAction?.type === "generate_character" || ctaAction?.type === "generate_worldbuilding";
      const generateInputs = isEntityGenerateCta
        ? entityGenerateInputsFromCtaAction(ctaAction)
        : null;
      queryClient.setQueryData(chatQueryKey(projectId, effectiveChatId), (prev: PersistentChatWithMessages | undefined) => {
        if (!prev?.messages?.length) return prev;
        const messages = [...prev.messages];
        for (let i = messages.length - 1; i >= 0; i--) {
          const message = messages[i];
          if (message?.role !== "user") continue;
          const existingMetadata =
            message.metadata && typeof message.metadata === "object"
              ? { ...(message.metadata as Record<string, unknown>) }
              : {};
          const baseTitle =
            (typeof message.content === "string" ? message.content.split("\n")[0]?.trim() : "")
            || (ctaAction.type === "generate_character" ? "Generate Character" : "Generate Element");
          messages[i] = {
            ...message,
            ...(isEntityGenerateCta && ctaAction
              ? {
                  content: buildEntityGenerateUserMessageFromCtaAction(baseTitle, {
                    ...ctaAction,
                    ...(generateInputs ?? {}),
                  }),
                }
              : {}),
            metadata: {
              ...existingMetadata,
              ...(generateInputs ? { generateInputs } : {}),
              ...(ctaAction?.type ? { ctaActionType: ctaAction.type } : {}),
            },
          };
          break;
        }
        return { ...prev, messages };
      });
    }

    set({ currentTurnPassage: passage?.trim() ? passage : null });
    const activeCommentId = activeManuscriptCommentId?.trim();
    if (activeCommentId) {
      const allComments = useCommentStore.getState().allComments;
      const stack = Array.isArray(allComments) ? [...allComments] : [];
      let previewText: string | undefined;
      while (stack.length > 0) {
        const next = stack.shift() as any;
        if (!next) continue;
        if (String(next.id ?? "") === activeCommentId) {
          previewText = firstFiveWords(typeof next.content === "string" ? next.content : "");
          break;
        }
        if (Array.isArray(next.replies) && next.replies.length > 0) {
          stack.push(...next.replies);
        }
      }
      set({
        currentTurnReferencedComments: [{
          commentId: activeCommentId,
          documentId: documentId?.trim() || undefined,
          previewText,
        }],
      });
    } else {
      set({ currentTurnReferencedComments: [] });
    }

    let resolvedChatId = effectiveChatId;
    const isNewChat = effectiveChatId === NEW_CHAT_ID;
    const pendingProjectUiActionsRef: { current: Promise<void> } = { current: Promise.resolve() };
    const agenticWriteButtonSettings = buildAgenticWriteButtonSettingsFromEditor();

    const outlineFlush = await flushDirtyOutlineBeforeMessage(projectId);
    const outlineSaveFailed = outlineFlush.attempted && !outlineFlush.succeeded;
    const shouldSkipCachedSelectionMerge =
      !String(passage ?? "").trim()
      && !params.editorSelectionHint
      && !!store.dismissedHighlight?.trim();

    const serverResendOfMessageId =
      params.resendOfMessageId && !isOptimisticClientUserMessageId(params.resendOfMessageId)
        ? params.resendOfMessageId
        : undefined;

    if (store.abortController.current.signal.aborted || get().stopping) return;
    const payload: AgenticChatRequestBody = {
      clientRequestId,
      ...(isReplacement ? { priorClientRequestIds: params.priorClientRequestIds, replacesClientRequestId: params.replacesClientRequestId, targetClientRequestId: params.targetClientRequestId } : {}),
      ...(params.resendIntent ? { resendIntent: params.resendIntent } : {}),
      projectId,
      chatId: effectiveChatId === NEW_CHAT_ID ? undefined : effectiveChatId,
      message: instructions,
      ...(params.pastedExcerpts?.length ? { pastedExcerpts: params.pastedExcerpts } : {}),
      ...(passage !== undefined ? { passage } : {}),
      ...(passageHtml?.trim() ? { passageHtml } : {}),
      isStoryBibleActive: params.isStoryBibleActive === true,
      model: modelKey,
      mode,
      manuscriptChangeCapability: {
        capabilityVersion: 2,
        canReview: true,
        canAcknowledgeVisibility: false,
      },
      documentText,
      documentId,
      ...(params.openDocumentId ? { openDocumentId: params.openDocumentId } : {}),
      ...(selectionAnnotationIds?.length ? { selectionAnnotationIds } : {}),
      ...(commentSidebarOrderIds?.length ? { commentSidebarOrderIds } : {}),
      ...(activeManuscriptCommentId ? { activeManuscriptCommentId } : {}),
      ...(params.hidden && { hidden: true }),
      ...(params.ctaAction ? { ctaAction: params.ctaAction } : {}),
      ...(params.userMessageMetadata ? { userMessageMetadata: params.userMessageMetadata } : {}),
      ...(serverResendOfMessageId ? { resendOfMessageId: serverResendOfMessageId } : {}),
      ...(preferredParentMessageId !== undefined ? { parentMessageId: preferredParentMessageId } : {}),
      ...(params.editorSelectionHint ? { editorSelectionHint: params.editorSelectionHint } : {}),
      ...(() => {
        const trimmedPassage = String(passage ?? "").trim();
        const targetDocumentId = String(documentId ?? params.editorSelectionHint?.documentId ?? "").trim();
        const selectionTarget = trimmedPassage && targetDocumentId
          ? buildManuscriptSelectionTarget({
            documentId: targetDocumentId,
            passageText: trimmedPassage,
            editorSelectionHint: params.editorSelectionHint,
          })
          : null;
        return selectionTarget ? { selectionTarget } : {};
      })(),
      ...(params.editedPlanApproved ? { editedPlanApproved: true } : {}),
      ...(params.editedPlan ? { editedPlan: params.editedPlan } : {}),
      ...(agenticWriteButtonSettings ? { agenticWriteButtonSettings } : {}),
      ...(outlineSaveFailed ? { outlineSaveFailed: true } : {}),
      ...(shouldSkipCachedSelectionMerge ? { skipCachedEditorSelectionMerge: true } : {}),
      timeZone: getBrowserTimeZone(),
    };

    const streamSignal = store.abortController.current.signal;
    const messagePath = isReplacement ? "/api/ai/agenticChat/message/replace" : "/api/ai/agenticChat/message";
    const messageEndpoint = shouldEnableForeignLanguageNaturalToneExperiment()
      ? `${messagePath}?${CHAT_NATURAL_TONE_EXPERIMENT_QUERY_PARAM}=1` : messagePath;
    const deliverStreamChunk = (chunk: AgenticLegacyStreamChunk) => {
        if (!chunk || isStalePersistentChatStream(get, streamSignal)) return;

        const shouldEnterStreamingPhase =
          chunk.event
            ? !["chat_acknowledged", "chat_response_context", "mentioned_entity", "context_element"].includes(chunk.event)
            : Boolean(chunk.text || chunk.type || chunk.documentTitle);

        if (shouldEnterStreamingPhase && get().responsePhase !== "streaming") {
          set({ responsePhase: "streaming" });
        }

        if (chunk.event) {
          switch (chunk.event) {
            case "chat_response_context": {
              set({ currentPreserveChatTextWithSuggestions: chunk.preserveChatTextWithSuggestions === true });
              break;
            }
            case "chat_acknowledged": {
              const acknowledgedRequest = get().activeRequest;
              if (acknowledgedRequest?.clientRequestId === clientRequestId) {
                set({ activeRequest: { ...acknowledgedRequest, priorClientRequestIds: [] } });
              }
              const updatedChatId = typeof chunk.chatId === "string" ? chunk.chatId : null;
              if (updatedChatId && updatedChatId !== effectiveChatId) {
                resolvedChatId = updatedChatId;
                if (get().activeRequest?.clientRequestId === clientRequestId) {
                  set({ activeRequest: { ...get().activeRequest!, chatId: updatedChatId } });
                }
                const oldData = queryClient.getQueryData(chatQueryKey(projectId, effectiveChatId));
                if (oldData) {
                  queryClient.setQueryData(chatQueryKey(projectId, updatedChatId), oldData);
                }
                // Only skip reset when materializing from the compose placeholder (NEW_CHAT_ID → persisted id).
                if (chatId === NEW_CHAT_ID) {
                  set({ pendingNewChatMaterializationId: updatedChatId });
                }
                setChatId(updatedChatId);
                queryClient.invalidateQueries(["chats", projectId]);
              }
              const model = typeof chunk.model === "string" ? chunk.model : null;
              const userMessageId = typeof chunk.userMessageId === "string" ? chunk.userMessageId : null;
              const optimisticUserMessageId = get().optimisticUserMessageId;
              if (model) {
                set({ currentModel: model });
              }
              if (userMessageId) {
                queryClient.setQueryData(chatQueryKey(projectId, resolvedChatId), (prev: PersistentChatWithMessages | undefined) => {
                  if (!prev?.messages?.length) {
                    return prev;
                  }

                  const nextMessages = [...prev.messages];
                  let targetIndex = optimisticUserMessageId
                    ? nextMessages.findIndex((message) => message.id === optimisticUserMessageId)
                    : -1;

                  if (targetIndex === -1) {
                    for (let i = nextMessages.length - 1; i >= 0; i--) {
                      const message = nextMessages[i];
                      if (message?.role === "user" && !message.id) {
                        targetIndex = i;
                        break;
                      }
                    }
                  }

                  if (targetIndex === -1) {
                    return prev;
                  }

                  nextMessages[targetIndex] = {
                    ...nextMessages[targetIndex],
                    id: userMessageId,
                  };

                  return {
                    ...prev,
                    active_leaf_message_id:
                      prev.active_leaf_message_id === optimisticUserMessageId
                        ? userMessageId
                        : prev.active_leaf_message_id,
                    messages: nextMessages,
                  };
                });
                // The server may branch a resend after saving an interrupted reply that
                // the client has not fetched. Its acknowledged ID overrides our in-place
                // prediction so completed replies reconcile against the correct branch.
                set({ currentTurnUserMessageId: userMessageId, optimisticUserMessageId: null });
              }
              break;
            }
            case "tool_start": {
              const callId = typeof chunk.callId === "string" ? chunk.callId : "";
              const tool = typeof chunk.tool === "string" ? chunk.tool : "tool";
              const toolArgs =
                typeof chunk.arguments === "object" && chunk.arguments !== null
                  ? chunk.arguments as Record<string, unknown>
                  : undefined;
              const isWrite = isWriteToolName(tool, toolArgs);
              const readTargetLabel = typeof chunk.readTargetLabel === "string" ? chunk.readTargetLabel : undefined;
              if (tool === "mutate_manuscript_comments") {
                const referencedIds = extractReferencedCommentIdsFromMutationArgs(chunk.arguments);
                if (referencedIds.length > 0) {
                  const existingById = new Map<string, ReferencedComment>(
                    get().currentTurnReferencedComments.map((reference) => [reference.commentId, reference]),
                  );
                  const updated = dedupeReferencedComments([
                    ...get().currentTurnReferencedComments,
                    ...referencedIds.map((commentId) => ({
                      commentId,
                      documentId: existingById.get(commentId)?.documentId
                        ?? get().currentTurnReferencedComments[0]?.documentId
                        ?? documentId
                        ?? undefined,
                      previewText: existingById.get(commentId)?.previewText,
                    })),
                  ]);
                  set({ currentTurnReferencedComments: updated });
                }
              }
              {
                const signal = getToolSignal(tool);
                if (signal) useCrossAppSignalStore.getState().setSignalActive(signal, true);
              }
              get().setToolRuns((prev) => [...prev.filter((t) => t.callId !== callId), { callId, name: tool, status: "running" }]);
              get().finalizeReasoningTask();
              let cta = get().ctaAction;
              if (
                tool === "generate_cast"
                && cta?.type === "generate_cast"
                && cta.status !== "in_progress"
              ) {
                cta = { ...cta, status: "in_progress" };
                set({
                  ctaAction: cta,
                  ...storyBibleCtaProgressFieldsForSetCtaAction(cta),
                });
              }
              const skipEntityCtaTimeline =
                cta?.status === "in_progress" && isStoryBibleCtaType(cta.type);
              const skipHiddenThinkingTimeline = shouldSkipThinkingToolTimeline(tool, get().mode);
              if (!skipEntityCtaTimeline && !skipHiddenThinkingTimeline) {
                get().startToolTimelineTask(
                  callId || undefined,
                  tool,
                  undefined,
                  isWrite,
                  readTargetLabel,
                  toolArgs,
                );
              }
              if (
                tool === "manage_project_documents"
                || tool === "apply_manuscript_edits"
              ) {
                let op: string | undefined;
                const raw = (chunk as { arguments?: unknown }).arguments;
                if (raw && typeof raw === "object" && raw !== null && "operation" in raw) {
                  op = String((raw as { operation?: unknown }).operation ?? "");
                } else if (typeof raw === "string") {
                  try {
                    const parsed = JSON.parse(raw) as { operation?: string };
                    op = parsed?.operation;
                  } catch {
                    /* ignore */
                  }
                }
                if (manuscriptUndoCaptureOperation(op)) {
                  window.dispatchEvent(new CustomEvent("sudo-agent-edit-document-start", { bubbles: false }));
                }
              }
              if (tool === "generate_write_button_prose") {
                cancelTextBufferFlush();
                set({ currentStreamingText: "" });
                _rewriteArtifactStream = {
                  callId,
                  bufferedText: "",
                  suppressUntilStreamEnd: false,
                };
                // Selection rewrite fast path applies via server Yjs write (not manage_project_documents).
                window.dispatchEvent(new CustomEvent("sudo-agent-edit-document-start", { bubbles: false }));
              }
              {
                const ctaForProgress = get().ctaAction;
                if (
                  ctaForProgress?.status === "in_progress"
                  && isStoryBibleCtaType(ctaForProgress.type)
                ) {
                  onStoryBibleCtaToolStart(get, set, tool);
                }
                ensureActiveCommentingSessionOnToolStart(get, set, tool, toolArgs);
                onCommentingToolStart(get, set, tool);
              }
              break;
            }
            case "write_prose_context": {
              const writePovTenseChiclets = parseWritePovTenseChiclets(
                (chunk as { writePovTenseChiclets?: unknown }).writePovTenseChiclets,
              );
              if (writePovTenseChiclets?.length) {
                set({ currentWritePovTenseChiclets: writePovTenseChiclets });
              }
              break;
            }
            case "tool_complete": {
              const callId = typeof chunk.callId === "string" ? chunk.callId : "";
              const tool = typeof chunk.tool === "string" ? chunk.tool : "tool";
              const toolModel = typeof chunk.model === "string" ? chunk.model.trim() : "";
              if (toolModel) {
                set({ currentModel: toolModel });
              }
              const toolPovTenseChiclets = parseWritePovTenseChiclets(
                (chunk as { writePovTenseChiclets?: unknown }).writePovTenseChiclets,
              );
              if (toolPovTenseChiclets?.length) {
                set({ currentWritePovTenseChiclets: toolPovTenseChiclets });
              }
              const outcome = typeof chunk.outcome === "string"
                ? chunk.outcome
                : typeof chunk.summary === "string"
                  ? chunk.summary
                  : undefined;
              const elementType = typeof chunk.elementType === "string" ? chunk.elementType : undefined;
              const entityName = sanitizeMentionedEntityName(chunk.entityName);
              {
                const signal = getToolSignal(tool);
                if (signal) useCrossAppSignalStore.getState().setSignalActive(signal, false);
              }
              const newStatus = outcome?.toLowerCase().startsWith("error") ? "error" : "complete";
              if (
                tool === "generate_write_button_prose"
                && _rewriteArtifactStream
                && (!callId || _rewriteArtifactStream.callId === callId)
              ) {
                const bufferedText = _rewriteArtifactStream.bufferedText;
                if (newStatus === "complete" && rewriteToolResultHasSuggestionCard(chunk as Record<string, unknown>)) {
                  _rewriteArtifactStream = {
                    ..._rewriteArtifactStream,
                    bufferedText: "",
                    suppressUntilStreamEnd: true,
                  };
                } else {
                  resetRewriteArtifactStream();
                  if (newStatus === "complete" && bufferedText) {
                    appendStreamingText(bufferedText, set);
                    flushTextBufferSync(set);
                  }
                }
              }
              maybeRecordAgentManuscriptEditUndo(tool, newStatus, outcome);
              maybeOpenCommentSidebarAfterCommentCreate(chunk, newStatus);
              maybeApplyOptimisticCommentDelete(chunk, newStatus);
              get().setToolRuns((prev) => {
                if (callId) {
                  return prev.map((t) =>
                    t.callId === callId ? { ...t, status: newStatus, summary: outcome } : t
                  );
                }
                const runningIndex = prev.findIndex((t) => t.status === "running");
                if (runningIndex === -1) return prev;
                return prev.map((t, i) =>
                  i === runningIndex ? { ...t, status: newStatus, summary: outcome } : t
                );
              });
              if (newStatus === "complete") {
                onStoryBibleCtaToolComplete(get, set, tool, outcome);
                onCommentingToolComplete(get, set, tool);
              }
              if (elementType) {
                set((state) => ({
                  contextElements: state.contextElements.includes(elementType)
                    ? state.contextElements
                    : [...state.contextElements, elementType],
                }));
              }
              if (entityName && elementType) {
                set((state) => ({
                  mentionedEntities: state.mentionedEntities.some((e) => e.type === elementType && e.name === entityName)
                    ? state.mentionedEntities
                    : [...state.mentionedEntities, { type: elementType, name: entityName }],
                }));
              }
              let parsedClientActions: ClientUiAction[] = [];
              let legacyClientActions: unknown[] = [];
              if (newStatus === "complete") {
                const clientActionsPayload = extractClientActionsFromToolCompleteChunk(chunk as Record<string, unknown>);
                if (clientActionsPayload.length > 0) {
                  parsedClientActions = suppressDocumentSwitchForOffScreenFeedback(
                    parseClientUiActionsFromChunk(clientActionsPayload),
                    clientActionsPayload,
                  );
                  legacyClientActions = extractLegacyClientUiActions(clientActionsPayload);
                }
              }

              // Sidebar tree refresh / active doc switch are async; if we mark the tool complete first,
              // "Updated documents" appears before folders move in the UI.
              const awaitProjectUiBeforeTimelineComplete =
                parsedClientActions.some(
                  (a) => a.type === "refresh_project" || a.type === "set_active_document",
                );

              const finishToolCompleteTimeline = () => {
                if (!shouldSkipThinkingToolTimeline(tool, get().mode)) {
                  get().completeToolTimelineTask(callId || undefined, outcome, elementType);
                }
                const sbHint = inferStoryBibleRefreshHintFromToolCompleteChunk(tool, chunk as Record<string, unknown>);
                if (sbHint && newStatus === "complete") {
                  void applyStoryBibleRefreshHint(projectId, sbHint, storyBibleRefreshShouldClearPendingDiffs(tool));
                }
                if (newStatus === "complete" && parsedClientActions.length > 0 && !awaitProjectUiBeforeTimelineComplete) {
                  void applyClientUiActionsFromStream(parsedClientActions);
                }
                if (newStatus === "complete" && legacyClientActions.length > 0) {
                  void applyClientUiActions(legacyClientActions);
                }
              };

              if (awaitProjectUiBeforeTimelineComplete && parsedClientActions.length > 0) {
                pendingProjectUiActionsRef.current = pendingProjectUiActionsRef.current
                  .catch((error: unknown) => {
                    Sentry.captureException(error, {
                      tags: { feature: "apply_client_ui_actions", action: "prior_project_ui_actions_chain" },
                    });
                  })
                  .then(async () => {
                    await applyClientUiActionsFromStream(parsedClientActions);
                  })
                  .catch((error: unknown) => {
                    Sentry.captureException(error, {
                      tags: { feature: "apply_client_ui_actions", action: "stream_project_ui_actions" },
                      extra: { parsedClientActions },
                    });
                  });
                void (async () => {
                  try {
                    await pendingProjectUiActionsRef.current;
                  } catch (error) {
                    Sentry.captureException(error, {
                      tags: { feature: "apply_client_ui_actions", action: "stream_project_ui_actions" },
                      extra: { parsedClientActions },
                    });
                  } finally {
                    finishToolCompleteTimeline();
                  }
                })();
              } else {
                finishToolCompleteTimeline();
              }
              break;
            }
            case "search_results": {
              break;
            }
            case "context_element": {
              const elementType = typeof chunk.elementType === "string" ? chunk.elementType : undefined;
              if (elementType) {
                set((state) => ({
                  contextElements: state.contextElements.includes(elementType)
                    ? state.contextElements
                    : [...state.contextElements, elementType],
                }));
              }
              break;
            }
            case "mentioned_entity": {
              const entityType = typeof chunk.entityType === "string" ? chunk.entityType : undefined;
              const entityName = sanitizeMentionedEntityName(chunk.entityName);
              if (entityType && entityName) {
                set((state) => ({
                  mentionedEntities: state.mentionedEntities.some((e) => e.type === entityType && e.name === entityName)
                    ? state.mentionedEntities
                    : [...state.mentionedEntities, { type: entityType, name: entityName }],
                }));
              }
              break;
            }
            case "outline_update": {
              set((state) => ({
                currentChatMadeChanges: true,
                outlineUpdates: [
                  ...state.outlineUpdates,
                  {
                    summary: typeof chunk.summary === "string" ? chunk.summary : "Outline updated",
                    chapterIds: Array.isArray(chunk.chapterIds) ? chunk.chapterIds : [],
                    rationale: typeof chunk.rationale === "string" ? chunk.rationale : undefined,
                  },
                ],
              }));
              applyOutlineUpdateFromChunk(chunk);
              const outlineUpdateRefetchGeneration = startOutlineUpdateRefetch();
              (async () => {
                try {
                  const { useStructuredOutlineStore } = await import("src/components/StoryBible/StructuredOutline/StructuredOutlineStore");
                  const outlineStore = useStructuredOutlineStore.getState();
                  const { outline } = await Auth.fetchWithIdToken("/api/outline", { projectId });
                  if (!isLatestOutlineUpdateRefetch(outlineUpdateRefetchGeneration)) {
                    return;
                  }
                  outlineStore.setOutline(outline);
                  reapplyChapterDiffsToOutline();
                  reapplyOutlineUpdateSummariesFromChunk(chunk);
                } catch (error) {
                  // silently ignore
                }
              })();
              onStoryBibleCtaContentUpdate(get, set);
              break;
            }
            case "character_update": {
              const characterId = typeof chunk.characterId === "string" ? chunk.characterId : "";
              const characterName = typeof chunk.characterName === "string" ? chunk.characterName : "";
              const updatedFields = Array.isArray(chunk.updatedFields) ? chunk.updatedFields : [];
              const oldFields = chunk.oldFields && typeof chunk.oldFields === "object" ? chunk.oldFields : {};
              const newFields = chunk.newFields && typeof chunk.newFields === "object" ? chunk.newFields : {};

              set((state) => ({
                currentChatMadeChanges: true,
                characterUpdates: [
                  ...state.characterUpdates,
                  { characterId, characterName, updatedFields, oldFields, newFields },
                ],
              }));
              onStoryBibleCtaContentUpdate(get, set);

              (async () => {
                try {
                  const { useStoryBibleStore } = await import("src/stores/storyBibleStore");
                  const activeProjectId = useProjectStore.getState().project?.id;
                  if (isStalePersistentChatStream(get, streamSignal) || (activeProjectId && activeProjectId !== projectId)) return;
                  const storyBibleStore = useStoryBibleStore.getState();
                  const isNewCharacter = Object.keys(oldFields).length === 0;

                  applyCharacterUpdateFromChunk(storyBibleStore, { characterId, oldFields, newFields, character: chunk.character as any, projectId });

                  // For new characters, mark as pending (all fields should be purple)
                  if (isNewCharacter && characterId) {
                    storyBibleStore.addPendingNewCharacter(characterId);
                  }

                  // Set diff for highlighting
                  if (characterId && Object.keys(newFields).length > 0) {
                    storyBibleStore.setCharacterDiff(characterId, oldFields, newFields);
                  }
                  if (characterId && updatedFields.includes("_deleted")) {
                    storyBibleStore.clearCharacterDiff(characterId);
                    storyBibleStore.removePendingNewCharacter(characterId);
                  }

                  const characterUpdateRefetchGeneration = startCharacterUpdateRefetch();
                  // Refresh characters to get updated data (for both new and modified characters)
                  // This ensures local state has the new values so clearing diffs shows the right content
                  await storyBibleStore.refreshCharacters(projectId, { force: true });
                  if (!isLatestCharacterUpdateRefetch(characterUpdateRefetchGeneration)) {
                    return;
                  }
                } catch (error) {
                  console.error("[character_update] Failed to refresh characters:", error);
                }
              })();
              break;
            }
            case "worldbuilding_update": {
              const entryId = typeof chunk.entryId === "string" ? chunk.entryId : "";
              const entryName = typeof chunk.entryName === "string" ? chunk.entryName : "";
              const updatedFields = Array.isArray(chunk.updatedFields) ? chunk.updatedFields : [];
              const oldFields = chunk.oldFields && typeof chunk.oldFields === "object" ? chunk.oldFields : {};
              const newFields = chunk.newFields && typeof chunk.newFields === "object" ? chunk.newFields : {};

              set((state) => ({
                currentChatMadeChanges: true,
                worldbuildingUpdates: [
                  ...state.worldbuildingUpdates,
                  { entryId, entryName, updatedFields, oldFields, newFields },
                ],
              }));
              onStoryBibleCtaContentUpdate(get, set);

              (async () => {
                try {
                  const { useStoryBibleStore } = await import("src/stores/storyBibleStore");
                  const storyBibleStore = useStoryBibleStore.getState();
                  const isNewEntry = Object.keys(oldFields).length === 0;

                  // For new entries, mark as pending (all fields should be purple)
                  if (isNewEntry && entryId) {
                    storyBibleStore.addPendingNewWorldbuilding(entryId);
                  }

                  // Set diff for highlighting
                  if (entryId && Object.keys(newFields).length > 0) {
                    storyBibleStore.setWorldbuildingDiff(entryId, oldFields, newFields);
                  }
                  if (entryId && updatedFields.includes("_deleted")) {
                    storyBibleStore.clearWorldbuildingDiff(entryId);
                    storyBibleStore.removePendingNewWorldbuilding(entryId);
                  }

                  const worldbuildingUpdateRefetchGeneration = startWorldbuildingUpdateRefetch();
                  // Refresh worldbuilding to get updated data (for both new and modified entries)
                  // This ensures local state has the new values so clearing diffs shows the right content
                  await storyBibleStore.refreshWorldbuildingEntries(projectId, "persistentChatStore.worldbuilding_update", { force: true });
                  if (!isLatestWorldbuildingUpdateRefetch(worldbuildingUpdateRefetchGeneration)) {
                    return;
                  }
                } catch (error) {
                  console.error("[worldbuilding_update] Failed to refresh worldbuilding:", error);
                }
              })();
              break;
            }
            case "meta_update": {
              const updatedFields = Array.isArray(chunk.updatedFields) ? chunk.updatedFields : [];
              const oldFields = chunk.oldFields && typeof chunk.oldFields === "object" ? chunk.oldFields : {};
              const newFields = chunk.newFields && typeof chunk.newFields === "object" ? chunk.newFields : {};

              set((state) => ({
                currentChatMadeChanges: true,
                metaUpdates: [
                  ...state.metaUpdates,
                  { updatedFields, oldFields, newFields },
                ],
              }));
              onStoryBibleCtaContentUpdate(get, set);

              (async () => {
                try {
                  const { useStoryBibleStore } = await import("src/stores/storyBibleStore");
                  const storyBibleStore = useStoryBibleStore.getState();

                  if (!isCurrentStoryBibleOwner(storyBibleStore, projectId)) return;
                  applyMetaUpdateFromChunk(storyBibleStore, { updatedFields, oldFields, newFields }, projectId);

                  const metaUpdateRefetchGeneration = startMetaUpdateRefetch();
                  // Refetch meta and set diffs (same pattern as outline_update + act diffs)
                  const results = await Auth.fetchWithIdToken(`/api/story-engine/${projectId}/get`, {});
                  if (!isCurrentStoryBibleOwner(storyBibleStore, projectId) || !isLatestMetaUpdateRefetch(metaUpdateRefetchGeneration)) {
                    return;
                  }
                  if (!applyStoryEngineMetaToStore(storyBibleStore, projectId, results)) {
                    reapplyMetaDiffsFromUpdates(storyBibleStore, get().metaUpdates, projectId);
                    return;
                  }
                  reapplyMetaDiffsFromUpdates(storyBibleStore, get().metaUpdates, projectId);

                  // Set diffs for updated fields (enables purple highlighting)
                  for (const fieldKey of collectMetaUpdatedFieldKeys(get().metaUpdates)) {
                    storyBibleStore.setMetaDiff(fieldKey);
                  }
                } catch (error) {
                  console.error("[meta_update] Failed to refresh meta:", error);
                }
              })();
              break;
            }
            case "plan_proposed": {
              if (chunk.plan) {
                set({ activePlan: chunk.plan });
              }
              break;
            }
            case "suggested_action": {
              const action = typeof chunk.action === "string" ? chunk.action : "";
              const chapterId = typeof chunk.chapterId === "string" ? chunk.chapterId : "";
              const chapterName = typeof chunk.chapterName === "string" ? chunk.chapterName : "";
              const message = typeof chunk.message === "string" ? chunk.message : "";
              if (action && message) {
                set({ suggestedAction: { action, chapterId, chapterName, message } });
              }
              break;
            }
            case "ask_user_question": {
              const streamingTextBeforeFlush = get().currentStreamingText;
              const bufferedTextLengthBeforeFlush = _textBuffer.length;
              // Flush RAF-buffered chunks so the drafted prose renders
              // before the follow-up insertion question UI opens.
              flushTextBufferSync(set);
              // Support new format ({ questions }) and old format ({ question, inputType, options })
              const questions = normalizeAskUserQuestionSteps(
                Array.isArray(chunk.questions)
                  ? chunk.questions
                  : typeof chunk.question === "string" && chunk.question
                    ? [{ question: chunk.question, inputType: chunk.inputType ?? "button", options: Array.isArray(chunk.options) ? chunk.options : [] }]
                    : [],
              );
              if (questions.length > 0) {
                const firstQuestion = typeof questions[0]?.question === "string" ? questions[0].question : "";
                const castCountQuestion = userQuestionIsGenerateCastCharacterCount({ questions });
                if (shouldLogInsertDraftQuestionDebug() && isInsertDraftQuestionClient(firstQuestion)) {
                  // eslint-disable-next-line no-console
                  console.info("[agentic-debug] ask_user_question received", {
                    firstQuestion,
                    streamingTextBeforeFlushLength: streamingTextBeforeFlush.length,
                    bufferedTextLengthBeforeFlush,
                    streamingTextAfterFlushLength: get().currentStreamingText.length,
                  });
                }
                const ctaOnQuestion = get().ctaAction;
                set({
                  currentUserQuestion: { questions },
                  ...(castCountQuestion && ctaOnQuestion?.type === "generate_cast"
                    ? {
                        ctaAction: { ...ctaOnQuestion, status: "awaiting_input" as const },
                        pendingAgentResponse: false,
                        responsePhase: "idle" as const,
                      }
                    : {}),
                });
                if (get().activePlan?.status === "in_progress") {
                  void (async () => {
                    try {
                      await pendingProjectUiActionsRef.current;
                    } catch (error) {
                      Sentry.captureException(error, {
                        tags: { feature: "persistent_chat_store", action: "ask_user_question_pending_ui" },
                      });
                    } finally {
                      if (get().activePlan?.status === "in_progress") {
                        get().setPlanStatus("paused");
                      }
                    }
                  })();
                }
              }
              break;
            }
            case "plan_work_progress": {
              const plan = get().activePlan;
              if (plan?.id === chunk.planId && Number.isInteger(chunk.completed) && Number.isInteger(chunk.total)
                && chunk.completed >= 0 && chunk.completed <= chunk.total) {
                set({ activePlan: { ...applyGoalPlanProgressSnapshot(plan, chunk.steps), workProgress: { completed: chunk.completed, total: chunk.total } } });
              }
              break;
            }
            case "plan_outline_progress": {
              const plan = get().activePlan;
              if (plan?.id === chunk.planId && Number.isInteger(chunk.saved) && Number.isInteger(chunk.total)
                && chunk.saved >= 0 && chunk.saved <= chunk.total) {
                set({ activePlan: { ...plan, outlineProgress: { saved: chunk.saved, total: chunk.total } } });
              }
              break;
            }
            case "plan_step_start": {
              const stepIndex = typeof chunk.stepIndex === "number" ? chunk.stepIndex : undefined;
              const stepId = normalizePlanStepId(chunk.stepId);
              const hasLocator = !!stepId || typeof stepIndex === "number";
              if (!hasLocator) break;
              const currentStatus = get().activePlan?.status;
              const optimisticPauseAwaitingApi =
                currentStatus === "paused" && get().pausePlanApiRequestInFlight;
              const cannotPromotePlan =
                currentStatus === "cancelled" || currentStatus === "completed" || currentStatus === "cancelling";
              const resumingPlan = get().planResumeRequestInFlight;
              const shouldPromoteToInProgress =
                optimisticPauseAwaitingApi
                || resumingPlan
                || (!cannotPromotePlan && currentStatus !== "paused");
              if (shouldPromoteToInProgress) {
                if (optimisticPauseAwaitingApi) {
                  if (stepId) get().updatePlanStepById(stepId, "in_progress");
                  else get().updatePlanStep(stepIndex!, "in_progress");
                } else {
                  get().setPlanStatus("in_progress");
                  if (stepId) get().updatePlanStepById(stepId, "in_progress");
                  else get().updatePlanStep(stepIndex!, "in_progress");
                }
              }
              break;
            }
            case "plan_step_complete": {
              const plan = get().activePlan;
              if (!plan) break;
              const stepIndex = typeof chunk.stepIndex === "number" ? chunk.stepIndex : undefined;
              const stepId = normalizePlanStepId(chunk.stepId);
              const resolved = resolvePlanStreamStepIndex(plan, stepIndex, chunk.stepId);
              if (!shouldApplyPlanStepCompleteEvent(plan, resolved, chunk)) break;
              if (stepId) get().updatePlanStepById(stepId, "completed");
              else get().updatePlanStep(resolved, "completed");
              break;
            }
            case "plan_completed": {
              get().completePlan(chunk.planId);
              break;
            }
            case "plan_paused": {
              flushTextBufferSync(set);
              set((state) => {
                const plan = state.activePlan;
                if (!plan) {
                  return { streaming: false, currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {}, currentStreamingText: "", planResumeRequestInFlight: false };
                }
                return {
                  streaming: false,
                  currentStreamingText: "",
                  currentProsePreview: "",
                  currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {},
                  planResumeRequestInFlight: false,
                  activePlan: normalizePlanIfAllStepsDone(applyPlanPausedChunk(plan, chunk)),
                };
              });
              break;
            }
            case "plan_cancelled": {
              get().setPlanStatus("cancelled");
              break;
            }
            case "commenting_started": {
              const commentingId = typeof chunk.commentingId === "string" ? chunk.commentingId : `commenting-${Date.now()}`;
              set((state) => ({
                activeCommenting: {
                  id: commentingId,
                  status: "in_progress",
                  commentsCreated: 0,
                  feedbackTypes: Array.isArray(chunk.feedbackTypes) ? chunk.feedbackTypes : [],
                  customFeedbackInstructions: Array.isArray(chunk.customFeedbackInstructions) ? chunk.customFeedbackInstructions : [],
                },
                commentingProgress: mergeCommentingProgress(
                  state.commentingProgress,
                  COMMENTING_PROGRESS.started,
                ),
              }));
              break;
            }
            case "comment_created": {
              const totalCreated = typeof chunk.totalCreated === "number" ? chunk.totalCreated : 0;
              const feedbackTypes = Array.isArray(chunk.feedbackTypes) ? chunk.feedbackTypes : [];
              set((state) => ({
                activeCommenting: state.activeCommenting
                  ? { ...state.activeCommenting, commentsCreated: totalCreated, feedbackTypes }
                  : null,
              }));
              onCommentingCommentCreated(get, set, totalCreated);
              break;
            }
            case "commenting_complete": {
              onCommentingFinished(get, set);
              set((state) => ({
                activeCommenting:
                  state.activeCommenting && state.activeCommenting.status !== "cancelled"
                    ? { ...state.activeCommenting, status: "completed" as const }
                    : state.activeCommenting,
                ...COMMENTING_PROGRESS_RESET,
              }));
              break;
            }
            case "commenting_cancelled": {
              set((state) => ({
                activeCommenting: state.activeCommenting
                  ? { ...state.activeCommenting, status: "cancelled" as const }
                  : null,
                ...COMMENTING_PROGRESS_RESET,
              }));
              break;
            }
            case "write_prose_context": {
              const writePovTenseChiclets = parseWritePovTenseChiclets(
                (chunk as { writePovTenseChiclets?: unknown }).writePovTenseChiclets,
              );
              if (writePovTenseChiclets?.length) {
                set({ currentWritePovTenseChiclets: writePovTenseChiclets });
              }
              break;
            }
            case "story_bible_entity_generated": {
              const entity = parseStoryBibleGeneratedEntity(chunk);
              if (entity) {
                onStoryBibleCtaEntityGenerated(get, set);
                set({ streamingStoryBibleGeneratedEntity: entity });
                void (async () => {
                  try {
                    const { clearStoryBibleDiffsForGeneratedEntity } = await import(
                      "src/utils/clearStoryBibleDiffsForGeneratedEntity"
                    );
                    await clearStoryBibleDiffsForGeneratedEntity(entity, { projectId });
                  } catch {
                    // ignore
                  }
                })();
              }
              break;
            }
            case "tool_progress": {
              const callId = typeof chunk.callId === "string" ? chunk.callId : "";
              const message = typeof chunk.message === "string" ? chunk.message : "";
              if (message) {
                get().updateToolTimelineProgress(callId || undefined, message);
              }
              break;
            }
            case "prose_preview": {
              set(state => applyProsePreviewChunk(state.currentProsePreviews, chunk, state.currentProsePreviewExplanations, state.currentProsePreviewGroups));
              break;
            }
            case "prose_chunk":
            case "text_chunk": {
              const text = typeof chunk.text === "string" ? chunk.text : "";
              if (shouldLogInsertDraftQuestionDebug() && text.trim().length > 0) {
                const preview = text.trim().slice(0, 120);
                // eslint-disable-next-line no-console
                console.info("[agentic-debug] text_chunk received", {
                  chunkLength: text.length,
                  preview,
                  bufferedTextLengthBeforeAppend: _textBuffer.length,
                  currentStreamingTextLengthBeforeAppend: get().currentStreamingText.length,
                });
              }
              if (bufferArtifactTextIfNeeded(text)) {
                break;
              }
              if (chunk.type === "prose_chunk") {
                handleStreamedProseChunk(text, get, set);
              } else {
                appendStreamingText(text, set);
              }
              break;
            }
            case "text_clear": {
              cancelTextBufferFlush();
              if (_rewriteArtifactStream) {
                _rewriteArtifactStream.bufferedText = "";
              }
              set({ currentStreamingText: "" });
              break;
            }
            case "reasoning_chunk": {
              const text = typeof chunk.text === "string" ? chunk.text : "";
              get().appendReasoningTask(text);
              break;
            }
            case "text": {
              cancelTextBufferFlush();
              const text = typeof chunk.text === "string" ? chunk.text : "";
              get().clearThinkingPlaceholder();
              if (_rewriteArtifactStream) {
                if (!_rewriteArtifactStream.suppressUntilStreamEnd) {
                  _rewriteArtifactStream.bufferedText = text;
                }
                break;
              }
              set({ currentStreamingText: text });
              break;
            }
            case "complete": {
              if (isStalePersistentChatStream(get, streamSignal)) {
                break;
              }
              if (typeof chunk.assistantMessageId === "string" && get().currentRetryRequestId) {
                set({ currentRetryAssistantMessageId: chunk.assistantMessageId });
              }
              flushTextBufferSync(set);
              resetRewriteArtifactStream();
              get().clearThinkingPlaceholder();
              get().finalizeReasoningTask();
              get().completeRunningThinkingTasks();
              // When SSE ends with zero emitted `text_chunk` events plus an empty streamed buffer, downstream React
              // falls back on persisted chats—often showing "nothing landed." Most cases are guarded server-side via
              // conversationRunner finalize fallbacks, but regressions involving duplicate-suppression shortcuts or aborted
              // streams should be debugged jointly across API + PersistentChatSSE rather than blaming the executor alone.
              useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_OUTLINE_RUNNING, false);
              useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CHARACTER_RUNNING, false);
              useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_WORLDBUILDING_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CAST_RUNNING, false);
              const ctaOnComplete = get().ctaAction;
              if (ctaOnComplete?.signalKey) {
                useCrossAppSignalStore.getState().setSignalActive(ctaOnComplete.signalKey, false);
              }
              if (ctaOnComplete) {
                const castHasOpenQuestion =
                  ctaOnComplete.type === "generate_cast"
                  && Boolean(get().currentUserQuestion);
                const castAwaitingCastSizeQuestionDelivery =
                  ctaOnComplete.type === "generate_cast"
                  && ctaOnComplete.status === "awaiting_input"
                  && !get().currentUserQuestion;
                const keepCastAwaitingInput =
                  castHasOpenQuestion || castAwaitingCastSizeQuestionDelivery;
                set({
                  ctaAction: {
                    ...ctaOnComplete,
                    status: keepCastAwaitingInput ? "awaiting_input" : "completed",
                  },
                  ...(isStoryBibleCtaType(ctaOnComplete.type) && !keepCastAwaitingInput
                    ? STORY_BIBLE_CTA_PROGRESS_RESET
                    : {}),
                });
              }
              set((state) => ({
                streaming: false,
                ...(!state.currentTurnUserMessageId ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}),
                completedResponseAwaitingPersistence: Boolean(state.currentTurnUserMessageId) && !state.errorText,
                activeCommenting:
                  state.activeCommenting?.status === "in_progress" && state.activeCommenting.id.startsWith("feedback-run-")
                    ? state.activeCommenting
                    : null,
              }));
              get().setToolRuns((prev) => prev.map((tool) => (tool.status === "running" ? { ...tool, status: "complete" } : tool)));
              queryClient.invalidateQueries(chatQueryKey(projectId, resolvedChatId));
              queryClient.invalidateQueries(["chats", projectId]);
              queryClient.refetchQueries(chatQueryKey(projectId, resolvedChatId));
              // For CTA flows, the canned assistant reply already exists as local streaming text
              // (e.g. "I've generated the synopsis."). There's no need to wait for the persisted
              // assistant via `clearStreamingTextIfServerHasData` — clear pending immediately so
              // the StackedIcons "Working..." indicator switches to the completed state.
              if (ctaOnComplete) {
                if (pendingAgentResponseClearTimeout) {
                  clearTimeout(pendingAgentResponseClearTimeout);
                  pendingAgentResponseClearTimeout = null;
                }
                const deferPendingClearForCastSizeQuestion =
                  ctaOnComplete.type === "generate_cast"
                  && ctaOnComplete.status === "awaiting_input"
                  && !get().currentUserQuestion;
                if (!deferPendingClearForCastSizeQuestion) {
                  set({ pendingAgentResponse: false, responsePhase: "idle" });
                }
              } else {
                // Safety fallback: clear pendingAgentResponse after refetch has had time to land
                if (pendingAgentResponseClearTimeout) clearTimeout(pendingAgentResponseClearTimeout);
                pendingAgentResponseClearTimeout = setTimeout(() => {
                  pendingAgentResponseClearTimeout = null;
                  set({ pendingAgentResponse: false, responsePhase: "idle" });
                }, 2000);
              }

              if (isNewChat && resolvedChatId && resolvedChatId !== NEW_CHAT_ID) {
                (async () => {
                  try {
                    const chatData = queryClient.getQueryData(chatQueryKey(projectId, resolvedChatId)) as PersistentChatWithMessages | undefined;
                    const branchRaw = chatData?.messages || [];
                    const leaf = chatData?.active_leaf_message_id ?? null;
                    const messages = linearPathToLeaf(branchRaw as any, leaf);
                    if (messages.length > 0) {
                      const title = await generateChatTitle(messages as any, projectId);
                      if (title) {
                        await updateChatTitle(resolvedChatId, title);
                        queryClient.invalidateQueries(["chats", projectId]);
                        queryClient.invalidateQueries(chatQueryKey(projectId, resolvedChatId));
                      }
                    }
                  } catch (error) {
                    Sentry.captureException(error);
                  }
                })();
              }
              break;
            }
            case "error": {
              if (isStalePersistentChatStream(get, streamSignal)) {
                break;
              }
              flushTextBufferSync(set);
              get().finalizeReasoningTask();
              get().completeRunningThinkingTasks();
              useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_OUTLINE_RUNNING, false);
              useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CHARACTER_RUNNING, false);
              useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_WORLDBUILDING_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CAST_RUNNING, false);
              const ctaOnError = get().ctaAction;
              if (ctaOnError?.signalKey) {
                useCrossAppSignalStore.getState().setSignalActive(ctaOnError.signalKey, false);
              }
              const message = normalizePersistentChatErrorMessage(chunk.message);
              if (isReplacement) set({ failedFollowUp: { params, message, chatUnavailable: chunk.code === "CHAT_UNAVAILABLE" } });
              set({
                streaming: false,
                currentStreamingText: "",
                currentProsePreview: "",
                currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {},
                currentTurnUserMessageId: null,
                completedResponseAwaitingPersistence: false,
                currentTurnPassage: null,
                currentTurnReferencedComments: [],
                optimisticUserMessageId: null,
                errorText: message,
                pendingAgentResponse: false,
                responsePhase: "idle",
                streamingStoryBibleGeneratedEntity: null,
                ...(ctaOnError
                  ? {
                      ctaAction: { ...ctaOnError, status: "cancelled" as const },
                      ...(isStoryBibleCtaType(ctaOnError.type) ? STORY_BIBLE_CTA_PROGRESS_RESET : {}),
                    }
                  : {}),
              });
              get().setToolRuns((prev) => prev.map((tool) => (tool.status === "running" ? { ...tool, status: "error", summary: message } : tool)));
              queryClient.invalidateQueries(chatQueryKey(projectId, resolvedChatId));
              queryClient.invalidateQueries(["chats", projectId]);
              onStreamError?.(message);
              break;
            }
            default:
              break;
          }
          return;
        }

        if (chunk.documentTitle) {
          get().setSearchedDocs((prevDocs) => {
            if (!prevDocs.includes(chunk.documentTitle)) {
              return [chunk.documentTitle, ...prevDocs];
            }
            return prevDocs;
          });
        } else if (chunk.type === "context-cutoff") {
          set({ cutoffData: chunk.data });
        } else if (chunk.type) {
          get().setLookingAtData((prevData) => [...prevData, chunk.type]);
        } else if (chunk.text) {
          appendStreamingText(chunk.text, set);
        }
      };

    const deliverStreamDone = () => {
        if (isStalePersistentChatStream(get, streamSignal)) {
          return;
        }
        set({ activeRequest: null });
        flushTextBufferSync(set);
        resetRewriteArtifactStream();
        get().finalizeReasoningTask();
        get().completeRunningThinkingTasks();
        get().setToolRuns((prev) => prev.map((tool) => (tool.status === "running" ? { ...tool, status: "complete" } : tool)));
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_OUTLINE_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CHARACTER_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_WORLDBUILDING_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CAST_RUNNING, false);
        const ctaOnDone = get().ctaAction;
        if (ctaOnDone?.signalKey) {
          useCrossAppSignalStore.getState().setSignalActive(ctaOnDone.signalKey, false);
        }
        if (ctaOnDone && ctaOnDone.status === "in_progress") {
          set({ ctaAction: { ...ctaOnDone, status: "completed" } });
        }
        const deferPendingClearForCastSizeQuestion =
          ctaOnDone?.type === "generate_cast"
          && ctaOnDone.status === "awaiting_input"
          && !get().currentUserQuestion;
        // Transport completion ends work even if the persisted reply has not refetched.
        // Keep its local text and turn id for reconciliation, independently of the indicator.
        if (pendingAgentResponseClearTimeout) {
          clearTimeout(pendingAgentResponseClearTimeout);
          pendingAgentResponseClearTimeout = null;
        }
        set({
          streaming: false,
          ...(!get().currentTurnUserMessageId ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}),
          completedResponseAwaitingPersistence: Boolean(get().currentTurnUserMessageId) && !get().errorText && !streamSignal.aborted,
          responsePhase: "idle",
          ...(deferPendingClearForCastSizeQuestion
            ? {}
            : { pendingAgentResponse: false }),
        });
        queryClient.invalidateQueries(chatQueryKey(projectId, resolvedChatId));
        queryClient.invalidateQueries(["chats", projectId]);
        void queryClient.refetchQueries(chatQueryKey(projectId, resolvedChatId));
      };

    const deliverStreamError = (chunk: any) => {
        if (isStalePersistentChatStream(get, streamSignal)) {
          return;
        }
        const aborted = streamSignal.aborted || isAbortLikePersistentChatStreamError(chunk);
        if (isReplacement && !aborted) set({ failedFollowUp: { params,
          message: chunk?.message || "Could not confirm your edit. Your text is saved; please retry.",
          chatUnavailable: chunk?.code === "CHAT_UNAVAILABLE" } });
        // A failed transport does not prove the server stopped. Keep its request
        // handle and fence retries until cancellation settles.
        // abortStream flushes partial text and never rejects.
        void get().abortStream("stream_error");
        resetRewriteArtifactStream();
        get().finalizeReasoningTask();
        get().completeRunningThinkingTasks();
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_OUTLINE_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CHARACTER_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_WORLDBUILDING_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CAST_RUNNING, false);
        const ctaOnError = get().ctaAction;
        if (ctaOnError?.signalKey) {
          useCrossAppSignalStore.getState().setSignalActive(ctaOnError.signalKey, false);
        }
        if (ctaOnError && ctaOnError.status === "in_progress") {
          set({ ctaAction: { ...ctaOnError, status: "cancelled" } });
        }
        set({
          streaming: false,
          currentProsePreview: "",
          currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {},
          completedResponseAwaitingPersistence: false,
          currentTurnPassage: null,
          currentTurnReferencedComments: [],
          optimisticUserMessageId: null,
          pendingAgentResponse: false,
          responsePhase: "idle",
        });

        if (aborted) {
          get().setToolRuns((prev) => prev.map((tool) => (tool.status === "running" ? { ...tool, status: "complete", summary: "Stopped" } : tool)));
          queryClient.invalidateQueries(chatQueryKey(projectId, resolvedChatId));
          queryClient.invalidateQueries(["chats", projectId]);
          return;
        }

        if (chunk?.error && chunk?.message) {
          const errorMessage = normalizePersistentChatErrorMessage(chunk.message);
          set({ errorText: errorMessage });
          get().setToolRuns((prev) => prev.map((tool) => (tool.status === "running" ? { ...tool, status: "error", summary: errorMessage } : tool)));
        }

        // UI state and callbacks still receive expected stream errors; this only
        // keeps Sentry focused on unexpected client-side failures.
        if (shouldCapturePersistentChatStreamError(chunk)) {
          Sentry.captureException(chunk);
        }
        queryClient.invalidateQueries(chatQueryKey(projectId, resolvedChatId));
        queryClient.invalidateQueries(["chats", projectId]);
        const errMsg = normalizePersistentChatErrorMessage(chunk?.message);
        onStreamError?.(errMsg);
      };

    const deliverStreamStart = () => {
        if (isStalePersistentChatStream(get, streamSignal)) return;
        if (get().pendingAgentResponse) {
          set({ responsePhase: "connecting" });
        }
      };

    await sendViaAgenticVercelChat({
      url: messageEndpoint,
      body: payload,
      signal: streamSignal as any,
      onChunk: deliverStreamChunk,
      onDone: deliverStreamDone,
      onError: deliverStreamError,
      onStart: deliverStreamStart,
    });
  },

  clearCurrentUserQuestion: () => set({ currentUserQuestion: null }),
  setCtaAction: (action) => {
    // Bail on content-identical re-sets (before set(), so subscribers aren't notified):
    // publishing a new reference for an unchanged CTA lets render-coupled callers
    // (e.g. instant-fire effects) loop forever (SW-9615).
    if (ctaActionsEquivalent(get().ctaAction, action)) return;
    set({
      ctaAction: action,
      ...storyBibleCtaProgressFieldsForSetCtaAction(action),
    });
  },
  updateCtaActionStatus: (status) => {
    const current = get().ctaAction;
    if (!current || current.status === status) return;
    set({ ctaAction: { ...current, status } });
  },

  setPlan: (plan) => set({ activePlan: plan }),
  cancelCommenting: () =>
    set((state) => ({
      activeCommenting: state.activeCommenting
        ? { ...state.activeCommenting, status: "cancelled" as const }
        : null,
      ...COMMENTING_PROGRESS_RESET,
    })),

  updatePlanStep: (stepIndex, status) =>
    set((state) => {
      if (!state.activePlan) return {};
      if (
        state.activePlan.status === "paused"
        && status === "in_progress"
        && !state.pausePlanApiRequestInFlight
        && !state.planResumeRequestInFlight
      ) {
        return {};
      }
      const steps = state.activePlan.steps.map((s, i) => (i === stepIndex ? { ...s, status } : s));
      const nextIndex = status === "completed"
        ? steps[stepIndex]?.goalId
          ? resolvePlanCurrentStepIndex({ ...state.activePlan, steps })
          : stepIndex + 1
        : state.activePlan.currentStepIndex;
      const maxIndex = Math.max(steps.length - 1, 0);
      return {
        activePlan: {
          ...state.activePlan,
          steps,
          currentStepIndex: steps.length > 0 ? Math.min(nextIndex, maxIndex) : 0,
        },
      };
    }),

  updatePlanStepById: (stepId, status) =>
    set((state) => {
      if (!state.activePlan) return {};
      const targetStepId = String(stepId).trim();
      if (!targetStepId) return {};
      const stepIndex = state.activePlan.steps.findIndex((s) => String(s.id).trim() === targetStepId);
      if (stepIndex === -1) return {};
      if (
        state.activePlan.status === "paused"
        && status === "in_progress"
        && !state.pausePlanApiRequestInFlight
        && !state.planResumeRequestInFlight
      ) {
        return {};
      }
      const steps = state.activePlan.steps.map((s, i) => (i === stepIndex ? { ...s, status } : s));
      const nextIndex = status === "completed"
        ? steps[stepIndex]?.goalId
          ? resolvePlanCurrentStepIndex({ ...state.activePlan, steps })
          : stepIndex + 1
        : state.activePlan.currentStepIndex;
      const maxIndex = Math.max(steps.length - 1, 0);
      return {
        activePlan: {
          ...state.activePlan,
          steps,
          currentStepIndex: steps.length > 0 ? Math.min(nextIndex, maxIndex) : 0,
        },
      };
    }),

  setPlanStatus: (status) => set((state) => {
    if (!state.activePlan) return {};
    return { activePlan: { ...state.activePlan, status } };
  }),

  completePlan: (planId) => set((state) => {
    const plan = state.activePlan;
    if (!plan || (planId && planId !== plan.id)) return {};
    // Keep this separate from streamed prose so the detailed response replaces it.
    const completedTitles = plan.steps
      .filter((step) => step.status === "completed")
      .map((step) => step.title.replace(/[\\`*_{}\[\]<>!]/g, "\\$&").replace(/[\r\n]+/g, " "));
    return {
      activePlan: { ...plan, status: "completed" },
      completedResponseAwaitingPersistence: true,
      completedPlanSummaryId: plan.id,
      completedPlanSummary: completedTitles.length
        ? `All done. Completed:\n\n${completedTitles.map((title) => `- ${title}`).join("\n")}`
        : "All done. Your plan is complete.",
    };
  }),

  removePlanStep: (stepId) =>
    set((state) => {
      if (!state.activePlan) return {};
      const prevSteps = state.activePlan.steps;
      const removeIndex = prevSteps.findIndex((s) => s.id === stepId);
      if (removeIndex === -1) return {};
      const steps = prevSteps.filter((s) => s.id !== stepId);
      let currentStepIndex = state.activePlan.currentStepIndex;
      if (removeIndex < currentStepIndex) {
        currentStepIndex = Math.max(0, currentStepIndex - 1);
      } else if (removeIndex === currentStepIndex && currentStepIndex >= steps.length) {
        currentStepIndex = Math.max(0, steps.length - 1);
      }
      const maxIndex = Math.max(steps.length - 1, 0);
      return {
        activePlan: {
          ...state.activePlan,
          steps,
          currentStepIndex: steps.length > 0 ? Math.min(currentStepIndex, maxIndex) : 0,
        },
      };
    }),

  approvePlan: async ({ chatId, projectId, planId, removedStepIds, modelKey, mode, setChatId }) => {
    const store = get();
    store.resetForNewMessage();
    const streamSignal = store.abortController.current.signal as any;
    const agenticWriteButtonSettings = buildAgenticWriteButtonSettingsFromEditor();

    const plan = store.activePlan;
    if (plan) {
      const idsToRemove = new Set((removedStepIds ?? []).map((id) => String(id)));
      const filteredSteps = plan.steps.filter((s) => !idsToRemove.has(String(s.id)));
      set({ activePlan: { ...plan, status: "in_progress", steps: filteredSteps } });
    }

    let resolvedChatId = chatId;
    const planStreamUiChain: PlanStreamUiChainRef = { current: Promise.resolve() };

    const approveBody: PlanApproveRequestBody = {
      chatId,
      projectId,
      planId,
      ...(removedStepIds?.length ? { removedStepIds } : {}),
      ...(modelKey ? { model: modelKey } : {}),
      ...(mode ? { mode } : {}),
      ...(agenticWriteButtonSettings ? { agenticWriteButtonSettings } : {}),
    };

    await sendViaAgenticVercelChat({
      url: "/api/ai/agenticChat/plan/approve",
      body: approveBody,
      signal: streamSignal,
      onChunk: (chunk: any) => {
        if (isStalePersistentChatStream(get, streamSignal)) return;
        if (!chunk?.event) return;
        switch (chunk.event) {
          case "chat_acknowledged": {
            const updatedChatId = typeof chunk.chatId === "string" ? chunk.chatId : null;
            if (updatedChatId && updatedChatId !== chatId) {
              resolvedChatId = updatedChatId;
              if (chatId === NEW_CHAT_ID) {
                set({ pendingNewChatMaterializationId: updatedChatId });
              }
              setChatId(updatedChatId);
            }
            if (chunk.model) set({ currentModel: chunk.model });
            break;
          }
          case "complete": {
            if (isStalePersistentChatStream(get, streamSignal)) break;
            flushTextBufferSync(set);
            get().finalizeReasoningTask();
            get().completeRunningThinkingTasks();
            useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_OUTLINE_RUNNING, false);
            useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CHARACTER_RUNNING, false);
            useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_WORLDBUILDING_RUNNING, false);
            useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CAST_RUNNING, false);
            set((state) => {
              const p = state.activePlan;
              const nextPlan = activePlanAfterApprovePlanStreamEnd(p);
              if (!p) {
                return { streaming: false, ...(!state.completedResponseAwaitingPersistence ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}), pendingAgentResponse: false, responsePhase: "idle", activePlan: null, activeCommenting: null };
              }
              const normalized = normalizePlanIfAllStepsDone(p);
              if (normalized && normalized.status === "completed") {
                return { streaming: false, ...(!state.completedResponseAwaitingPersistence ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}), pendingAgentResponse: false, responsePhase: "idle", activePlan: normalized, activeCommenting: null };
              }
              if (nextPlan && (nextPlan.status === "paused" || nextPlan.status === "cancelled")) {
                return { streaming: false, ...(!state.completedResponseAwaitingPersistence ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}), pendingAgentResponse: false, responsePhase: "idle", activePlan: nextPlan, activeCommenting: null };
              }
              return { streaming: false, ...(!state.completedResponseAwaitingPersistence ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}), pendingAgentResponse: false, responsePhase: "idle", activePlan: null, activeCommenting: null };
            });
            queryClient.invalidateQueries(chatQueryKey(projectId, resolvedChatId));
            queryClient.invalidateQueries(["chats", projectId]);
            queryClient.refetchQueries(chatQueryKey(projectId, resolvedChatId));
            break;
          }
          case "error": {
            if (isStalePersistentChatStream(get, streamSignal)) break;
            set({ completedResponseAwaitingPersistence: false });
            flushTextBufferSync(set);
            get().finalizeReasoningTask();
            get().completeRunningThinkingTasks();
            useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_OUTLINE_RUNNING, false);
            useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CHARACTER_RUNNING, false);
            useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_WORLDBUILDING_RUNNING, false);
            useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CAST_RUNNING, false);
            const message = typeof chunk.message === "string" ? chunk.message : "Something went wrong.";
            set((state) => {
              const p = state.activePlan;
              const nextPlan = activePlanAfterApprovePlanStreamEnd(p);
              if (!p) {
                return { streaming: false, currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {}, errorText: message, pendingAgentResponse: false, responsePhase: "idle", activePlan: null, activeCommenting: null };
              }
              const normalized = normalizePlanIfAllStepsDone(p);
              if (normalized && normalized.status === "completed") {
                return { streaming: false, currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {}, errorText: message, pendingAgentResponse: false, responsePhase: "idle", activePlan: normalized, activeCommenting: null };
              }
              if (nextPlan && (nextPlan.status === "paused" || nextPlan.status === "cancelled")) {
                return { streaming: false, currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {}, errorText: message, pendingAgentResponse: false, responsePhase: "idle", activePlan: nextPlan, activeCommenting: null };
              }
              return { streaming: false, currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {}, errorText: message, pendingAgentResponse: false, responsePhase: "idle", activePlan: null, activeCommenting: null };
            });
            queryClient.invalidateQueries(chatQueryKey(projectId, resolvedChatId));
            break;
          }
          default:
            dispatchPlanChunkEvent(chunk, get, set, projectId, planStreamUiChain);
            break;
        }
      },
      onDone: () => {
        if (isStalePersistentChatStream(get, streamSignal)) return;
        flushTextBufferSync(set);
        get().finalizeReasoningTask();
        get().completeRunningThinkingTasks();
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_OUTLINE_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CHARACTER_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_WORLDBUILDING_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CAST_RUNNING, false);
        set((state) => {
          const p = state.activePlan;
          const nextPlan = activePlanAfterApprovePlanStreamEnd(p);
          if (!p) {
            return { streaming: false, ...(!state.completedResponseAwaitingPersistence ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}), pendingAgentResponse: false, responsePhase: "idle", activePlan: null, activeCommenting: null };
          }
          const normalized = normalizePlanIfAllStepsDone(p);
          if (normalized && normalized.status === "completed") {
            return { streaming: false, ...(!state.completedResponseAwaitingPersistence ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}), pendingAgentResponse: false, responsePhase: "idle", activePlan: normalized, activeCommenting: null };
          }
          if (nextPlan && (nextPlan.status === "paused" || nextPlan.status === "cancelled")) {
            return { streaming: false, ...(!state.completedResponseAwaitingPersistence ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}), pendingAgentResponse: false, responsePhase: "idle", activePlan: nextPlan, activeCommenting: null };
          }
          return { streaming: false, ...(!state.completedResponseAwaitingPersistence ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}), pendingAgentResponse: false, responsePhase: "idle", activePlan: null, activeCommenting: null };
        });
        queryClient.invalidateQueries(chatQueryKey(projectId, resolvedChatId));
        queryClient.invalidateQueries(["chats", projectId]);
      },
      onError: (chunk: any) => {
        if (isStalePersistentChatStream(get, streamSignal)) return;
        set({ completedResponseAwaitingPersistence: false });
        cancelTextBufferFlush();
        get().finalizeReasoningTask();
        get().completeRunningThinkingTasks();
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_OUTLINE_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CHARACTER_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_WORLDBUILDING_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CAST_RUNNING, false);
        const aborted = streamSignal.aborted || isAbortLikePersistentChatStreamError(chunk);
        const message = typeof chunk?.message === "string" ? chunk.message : "Something went wrong. Please try again.";
        get().setToolRuns((prev) => prev.map((tool) => (
          tool.status === "running"
            ? { ...tool, status: aborted ? "complete" : "error", summary: aborted ? "Stopped" : message }
            : tool
        )));
        set((state) => {
          const commentingUpdate = aborted && state.activeCommenting?.status === "in_progress"
            ? { activeCommenting: { ...state.activeCommenting, status: "cancelled" as const } }
            : {};
          const base = {
            streaming: false,
            currentProsePreview: "",
            currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {},
            ...(aborted ? {} : { currentStreamingText: "" }),
            pendingAgentResponse: false,
            responsePhase: "idle" as const,
            ...(aborted ? {} : { errorText: message }),
            ...commentingUpdate,
          };
          const p = state.activePlan;
          if (!p) {
            return { ...base, activePlan: null };
          }
          const normalized = normalizePlanIfAllStepsDone(p);
          if (normalized && normalized.status === "completed") {
            return { ...base, activePlan: normalized };
          }
          if (p.status === "paused" || p.status === "cancelled") {
            return { ...base, activePlan: p };
          }
          return { ...base, activePlan: aborted ? { ...p, status: "cancelled" as const } : null };
        });
        queryClient.invalidateQueries(chatQueryKey(projectId, resolvedChatId));
        queryClient.invalidateQueries(["chats", projectId]);
        // Plan stream failures use normal error UI above; expected stream chunks
        // should not become generic Sentry `onError` groups.
        if (!aborted && shouldCapturePersistentChatStreamError(chunk)) {
          Sentry.captureException(chunk);
        }
      },
    });
  },

  cancelPlan: async ({ chatId, projectId, planId }) => {
    try {
      get().setPlanStatus("cancelling");
      await Auth.fetchWithIdToken("/api/ai/agenticChat/plan/cancel", { chatId, projectId, planId });
      set({ activePlan: null });
      queryClient.invalidateQueries(chatQueryKey(projectId, chatId));
    } catch (error) {
      console.error("Failed to cancel plan:", error);
      set({ activePlan: null });
    }
  },

  pausePlan: async ({ chatId, projectId, planId }) => {
    const previousStatus = get().activePlan?.status;
    const shouldOptimisticallyPause = previousStatus === "in_progress";
    if (shouldOptimisticallyPause) {
      set({ pausePlanApiRequestInFlight: true, planResumeRequestInFlight: false });
      get().setPlanStatus("paused");
    } else {
      set({ planResumeRequestInFlight: false });
    }
    // Drop the approve/resume SSE so the server can pause and the client is not stuck streaming.
    if (get().streaming || get().pendingAgentResponse) {
      abortAgentStreamPreservingPlan(set, get, { preservePauseInFlight: shouldOptimisticallyPause });
      if (shouldOptimisticallyPause) {
        const current = get().activePlan;
        if (current && current.id === planId && current.status !== "paused") {
          set({ activePlan: { ...current, status: "paused" }, pausePlanApiRequestInFlight: true });
        }
      }
    }
    try {
      await Auth.fetchWithIdToken("/api/ai/agenticChat/plan/pause", { chatId, projectId, planId });
    } catch (error) {
      if (shouldOptimisticallyPause) {
        const current = get().activePlan;
        if (current && current.id === planId && current.status === "paused" && previousStatus === "in_progress") {
          get().setPlanStatus(previousStatus);
        }
      }
      console.error("Failed to pause plan:", error);
      throw error;
    } finally {
      if (shouldOptimisticallyPause) {
        set({ pausePlanApiRequestInFlight: false });
      }
    }
  },

  resumePlan: async ({ chatId, projectId, planId, modelKey, mode, setChatId }) => {
    const planSnapshot = get().activePlan;
    if (get().streaming || get().pendingAgentResponse) {
      abortAgentStreamPreservingPlan(set, get);
    } else {
      setStreamAbortSource(get().abortController.current.signal, "replacement");
      get().abortController.current.abort();
      set({ abortController: { current: new AbortController() } });
    }
    set({
      planResumeRequestInFlight: true,
      ...(planSnapshot ? { activePlan: { ...planSnapshot, status: "in_progress" } } : {}),
    });

    let streamSignal: AbortSignal | undefined;
    try {
      get().resetForNewMessage();
      const activeStreamSignal = get().abortController.current.signal as AbortSignal;
      streamSignal = activeStreamSignal;
      const agenticWriteButtonSettings = buildAgenticWriteButtonSettingsFromEditor();

      let resolvedChatId = chatId;
      const planStreamUiChain: PlanStreamUiChainRef = { current: Promise.resolve() };

      const resumeBody: PlanResumeRequestBody = {
        chatId,
        projectId,
        planId,
        ...(modelKey ? { model: modelKey } : {}),
        ...(mode ? { mode } : {}),
        ...(agenticWriteButtonSettings ? { agenticWriteButtonSettings } : {}),
      };

      await sendViaAgenticVercelChat({
      url: "/api/ai/agenticChat/plan/resume",
      body: resumeBody,
      signal: activeStreamSignal,
      onChunk: (chunk: any) => {
        if (isStalePersistentChatStream(get, activeStreamSignal)) return;
        if (!chunk?.event) return;
        switch (chunk.event) {
          case "chat_acknowledged": {
            const updatedChatId = typeof chunk.chatId === "string" ? chunk.chatId : null;
            if (updatedChatId && updatedChatId !== chatId) {
              resolvedChatId = updatedChatId;
              if (chatId === NEW_CHAT_ID) {
                set({ pendingNewChatMaterializationId: updatedChatId });
              }
              setChatId(updatedChatId);
            }
            if (chunk.model) set({ currentModel: chunk.model });
            break;
          }
          case "complete": {
            if (isStalePersistentChatStream(get, activeStreamSignal)) break;
            flushTextBufferSync(set);
            get().finalizeReasoningTask();
            get().completeRunningThinkingTasks();
            useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_OUTLINE_RUNNING, false);
            useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CHARACTER_RUNNING, false);
            useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_WORLDBUILDING_RUNNING, false);
            useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CAST_RUNNING, false);
            set((state) => {
              const p = state.activePlan;
              if (!p) {
                return { streaming: false, ...(!state.completedResponseAwaitingPersistence ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}), pendingAgentResponse: false, responsePhase: "idle", activePlan: null, activeCommenting: null };
              }
              if (p.status === "in_progress") {
                // Do not force-pause here: stream closure can race with metadata refetch during resume.
                // Keep local in_progress and let persisted server plan state reconcile authoritatively.
                return { streaming: false, ...(!state.completedResponseAwaitingPersistence ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}), pendingAgentResponse: false, responsePhase: "idle", activePlan: normalizePlanIfAllStepsDone(p), activeCommenting: null };
              }
              if (p.status === "completed" || p.status === "paused" || p.status === "cancelled" || p.status === "cancelling") {
                return { streaming: false, ...(!state.completedResponseAwaitingPersistence ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}), pendingAgentResponse: false, responsePhase: "idle", activePlan: normalizePlanIfAllStepsDone(p), activeCommenting: null };
              }
              return { streaming: false, ...(!state.completedResponseAwaitingPersistence ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}), pendingAgentResponse: false, responsePhase: "idle", activePlan: null, activeCommenting: null };
            });
            queryClient.invalidateQueries(chatQueryKey(projectId, resolvedChatId));
            queryClient.invalidateQueries(["chats", projectId]);
            queryClient.refetchQueries(chatQueryKey(projectId, resolvedChatId));
            break;
          }
          case "error": {
            if (isStalePersistentChatStream(get, activeStreamSignal)) break;
            set({ completedResponseAwaitingPersistence: false });
            flushTextBufferSync(set);
            get().finalizeReasoningTask();
            get().completeRunningThinkingTasks();
            useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_OUTLINE_RUNNING, false);
            useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CHARACTER_RUNNING, false);
            useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_WORLDBUILDING_RUNNING, false);
            useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CAST_RUNNING, false);
            const message = typeof chunk.message === "string" ? chunk.message : "Something went wrong.";
            set((state) => {
              const currentPlan = state.activePlan;
              if (!currentPlan) {
                return { streaming: false, currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {}, errorText: message, pendingAgentResponse: false, responsePhase: "idle", activePlan: null, activeCommenting: null };
              }
              const shouldPreservePlan = currentPlan.status === "in_progress"
                ? { ...currentPlan, status: "paused" as const }
                : currentPlan;
              const preserveOnError =
                currentPlan.status === "paused"
                || currentPlan.status === "cancelled"
                || currentPlan.status === "in_progress"
                || currentPlan.status === "completed";
              return {
                streaming: false,
                currentProsePreview: "",
                currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {},
                errorText: message,
                pendingAgentResponse: false,
                responsePhase: "idle",
                activePlan: preserveOnError ? normalizePlanIfAllStepsDone(shouldPreservePlan) : null,
                activeCommenting: null,
              };
            });
            queryClient.invalidateQueries(chatQueryKey(projectId, resolvedChatId));
            break;
          }
          default:
            dispatchPlanChunkEvent(chunk, get, set, projectId, planStreamUiChain);
            break;
        }
      },
      onDone: () => {
        if (isStalePersistentChatStream(get, activeStreamSignal)) return;
        flushTextBufferSync(set);
        get().finalizeReasoningTask();
        get().completeRunningThinkingTasks();
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_OUTLINE_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CHARACTER_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_WORLDBUILDING_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CAST_RUNNING, false);
        set((state) => {
          const p = state.activePlan;
          if (!p) {
            return { streaming: false, ...(!state.completedResponseAwaitingPersistence ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}), pendingAgentResponse: false, responsePhase: "idle", activePlan: null, activeCommenting: null };
          }
          if (p.status === "in_progress") {
            // Avoid reverting resumed plans to paused purely because the stream closed before
            // the latest assistant metadata was merged.
            return { streaming: false, ...(!state.completedResponseAwaitingPersistence ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}), pendingAgentResponse: false, responsePhase: "idle", activePlan: normalizePlanIfAllStepsDone(p), activeCommenting: null };
          }
          if (p.status === "completed" || p.status === "paused" || p.status === "cancelled" || p.status === "cancelling") {
            return { streaming: false, ...(!state.completedResponseAwaitingPersistence ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}), pendingAgentResponse: false, responsePhase: "idle", activePlan: normalizePlanIfAllStepsDone(p), activeCommenting: null };
          }
          return { streaming: false, ...(!state.completedResponseAwaitingPersistence ? { currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {} } : {}), pendingAgentResponse: false, responsePhase: "idle", activePlan: null, activeCommenting: null };
        });
        queryClient.invalidateQueries(chatQueryKey(projectId, resolvedChatId));
        queryClient.invalidateQueries(["chats", projectId]);
        void queryClient.refetchQueries(chatQueryKey(projectId, resolvedChatId));
      },
      onError: (chunk: any) => {
        if (isStalePersistentChatStream(get, activeStreamSignal)) return;
        set({ completedResponseAwaitingPersistence: false });
        cancelTextBufferFlush();
        get().finalizeReasoningTask();
        get().completeRunningThinkingTasks();
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_OUTLINE_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CHARACTER_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_WORLDBUILDING_RUNNING, false);
        useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.AGENTIC_CAST_RUNNING, false);
        const aborted = activeStreamSignal.aborted || isAbortLikePersistentChatStreamError(chunk);
        const message = typeof chunk?.message === "string" ? chunk.message : "Something went wrong. Please try again.";
        get().setToolRuns((prev) => prev.map((tool) => (
          tool.status === "running"
            ? { ...tool, status: aborted ? "complete" : "error", summary: aborted ? "Stopped" : message }
            : tool
        )));
        set((state) => {
          const p = state.activePlan;
          if (!p) {
            return {
              streaming: false,
              currentStreamingText: "",
              currentProsePreview: "",
              currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {},
              pendingAgentResponse: false,
              responsePhase: "idle",
              ...(aborted ? {} : { errorText: message }),
              activePlan: null,
            };
          }
          if (p.status === "in_progress") {
            return {
              streaming: false,
              currentStreamingText: "",
              currentProsePreview: "",
              currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {},
              pendingAgentResponse: false,
              responsePhase: "idle",
              ...(aborted ? {} : { errorText: message }),
              activePlan: { ...p, status: "paused" },
            };
          }
          if (p.status === "completed" || p.status === "paused" || p.status === "cancelled" || p.status === "cancelling") {
            return {
              streaming: false,
              currentStreamingText: "",
              currentProsePreview: "",
              currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {},
              pendingAgentResponse: false,
              responsePhase: "idle",
              ...(aborted ? {} : { errorText: message }),
              activePlan: normalizePlanIfAllStepsDone(p),
            };
          }
          return {
            streaming: false,
            currentStreamingText: "",
            currentProsePreview: "",
            currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {},
            pendingAgentResponse: false,
            responsePhase: "idle",
            ...(aborted ? {} : { errorText: message }),
            activePlan: null,
          };
        });
        queryClient.invalidateQueries(chatQueryKey(projectId, resolvedChatId));
        queryClient.invalidateQueries(["chats", projectId]);
        // Resume stream failures use normal error UI above; expected stream
        // chunks should not become generic Sentry `onError` groups.
        if (!aborted && shouldCapturePersistentChatStreamError(chunk)) {
          Sentry.captureException(chunk);
        }
      },
    });
    } finally {
      // Clear unless a newer stream superseded this resume (stale signal).
      // Also clear if reset threw before streamSignal was captured.
      if (!streamSignal || !isStalePersistentChatStream(get, streamSignal)) {
        set({ planResumeRequestInFlight: false });
      }
    }
  },
}));

/** Reconcile Feedback that fails after the agentic response has already closed. */
export function reportAgenticFeedbackRunFailed(message: string): void {
  usePersistentChatStore.setState((state) => ({
    errorText: message,
    activeCommenting: state.activeCommenting
      ? { ...state.activeCommenting, status: "cancelled" as const }
      : null,
  }));
}

function dispatchPlanChunkEvent(
  chunk: AgenticLegacyStreamChunk,
  get: () => PersistentChatStore,
  set: (partial: Partial<PersistentChatState> | ((state: PersistentChatState) => Partial<PersistentChatState>)) => void,
  projectId: string,
  /** Same-turn client UI work as `pendingProjectUiActionsRef` on `sendMessage` — wait before insert question overlay */
  planStreamUiChain?: PlanStreamUiChainRef,
) {
  switch (chunk.event) {
    case "plan_work_progress": {
              const plan = get().activePlan;
              if (plan?.id === chunk.planId && Number.isInteger(chunk.completed) && Number.isInteger(chunk.total)
                && chunk.completed >= 0 && chunk.completed <= chunk.total) {
                set({ activePlan: { ...applyGoalPlanProgressSnapshot(plan, chunk.steps), workProgress: { completed: chunk.completed, total: chunk.total } } });
              }
              break;
            }
            case "plan_outline_progress": {
              const plan = get().activePlan;
              if (plan?.id === chunk.planId && Number.isInteger(chunk.saved) && Number.isInteger(chunk.total)
                && chunk.saved >= 0 && chunk.saved <= chunk.total) {
                set({ activePlan: { ...plan, outlineProgress: { saved: chunk.saved, total: chunk.total } } });
              }
              break;
            }
            case "plan_step_start": {
      const stepIndex = typeof chunk.stepIndex === "number" ? chunk.stepIndex : undefined;
      const stepId = normalizePlanStepId(chunk.stepId);
      const hasLocator = !!stepId || typeof stepIndex === "number";
      if (!hasLocator) break;
      const currentStatus = get().activePlan?.status;
      const optimisticPauseAwaitingApi =
        currentStatus === "paused" && get().pausePlanApiRequestInFlight;
      const cannotPromotePlan =
        currentStatus === "cancelled" || currentStatus === "completed" || currentStatus === "cancelling";
      const resumingPlan = get().planResumeRequestInFlight;
      const shouldPromoteToInProgress =
        optimisticPauseAwaitingApi
        || resumingPlan
        || (!cannotPromotePlan && currentStatus !== "paused");
      if (shouldPromoteToInProgress) {
        if (optimisticPauseAwaitingApi) {
          if (stepId) get().updatePlanStepById(stepId, "in_progress");
          else get().updatePlanStep(stepIndex!, "in_progress");
        } else {
          get().setPlanStatus("in_progress");
          if (stepId) get().updatePlanStepById(stepId, "in_progress");
          else get().updatePlanStep(stepIndex!, "in_progress");
        }
      }
      break;
    }
    case "plan_step_complete": {
      const plan = get().activePlan;
      if (!plan) break;
      const stepIndex = typeof chunk.stepIndex === "number" ? chunk.stepIndex : undefined;
      const stepId = normalizePlanStepId(chunk.stepId);
      const resolved = resolvePlanStreamStepIndex(plan, stepIndex, chunk.stepId);
      if (!shouldApplyPlanStepCompleteEvent(plan, resolved, chunk)) break;
      if (stepId) get().updatePlanStepById(stepId, "completed");
      else get().updatePlanStep(resolved, "completed");
      break;
    }
    case "plan_completed":
      get().completePlan(chunk.planId);
      break;
    case "plan_paused":
      flushTextBufferSync(set);
      set((state) => {
        const plan = state.activePlan;
        if (!plan) return { streaming: false, currentProsePreview: "", currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {}, planResumeRequestInFlight: false };
        return {
          streaming: false,
          currentProsePreview: "",
          currentProsePreviews: {}, currentProsePreviewExplanations: {}, currentProsePreviewGroups: {},
          planResumeRequestInFlight: false,
          activePlan: normalizePlanIfAllStepsDone(applyPlanPausedChunk(plan, chunk)),
        };
      });
      break;
    case "plan_cancelled":
      get().setPlanStatus("cancelled");
      break;
    case "tool_start": {
      const callId = typeof chunk.callId === "string" ? chunk.callId : "";
      const tool = typeof chunk.tool === "string" ? chunk.tool : "tool";
      const toolArgs = typeof chunk.arguments === "object" && chunk.arguments !== null ? chunk.arguments as Record<string, unknown> : undefined;
      const isWrite = isWriteToolName(tool, toolArgs);
      const readTargetLabel = typeof chunk.readTargetLabel === "string" ? chunk.readTargetLabel : undefined;
      {
        const signal = getToolSignal(tool);
        if (signal) useCrossAppSignalStore.getState().setSignalActive(signal, true);
      }
      get().setToolRuns((prev) => [...prev.filter((t) => t.callId !== callId), { callId, name: tool, status: "running" }]);
      get().finalizeReasoningTask();
      if (!shouldSkipThinkingToolTimeline(tool, get().mode)) {
        get().startToolTimelineTask(callId || undefined, tool, undefined, isWrite, readTargetLabel, toolArgs);
      }
      break;
    }
    case "tool_progress": {
      const callId = typeof chunk.callId === "string" ? chunk.callId : "";
      const message = typeof chunk.message === "string" ? chunk.message : "";
      if (message) {
        get().updateToolTimelineProgress(callId || undefined, message);
      }
      break;
    }
    case "tool_complete": {
      const callId = typeof chunk.callId === "string" ? chunk.callId : "";
      const tool = typeof chunk.tool === "string" ? chunk.tool : "tool";
      const outcome = typeof chunk.outcome === "string"
        ? chunk.outcome
        : typeof chunk.summary === "string"
          ? chunk.summary
          : undefined;
      const elementType = typeof chunk.elementType === "string" ? chunk.elementType : undefined;
      const entityName = sanitizeMentionedEntityName(chunk.entityName);
      {
        const signal = getToolSignal(tool);
        if (signal) useCrossAppSignalStore.getState().setSignalActive(signal, false);
      }
      const newStatus = outcome?.toLowerCase().startsWith("error") ? "error" : "complete";
      maybeRecordAgentManuscriptEditUndo(tool, newStatus, outcome);
      maybeOpenCommentSidebarAfterCommentCreate(chunk, newStatus);
      maybeApplyOptimisticCommentDelete(chunk, newStatus);
      get().setToolRuns((prev) => {
        if (callId) return prev.map((t) => t.callId === callId ? { ...t, status: newStatus as any, summary: outcome } : t);
        const idx = prev.findIndex((t) => t.status === "running");
        if (idx === -1) return prev;
        return prev.map((t, i) => i === idx ? { ...t, status: newStatus as any, summary: outcome } : t);
      });
      if (elementType) {
        set((state) => ({
          contextElements: state.contextElements.includes(elementType)
            ? state.contextElements
            : [...state.contextElements, elementType],
        }));
      }
      if (entityName && elementType) {
        set((state) => ({
          mentionedEntities: state.mentionedEntities.some((e) => e.type === elementType && e.name === entityName)
            ? state.mentionedEntities
            : [...state.mentionedEntities, { type: elementType, name: entityName }],
        }));
      }

      let parsedClientActionsForPlan: ClientUiAction[] = [];
      let legacyClientActionsForPlan: unknown[] = [];
      if (newStatus === "complete") {
        const clientActionsPayload = extractClientActionsFromToolCompleteChunk(chunk as Record<string, unknown>);
        if (clientActionsPayload.length > 0) {
          parsedClientActionsForPlan = suppressDocumentSwitchForOffScreenFeedback(
            parseClientUiActionsFromChunk(clientActionsPayload),
            clientActionsPayload,
          );
          legacyClientActionsForPlan = extractLegacyClientUiActions(clientActionsPayload);
        }
      }

      const planAwaitProjectUiBeforeTimelineComplete =
        parsedClientActionsForPlan.some(
          (a) => a.type === "refresh_project" || a.type === "set_active_document",
        );

      const finishPlanToolCompleteTimeline = () => {
        if (!shouldSkipThinkingToolTimeline(tool, get().mode)) {
          get().completeToolTimelineTask(callId || undefined, outcome, elementType);
        }
        const sbHint = inferStoryBibleRefreshHintFromToolCompleteChunk(tool, chunk as Record<string, unknown>);
        if (sbHint && newStatus === "complete") {
          void applyStoryBibleRefreshHint(projectId, sbHint, storyBibleRefreshShouldClearPendingDiffs(tool));
        }
        if (newStatus === "complete" && parsedClientActionsForPlan.length > 0 && !planAwaitProjectUiBeforeTimelineComplete) {
          void applyClientUiActionsFromStream(parsedClientActionsForPlan);
        }
        if (newStatus === "complete" && legacyClientActionsForPlan.length > 0) {
          void applyClientUiActions(legacyClientActionsForPlan);
        }
      };

      if (newStatus === "complete") {
        if (planStreamUiChain) {
          if (planAwaitProjectUiBeforeTimelineComplete && parsedClientActionsForPlan.length > 0) {
            planStreamUiChain.current = planStreamUiChain.current
              .catch((error: unknown) => {
                Sentry.captureException(error, {
                  tags: { feature: "apply_client_ui_actions", action: "plan_stream_prior_chain" },
                });
              })
              .then(async () => {
                await applyClientUiActionsFromStream(parsedClientActionsForPlan);
              })
              .catch((error: unknown) => {
                Sentry.captureException(error, {
                  tags: { feature: "apply_client_ui_actions", action: "plan_stream_project_ui_refresh" },
                  extra: { parsedClientActionsForPlan },
                });
              })
              .then(() => {
                finishPlanToolCompleteTimeline();
              });
          } else {
            planStreamUiChain.current = planStreamUiChain.current
              .catch((error: unknown) => {
                Sentry.captureException(error, {
                  tags: { feature: "apply_client_ui_actions", action: "plan_stream_prior_chain" },
                });
              })
              .then(() => {
                finishPlanToolCompleteTimeline();
              });
          }
        } else {
          finishPlanToolCompleteTimeline();
        }
      } else {
        if (!shouldSkipThinkingToolTimeline(tool, get().mode)) {
          get().completeToolTimelineTask(callId || undefined, outcome, elementType);
        }
      }
      break;
    }
    case "ask_user_question": {
      flushTextBufferSync(set);
      const questions = normalizeAskUserQuestionSteps(
        Array.isArray(chunk.questions)
          ? chunk.questions
          : typeof chunk.question === "string" && chunk.question
            ? [{ question: chunk.question, inputType: chunk.inputType ?? "button", options: Array.isArray(chunk.options) ? chunk.options : [] }]
            : [],
      );
      if (questions.length > 0) {
        const firstQuestion = typeof questions[0]?.question === "string" ? questions[0].question : "";
        if (shouldLogInsertDraftQuestionDebug() && isInsertDraftQuestionClient(firstQuestion)) {
          // eslint-disable-next-line no-console
          console.info("[agentic-debug] ask_user_question received (plan/resume stream)", {
            firstQuestion,
            streamingTextAfterFlushLength: get().currentStreamingText.length,
          });
        }
        set({ currentUserQuestion: { questions } });
        if (get().activePlan?.status === "in_progress") {
          void (async () => {
            try {
              if (planStreamUiChain) {
                await planStreamUiChain.current;
              }
            } catch (error) {
              Sentry.captureException(error, {
                tags: { feature: "persistent_chat_store", action: "ask_user_question_plan_stream" },
              });
            } finally {
              if (get().activePlan?.status === "in_progress") {
                get().setPlanStatus("paused");
              }
            }
          })();
        }
      }
      break;
    }
    case "chat_response_context": {
      set({ currentPreserveChatTextWithSuggestions: chunk.preserveChatTextWithSuggestions === true });
      break;
    }
    case "write_prose_context": {
      const writePovTenseChiclets = parseWritePovTenseChiclets(
        (chunk as { writePovTenseChiclets?: unknown }).writePovTenseChiclets,
      );
      if (writePovTenseChiclets?.length) {
        set({ currentWritePovTenseChiclets: writePovTenseChiclets });
      }
      break;
    }
    case "prose_preview": {
      set(state => applyProsePreviewChunk(state.currentProsePreviews, chunk, state.currentProsePreviewExplanations, state.currentProsePreviewGroups));
      break;
    }
    case "prose_chunk":
    case "text_chunk": {
      const text = typeof chunk.text === "string" ? chunk.text : "";
      if (chunk.type === "prose_chunk") {
        handleStreamedProseChunk(text, get, set);
      } else {
        appendStreamingText(text, set);
      }
      break;
    }
    case "text_clear": {
      cancelTextBufferFlush();
      set({ currentStreamingText: "" });
      break;
    }
    case "text": {
      cancelTextBufferFlush();
      set({ currentStreamingText: typeof chunk.text === "string" ? chunk.text : "" });
      break;
    }
    case "reasoning_chunk": {
      const text = typeof chunk.text === "string" ? chunk.text : "";
      get().appendReasoningTask(text);
      break;
    }
    case "outline_update": {
      set((state) => ({
        outlineUpdates: [
          ...state.outlineUpdates,
          {
            summary: typeof chunk.summary === "string" ? chunk.summary : "Outline updated",
            chapterIds: Array.isArray(chunk.chapterIds) ? chunk.chapterIds : [],
            rationale: typeof chunk.rationale === "string" ? chunk.rationale : undefined,
          },
        ],
      }));
      applyOutlineUpdateFromChunk(chunk);
      const outlineUpdateRefetchGeneration = startOutlineUpdateRefetch();
      (async () => {
        try {
          const { useStructuredOutlineStore } = await import("src/components/StoryBible/StructuredOutline/StructuredOutlineStore");
          const outlineStore = useStructuredOutlineStore.getState();
          const { outline } = await Auth.fetchWithIdToken("/api/outline", { projectId });
          if (!isLatestOutlineUpdateRefetch(outlineUpdateRefetchGeneration)) {
            return;
          }
          outlineStore.setOutline(outline);
          reapplyChapterDiffsToOutline();
          reapplyOutlineUpdateSummariesFromChunk(chunk);
        } catch { /* ignore */ }
      })();
      break;
    }
    case "character_update": {
      const characterId = typeof chunk.characterId === "string" ? chunk.characterId : "";
      const characterName = typeof chunk.characterName === "string" ? chunk.characterName : "";
      const updatedFields = Array.isArray(chunk.updatedFields) ? chunk.updatedFields : [];
      const oldFields = chunk.oldFields && typeof chunk.oldFields === "object" ? chunk.oldFields : {};
      const newFields = chunk.newFields && typeof chunk.newFields === "object" ? chunk.newFields : {};
      set((state) => ({
        characterUpdates: [...state.characterUpdates, { characterId, characterName, updatedFields, oldFields, newFields }],
      }));
      (async () => {
        try {
          const { useStoryBibleStore } = await import("src/stores/storyBibleStore");
          const activeProjectId = useProjectStore.getState().project?.id;
          if (activeProjectId && activeProjectId !== projectId) return;
          const store = useStoryBibleStore.getState();
          applyCharacterUpdateFromChunk(store, { characterId, oldFields, newFields, character: chunk.character as any, projectId });
          if (Object.keys(oldFields).length === 0 && characterId) store.addPendingNewCharacter(characterId);
          if (characterId && Object.keys(newFields).length > 0) store.setCharacterDiff(characterId, oldFields, newFields);
          if (characterId && updatedFields.includes("_deleted")) {
            store.clearCharacterDiff(characterId);
            store.removePendingNewCharacter(characterId);
          }
          const characterUpdateRefetchGeneration = startCharacterUpdateRefetch();
          await store.refreshCharacters(projectId, { force: true });
          if (!isLatestCharacterUpdateRefetch(characterUpdateRefetchGeneration)) return;
        } catch { /* ignore */ }
      })();
      break;
    }
    case "worldbuilding_update": {
      const entryId = typeof chunk.entryId === "string" ? chunk.entryId : "";
      const entryName = typeof chunk.entryName === "string" ? chunk.entryName : "";
      const updatedFields = Array.isArray(chunk.updatedFields) ? chunk.updatedFields : [];
      const oldFields = chunk.oldFields && typeof chunk.oldFields === "object" ? chunk.oldFields : {};
      const newFields = chunk.newFields && typeof chunk.newFields === "object" ? chunk.newFields : {};
      set((state) => ({
        worldbuildingUpdates: [...state.worldbuildingUpdates, { entryId, entryName, updatedFields, oldFields, newFields }],
      }));
      (async () => {
        try {
          const { useStoryBibleStore } = await import("src/stores/storyBibleStore");
          const store = useStoryBibleStore.getState();
          if (Object.keys(oldFields).length === 0 && entryId) store.addPendingNewWorldbuilding(entryId);
          if (entryId && Object.keys(newFields).length > 0) store.setWorldbuildingDiff(entryId, oldFields, newFields);
          if (entryId && updatedFields.includes("_deleted")) {
            store.clearWorldbuildingDiff(entryId);
            store.removePendingNewWorldbuilding(entryId);
          }
          const worldbuildingUpdateRefetchGeneration = startWorldbuildingUpdateRefetch();
          await store.refreshWorldbuildingEntries(projectId, "persistentChatStore.worldbuilding_update_plan", { force: true });
          if (!isLatestWorldbuildingUpdateRefetch(worldbuildingUpdateRefetchGeneration)) return;
        } catch { /* ignore */ }
      })();
      break;
    }
    case "meta_update": {
      const updatedFields = Array.isArray(chunk.updatedFields) ? chunk.updatedFields : [];
      const oldFields = chunk.oldFields && typeof chunk.oldFields === "object" ? chunk.oldFields : {};
      const newFields = chunk.newFields && typeof chunk.newFields === "object" ? chunk.newFields : {};
      set((state) => ({
        metaUpdates: [...state.metaUpdates, { updatedFields, oldFields, newFields }],
      }));
      (async () => {
        try {
          const { useStoryBibleStore } = await import("src/stores/storyBibleStore");
          const store = useStoryBibleStore.getState();
          if (!isCurrentStoryBibleOwner(store, projectId)) return;
          applyMetaUpdateFromChunk(store, { updatedFields, oldFields, newFields }, projectId);
          const metaUpdateRefetchGeneration = startMetaUpdateRefetch();
          const results = await Auth.fetchWithIdToken(`/api/story-engine/${projectId}/get`, {});
          if (!isCurrentStoryBibleOwner(store, projectId) || !isLatestMetaUpdateRefetch(metaUpdateRefetchGeneration)) return;
          if (!applyStoryEngineMetaToStore(store, projectId, results)) {
            reapplyMetaDiffsFromUpdates(store, get().metaUpdates, projectId);
            return;
          }
          reapplyMetaDiffsFromUpdates(store, get().metaUpdates, projectId);
          for (const fieldKey of collectMetaUpdatedFieldKeys(get().metaUpdates)) store.setMetaDiff(fieldKey);
        } catch { /* ignore */ }
      })();
      break;
    }
    case "context_element": {
      const element = typeof chunk.elementType === "string" ? chunk.elementType : "";
      if (element) {
        set((state) => ({
          contextElements: state.contextElements.includes(element)
            ? state.contextElements
            : [...state.contextElements, element],
        }));
      }
      break;
    }
    case "mentioned_entity": {
      const entityType = typeof chunk.entityType === "string" ? chunk.entityType : "";
      const name = sanitizeMentionedEntityName(chunk.entityName) ?? "";
      if (entityType && name) {
        set((state) => ({
          mentionedEntities: state.mentionedEntities.some((e) => e.type === entityType && e.name === name)
            ? state.mentionedEntities
            : [...state.mentionedEntities, { type: entityType, name }],
        }));
      }
      break;
    }
  }
}
